import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  issuePath,
  parseAppRoute,
  pullRequestPath,
  qualityRepositoryPath,
  type InsightTab,
  type QualityTab,
  type SettingsTab,
  type StatusWindow,
  type View,
  type WorkTab,
} from "./app-routing";
import {
  analyticsAuthorIndex,
  analyticsHistoryDays,
  analyticsRepositoryChoices,
  analyticsUserChoices,
  filterAnalyticsItems,
  findingPriorityTrend,
  mergeLeadTimeTrend,
  outcomeBreakdown,
  overviewCoverageDigest,
  providerAttemptTrend,
  pullRequestFlow,
  reviewCycleDistribution,
  reviewDurationTrend,
  reviewImpactTrend,
  reviewThroughput,
  coverageByRepository,
  coverageReleaseSeries,
  portfolioSummary,
  portfolioTrend,
  testOwnershipLeaderboard,
  testQualitySummary,
  testReleaseSeries,
  testTotalsByRepository,
  threadHealthByRepository,
  type TestOwnerDatum,
  type TestReleaseDatum,
  type TestRepositoryDatum,
} from "./dashboard-data";
import { runProviderLabel } from "./run-display";
import { PortalApp } from "./portal-app";
import { PortalApiError, portalApi, type PortalRepository, type PortalUser } from "./portal-api";
import { portalRepositoryWithEnabled } from "./portal-data";
import {
  NATIVE_REVIEWER_CATALOG,
  ProviderUsageRefreshButton,
  ReviewerPoolDropdown,
  REVIEW_LEVELS,
  dispatchReviewers,
  normalizeReviewerPin,
  providerPinOptions,
  resolveReviewerSettings,
  reviewerCatalogFallback,
  reviewerNative,
  unionReviewerModels,
  providerUsageRefreshNotice,
  requestProviderUsageRefresh,
  usageMeters,
  type ReviewLevel,
} from "./provider-usage";
import { PullRequestTests, type UiTestRun } from "./pr-ui-tests";
import { recentTerminalReviewRuns } from "./review-run-history";
import { fetchRuntimeHealth } from "./runtime-health";
import {
  ChartCardSkeleton,
  MetricsSkeleton,
  SkeletonBar,
  SkeletonCount,
  SkeletonMeters,
  SkeletonRows,
  SkeletonStack,
  SkeletonTimeline,
} from "./skeleton";
import { StatusToast } from "./status-toast";
import { UpdateNotice } from "./update-notice";
import { MemorySettings } from "./memory-settings";
import { ReviewQualityPanel } from "./review-quality";
import { StatusPage } from "./status-page";
import netraKoiIcon from "./assets/netra-koi.jpg";
import "./styles.css";

function NetraBrandMark() {
  return <span className="brand-mark" aria-hidden="true"><img src={netraKoiIcon} alt="" /></span>;
}

/// Failing and skipped are the only two outcomes charted as adjacent release
/// series, so this pair is validated for deuteranopia and protanopia separation.
/// The compact suite meter similarly uses indigo/red/gray rather than placing
/// green passing beside red failing.
const TEST_FAILING_COLOR = "#c45b4f";
const TEST_SKIPPED_COLOR = "#7385cf";
const TEST_TOTAL_COLOR = "#4f9f6a";
/// Coverage sits beside the tests-green on the same tab, so it is a hue far
/// enough away to stay distinguishable with and without colour-vision
/// deficiency (validated: normal ΔE 22.6, deuteranopia 21.3).
const TEST_COVERAGE_COLOR = "#45619f";
const TEST_OWNER_COLOR = "#769eac";

type PreviewState =
  | "reserved"
  | "queued"
  | "building"
  | "starting"
  | "health_checking"
  | "active"
  | "failed"
  | "superseded"
  | "stale"
  | "undeploying"
  | "inactive"
  | "expired";

export type Preview = {
  id: string;
  repository: { owner: string; name: string };
  number: number;
  head_sha: string;
  status: PreviewState;
  public_url: string;
  created_at: string;
  updated_at: string;
  expires_at?: string;
  active_head_sha?: string;
  current_head_sha?: string;
  error?: string;
  log_path?: string;
};

type PreviewDiagnostics = {
  preview: Preview;
  backend: "docker" | "kubernetes";
  events: Array<{ kind: string; payload: Record<string, unknown>; created_at: string }>;
  log_tail?: string;
  queue_position?: number;
  estimated_wait_seconds?: number;
  routing: { exact_head: boolean; explanation: string };
};

type RepositorySetting = {
  repository: string;
  reviews_enabled: boolean;
  previews_enabled: boolean;
  review_level: "deep" | "standard" | "fast" | "auto";
  revision_count: number;
  active_count: number;
};

type RepositoryPolicy = {
  profile?: "balanced" | "strict" | "fast" | "custom";
  blocking_priority?: "P0" | "P1";
  require_pr_tests?: boolean;
  min_changed_line_coverage?: number;
  provider_strategy?: "auto" | "adversarial" | "fixed";
  // A provider id is a CLI name, or "cursor:<model>" for a model Cursor fronts.
  // Not a closed union: the roster is configured in PROVIDERS, not compiled in.
  provider?: string;
  protected_paths: string[];
  max_preview_cpu?: number;
  max_preview_memory_mb?: number;
};

type PreviewEnvironmentConfig = {
  manifest: string | null;
  values: Record<string, string>;
  build_values: Record<string, string>;
  bindings: Array<{
    environment_key: string;
    alias: string;
    source: string;
    delivery: "environment" | "file";
  }>;
  build_bindings: Array<{
    secret_id: string;
    alias: string;
    source: string;
  }>;
};

type PreviewEnvironmentImport = {
  config: PreviewEnvironmentConfig;
  imported_values: number;
  imported_build_values: number;
  imported_secrets: number;
};

export type ReviewJob = {
  id: string;
  repository: string;
  number: number;
  head_sha?: string;
  mode: "review" | "fix";
  trigger: string;
  status: string;
  provider?: string;
  preview_url?: string;
  error?: string;
  created_at: string;
  started_at?: string;
  provider_finished_at?: string;
  completed_at?: string;
  updated_at?: string;
  attempt?: number;
  result?: Record<string, unknown>;
  log_path?: string;
  log_tail?: string;
};

type IssueJob = Omit<ReviewJob, "head_sha" | "preview_url"> & {
  entity_type: "issue";
  base_sha?: string;
  fix_pr_url?: string;
  fix_pr_number?: number;
};

type RuntimeOverview = {
  runtime: string;
  review_concurrency: number;
  preview_ttl_hours: number;
  repositories: number;
  providers: Array<{
    provider: string;
    invocations: number;
    successes: number;
    failures: number;
    cooldown_until?: string;
    last_error?: string;
  }>;
  legacy_analytics?: {
    reviews: number;
    by_provider: Record<string, number>;
    by_status: Record<string, number>;
    by_repository: Record<string, number>;
    average_duration_seconds?: number;
    comments_added: number;
    replies: number;
    threads_resolved: number;
    historical_invocations: Record<string, number>;
    live_usage: Record<string, {
      data?: {
        available?: boolean;
        remaining_pct?: number;
        summary?: string;
        reset_label?: string;
        stale?: boolean;
        fetched_at?: string;
        refresh_error?: string;
        pools?: Array<{
          id: string;
          label: string;
          remaining_pct?: number;
          summary?: string;
          hard_exhausted?: boolean;
        }>;
      };
    }>;
  };
};

export type PullRequestInventory = {
  repository: string;
  number: number;
  title: string;
  body?: string;
  state: string;
  draft: boolean;
  author?: string;
  head_sha: string;
  head_ref: string;
  base_ref: string;
  created_at?: string;
  updated_at: string;
  additions?: number;
  deletions?: number;
  changed_files?: number;
  commits?: number;
  comments?: number;
  mergeable?: string;
  merge_state_status?: string;
  review_decision?: string;
  unresolved_threads?: number;
  reviews?: Array<Record<string, unknown>>;
  review_threads?: Array<Record<string, unknown>>;
  checks?: Array<{
    name: string;
    status?: string;
    conclusion?: string;
    url?: string;
  }>;
  check_summary?: {
    total: number;
    failing: number;
    running: number;
    passing: number;
    state: string;
  };
  health?: { code: string; label: string; bucket: string };
  review_count?: number;
  finding_count?: number;
  blocking_findings?: number;
  advisory_findings?: number;
  last_reviewed_head?: string;
  last_reviewed_at?: string;
  latest_run?: ReviewJob;
  mute?: { kind: string; reason: string; until_at?: string };
  url: string;
};

type IssueInventory = {
  repository: string;
  number: number;
  title: string;
  author?: string;
  labels: string[];
  assignees: string[];
  comments?: number;
  updated_at: string;
  created_at?: string;
  body?: string;
  url: string;
};

type Inventory = {
  pull_requests: PullRequestInventory[];
  issues: IssueInventory[];
  synced_at?: string;
  errors: string[];
};

type Finding = {
  id: number;
  repository: string;
  number: number;
  head_sha: string;
  fingerprint: string;
  priority: string;
  path?: string;
  title?: string;
  body?: string;
  created_at: string;
  provider?: string;
  feedback_status?: "unresolved" | "fixed" | "accepted" | "dismissed" | "stale";
  feedback_reason?: string;
};

export type RuntimeEvent = {
  sequence: number;
  kind: string;
  repository?: string;
  number?: number;
  payload: Record<string, unknown>;
  created_at: string;
};

type OperationalIncident = {
  fingerprint: string;
  severity: "high" | "medium" | "low";
  kind: string;
  title: string;
  detail?: string;
  created_at?: string;
  acknowledged_at?: string;
};

type TeamsStatus = {
  configured: boolean;
  endpoint: string;
  flow: string;
  mode?: string;
  confirmation_required?: boolean;
  app_id_present?: boolean;
  tenant_id_present?: boolean;
  client_secret_present?: boolean;
  scopes?: string[];
};

type TeamsRequest = {
  id: string;
  conversation_id: string;
  requester: string;
  conversation_type: string;
  team_id?: string;
  channel_id?: string;
  channel: string;
  state: string;
  repository?: string;
  title?: string;
  body?: string;
  mode?: string;
  source_message_url?: string;
  created_at: string;
  updated_at: string;
  issue_number?: number;
  issue_url?: string;
  issue_created_at?: string;
  execution_status?: string;
  fix_pr_url?: string;
};

type TeamsRequestHistory = {
  summary: {
    total_requests: number;
    issues_created: number;
    active_requests: number;
    cancelled_requests: number;
    completion_rate_percent: number;
    average_time_to_issue_seconds?: number;
  };
  requests: TeamsRequest[];
};

type FailingTest = {
  repository: string;
  release_tag: string;
  suite: string;
  name: string;
  file?: string | null;
  status: string;
  failure_message?: string | null;
  owner?: string | null;
  last_edited_by?: string | null;
  last_edited_at?: string | null;
};

type TestQuality = {
  releases: TestReleaseDatum[];
  owners: TestOwnerDatum[];
  repositories: TestRepositoryDatum[];
  failing: FailingTest[];
  artifact_match?: string;
};

type RepositoryTest = {
  test_key: string;
  suite: string;
  name: string;
  file?: string | null;
  line?: number | null;
  status: string;
  duration_ms: number;
  failure_message?: string | null;
  owner?: string | null;
  owner_lines?: number | null;
  total_lines?: number | null;
  created_by?: string | null;
  created_at?: string | null;
  last_edited_by?: string | null;
  last_edited_at?: string | null;
  attribution_error?: string | null;
};

type CoverageFile = {
  path: string;
  lines_covered: number;
  lines_valid: number;
  uncovered_lines: number;
  line_rate: number;
  branches_covered: number;
  branches_valid: number;
  branch_rate: number;
  previous_line_rate?: number | null;
  line_rate_change?: number | null;
};

type CoverageSummary = {
  release_tag: string;
  released_at: string;
  files: number;
  shards: number;
  directories: number;
  lines_covered: number;
  lines_valid: number;
  uncovered_lines: number;
  line_rate: number;
  branches_covered: number;
  branches_valid: number;
  branch_rate: number | null;
};

type CoverageAreaRow = {
  shard: string;
  directory: string;
  display_path: string;
  files: number;
  lines_covered: number;
  lines_valid: number;
  uncovered_lines: number;
  line_rate: number;
  branches_covered: number;
  branches_valid: number;
  branch_rate: number | null;
};

type RepositoryTestDetail = {
  repository: string;
  commit_sha?: string | null;
  tests: RepositoryTest[];
  // Totals over every measured file. `coverage_files` is a worst-first slice,
  // so a percentage must come from here and never from summing that list.
  coverage_summary: CoverageSummary | null;
  coverage_areas: CoverageAreaRow[];
  coverage_files: CoverageFile[];
};

type TestOwnerSummary = {
  name: string;
  tests: number;
  ownerLines: number;
  totalLines: number;
  created: number;
  lastEdited: number;
};

type CompletedPullRequest = {
  repo: string;
  number: number;
  state: string;
  title?: string;
  author?: string;
  branch?: string;
  base?: string;
  review_count?: number;
  findings_count?: number;
  created_at?: string;
  merged_at?: string;
  closed_at?: string;
  completed_at?: string;
  url?: string;
};

type PullRequestDetail = {
  pull_request: PullRequestInventory;
  jobs: ReviewJob[];
  findings: Finding[];
  events: RuntimeEvent[];
  previews: Preview[];
  quality_evidence?: {
    head_sha: string;
    ingested_at: string;
    provenance?: string;
    tests: {
      total: number; passed: number; failed: number; errored: number; skipped: number;
      added_since_release: number; removed_since_release: number;
      failures: Array<{ suite: string; name: string; file?: string; line?: number; status: string; message?: string }>;
    };
    coverage: {
      changed_lines: number; measured_lines: number; covered_lines: number; unmeasured_lines: number;
      percent?: number;
      files: Array<{ path: string; changed_lines: number; measured_lines: number; covered_lines: number; unmeasured_lines: number; coverage_percent?: number }>;
    };
  };
  ui_tests?: UiTestRun[];
};

type IssueDetail = {
  issue: IssueInventory;
  jobs: IssueJob[];
  events: RuntimeEvent[];
};

type ReviewerCatalogEntry = {
  id: string;
  label: string;
  group: string;
  custom?: boolean;
};

type RuntimeSettings = {
  paused: boolean;
  mode: "observe" | "review" | "fix";
  provider: string;
  model_inversion?: boolean;
  providers: string[];
  active_natives?: string[];
  active_providers?: string[];
  active_providers_deep?: string[];
  active_providers_standard?: string[];
  active_providers_fast?: string[];
  reviewer_catalog?: ReviewerCatalogEntry[];
  schema?: SettingDescriptor[];
  overrides?: Record<string, unknown>;
};

type SettingDescriptor = {
  key: string;
  label: string;
  section: string;
  description: string;
  kind: "boolean" | "integer" | "text" | "choice";
  min?: number;
  max?: number;
  options: string[];
  value: boolean | number | string;
  default: boolean | number | string;
  source: "dashboard" | "environment" | "default";
  restart_required: boolean;
};

type GithubStatus = {
  connected: boolean;
  authentication: string;
  app_id_configured: boolean;
  installation_id?: string;
  private_key_configured: boolean;
  webhook_secret_configured: boolean;
  configured_repositories: number;
  accessible_repositories?: number;
  app_name?: string;
  app_slug?: string;
  app_owner?: string;
  app_url?: string;
  app_settings_url?: string;
  app_permissions?: Record<string, string>;
  subscribed_events?: string[];
  webhook_url?: string;
  configuration_error?: string;
  error?: string;
};

type GithubRepository = {
  full_name: string;
  private: boolean;
  archived: boolean;
  default_branch: string;
  url: string;
  selected: boolean;
};

type GithubImportDraft = {
  app_id: string;
  installation_id: string;
  client_id: string;
  slug: string;
  private_key: string;
  webhook_secret: string;
};

type ResourceOverview = {
  sampled_at: string;
  host: {
    name: string;
    os: string;
    cpu_count: number;
    cpu_percent: number;
    load_average: { one: number; five: number; fifteen: number };
    memory_total_bytes: number;
    memory_used_bytes: number;
    disk?: {
      name: string;
      mount: string;
      total_bytes: number;
      available_bytes: number;
    };
    preview_backend?: string;
  };
  processes: Array<{
    pid: number;
    name: string;
    cpu_percent: number;
    memory_bytes: number;
  }>;
  previews: Array<{
    preview_id: string;
    repository: string;
    number: number;
    head_sha: string;
    status: PreviewState;
    container?: string;
    runtime_port?: number;
    docker?: {
      CPUPerc?: string;
      MemPerc?: string;
      MemUsage?: string;
      NetIO?: string;
      BlockIO?: string;
      PIDs?: string;
    };
  }>;
};

type UndeployTarget = {
  id: string;
  repository: string;
  number: number;
};

type PrGroupMode = "status" | "created" | "repository" | "user" | "none";
type IssueGroupMode = "user" | "repository" | "none";

export type PullInventoryFilter = {
  user: string;
  status: string;
  repository: string;
  ci: string;
};

export type IssueInventoryFilter = {
  user: string;
  repository: string;
};

type AnalyticsRange = "14d" | "all";
type OverviewReviewTab = "ongoing" | "completed";

const terminalStates = new Set<PreviewState>([
  "failed",
  "superseded",
  "inactive",
  "expired",
]);

export function canManuallyDeploy(preview: Preview) {
  return ["inactive", "expired"].includes(preview.status)
    && (!preview.current_head_sha || preview.current_head_sha === preview.head_sha);
}

export function shortSha(sha?: string) {
  return sha ? sha.slice(0, 8) : "—";
}

export function relativeTime(value?: string) {
  if (!value) return "—";
  const seconds = Math.round((new Date(value).getTime() - Date.now()) / 1000);
  const formatter = new Intl.RelativeTimeFormat("en", { numeric: "auto" });
  if (Math.abs(seconds) < 60) return formatter.format(seconds, "second");
  const minutes = Math.round(seconds / 60);
  if (Math.abs(minutes) < 60) return formatter.format(minutes, "minute");
  return formatter.format(Math.round(minutes / 60), "hour");
}

export function incidentDetailSummary(detail?: string) {
  if (!detail) return "No diagnostic detail was recorded.";
  const lines = detail.split("\n").map((line) => line.trim()).filter(Boolean);
  const useful = [...lines].reverse().find((line) =>
    /(?:error:|failed to|timed out|unavailable|unauthorized|forbidden)/i.test(line)
    && !line.startsWith("#"),
  ) ?? lines[0] ?? detail;
  return useful.length > 220 ? `${useful.slice(0, 217)}…` : useful;
}

export function reviewRunDiagnostics(job: ReviewJob | IssueJob) {
  if (job.error) return incidentDetailSummary(job.error);
  if ("fix_pr_number" in job && job.fix_pr_number) return `Created fix PR #${job.fix_pr_number}`;
  if (job.status === "failed") return "Failed without recorded error detail";
  if (job.status === "aborted") return "Run was aborted";
  const metrics = [
    ["comments", job.result?.comments_added],
    ["replies", job.result?.replies],
    ["threads resolved", job.result?.threads_resolved],
  ].filter((metric): metric is [string, number] => typeof metric[1] === "number" && metric[1] > 0);
  return metrics.length > 0
    ? metrics.map(([label, value]) => `${value} ${label}`).join(" · ")
    : "No errors recorded";
}

export function durationBetween(start?: string, end?: string) {
  if (!start) return "—";
  const startMs = new Date(start).getTime();
  const endMs = end ? new Date(end).getTime() : Date.now();
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) return "—";
  const seconds = Math.max(0, Math.round((endMs - startMs) / 1000));
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remainder = seconds % 60;
  if (minutes < 60) return `${minutes}m ${remainder}s`;
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
}

function age(value?: string) {
  if (!value) return "—";
  return durationBetween(value, new Date().toISOString());
}

export function compactHours(value: number) {
  return value >= 48 ? `${(value / 24).toFixed(1)}d` : `${value.toFixed(1)}h`;
}

export function compactDurationSeconds(value: number) {
  if (!Number.isFinite(value) || value <= 0) return "—";
  if (value < 60) return `${Math.round(value)}s`;
  if (value < 3600) return `${(value / 60).toFixed(1)}m`;
  return `${(value / 3600).toFixed(1)}h`;
}

export function bytes(value?: number) {
  if (value === undefined || !Number.isFinite(value)) return "—";
  const units = ["B", "KiB", "MiB", "GiB", "TiB"];
  let amount = value;
  let unit = 0;
  while (amount >= 1024 && unit < units.length - 1) {
    amount /= 1024;
    unit += 1;
  }
  return `${amount >= 10 || unit === 0 ? amount.toFixed(0) : amount.toFixed(1)} ${units[unit]}`;
}

export function previewRuntimeLabel(backend?: string) {
  switch ((backend ?? "docker").trim().toLowerCase()) {
    case "k8s":
    case "kubernetes":
      return "Kubernetes";
    case "podman":
      return "Podman";
    default:
      return "Docker";
  }
}

export function humanEvent(event: RuntimeEvent) {
  const payload = event.payload ?? {};
  const notes = typeof payload.notes === "string" ? payload.notes : "";
  const status = typeof payload.status === "string" ? payload.status : "";
  const labels: Record<string, string> = {
    "review.queued": "Review queued",
    "review.started": "Review started",
    "review.prepared": "Review prepared",
    "review.aborted": "Review aborted",
    "provider.started": "Provider started",
    "provider.failed": "Provider failed",
    "provider.completed": "Provider completed",
    "review.provider_started": "Provider started",
    "review.provider_failed": "Provider failed",
    "review.provider_completed": "Provider completed",
    "review.completed": "Review completed",
    "review.failed": "Review failed",
    "issue.queued": "Issue run queued",
    "issue.started": "Issue run started",
    "issue.completed": "Issue run completed",
    "issue.failed": "Issue run failed",
    "issue.aborted": "Issue run aborted",
    pr_checked: "Review completed",
    webhook_kick: "GitHub event accepted",
    cycle_start: "Legacy cycle started",
    cycle_end: "Legacy cycle finished",
    issue_checked: "Issue reviewed",
    comment_added: "Review comment posted",
    reply: "Review thread replied",
    thread_resolved: "Review thread resolved",
    error: "Error",
  };
  return {
    title: labels[event.kind] ?? event.kind.replaceAll("_", " "),
    detail: notes || status || event.kind,
  };
}

function repoName(preview: Preview) {
  return `${preview.repository.owner}/${preview.repository.name}`;
}

export function counted(value: number, singular: string, plural = `${singular}s`) {
  return `${value} ${value === 1 ? singular : plural}`;
}

/// The verb beside a `counted` phrase has to agree with its number too.
/// `counted` inflects only the noun, and "1 repository report tests" is what
/// comes out when the verb is left to fend for itself.
export function verbFor(value: number, singular: string, plural: string) {
  return value === 1 ? singular : plural;
}

/// Reuse the shared `.state` pill tones for test outcomes. A runner error is a
/// failure, and a skipped test keeps the neutral tone rather than a warning one.
export function testStateClass(status: string) {
  if (status === "passed") return "active";
  if (status === "failed" || status === "errored") return "failed";
  return "inactive";
}

function testOwners(tests: RepositoryTest[]): TestOwnerSummary[] {
  const owners = new Map<string, TestOwnerSummary>();
  tests.forEach((test) => {
    const name = test.owner ?? "Unattributed";
    const owner = owners.get(name) ?? {
      name,
      tests: 0,
      ownerLines: 0,
      totalLines: 0,
      created: 0,
      lastEdited: 0,
    };
    owner.tests += 1;
    owner.ownerLines += test.owner_lines ?? 0;
    owner.totalLines += test.total_lines ?? 0;
    owner.created += test.created_by === test.owner && test.owner != null ? 1 : 0;
    owner.lastEdited += test.last_edited_by === test.owner && test.owner != null ? 1 : 0;
    owners.set(name, owner);
  });
  return [...owners.values()].sort((left, right) => right.tests - left.tests || left.name.localeCompare(right.name));
}

export function reviewFeedbackSummary(pull: PullRequestInventory) {
  const open = pull.unresolved_threads ?? 0;
  const blocking = pull.blocking_findings ?? 0;
  const advisory = pull.advisory_findings ?? 0;
  const recorded = blocking + advisory;
  if (open === 0 && recorded === 0) return "clear";
  if (open === recorded && recorded > 0) {
    if (blocking > 0 && advisory > 0) return `${open} open · ${blocking} blocking`;
    if (blocking > 0) return `${open} open blocking`;
    return `${open} open advisory`;
  }
  if (open > 0 && recorded > 0) return `${open} open · ${recorded} recorded`;
  if (open > 0) return `${open} open`;
  return `${recorded} recorded`;
}

export function createdPullGroup(createdAt?: string) {
  const timestamp = createdAt ? Date.parse(createdAt) : Number.NaN;
  if (!Number.isFinite(timestamp)) return "Creation date unknown";
  const ageDays = Math.max(0, Date.now() - timestamp) / 86_400_000;
  if (ageDays < 1) return "Created in the past 24 hours";
  if (ageDays < 7) return "Created in the past 7 days";
  if (ageDays < 30) return "Created in the past 30 days";
  return "Created earlier";
}

function uniqueWorkLabels(values: Array<string | undefined>) {
  const seen = new Map<string, string>();
  for (const value of values) {
    const trimmed = value?.trim();
    if (!trimmed) continue;
    const key = trimmed.toLocaleLowerCase();
    if (!seen.has(key)) seen.set(key, trimmed);
  }
  return [...seen.values()].sort((left, right) =>
    left.localeCompare(right, undefined, { sensitivity: "base" })
  );
}

function sameWorkUser(left?: string, right?: string) {
  return (left ?? "").toLocaleLowerCase() === (right ?? "").toLocaleLowerCase();
}

export function pullAuthorLabel(author?: string) {
  return author?.trim() || "unknown";
}

export function pullStatusLabel(pull: {
  health?: { label?: string };
  draft?: boolean;
}) {
  return pull.health?.label ?? (pull.draft ? "draft" : "syncing");
}

export function pullGroupLabel(pull: PullRequestInventory, mode: PrGroupMode) {
  if (mode === "status") return pullStatusLabel(pull);
  if (mode === "created") return createdPullGroup(pull.created_at);
  if (mode === "user") return `@${pullAuthorLabel(pull.author)}`;
  if (mode === "repository") return pull.repository;
  return "";
}

export function matchesPullInventoryFilter(
  pull: PullRequestInventory,
  filter: PullInventoryFilter,
) {
  if (filter.repository && pull.repository !== filter.repository) return false;
  if (filter.user && !sameWorkUser(pullAuthorLabel(pull.author), filter.user)) return false;
  if (filter.status && pullStatusLabel(pull) !== filter.status) return false;
  if (filter.ci && (pull.check_summary?.state ?? "none") !== filter.ci) return false;
  return true;
}

export function groupPullRequests(pulls: PullRequestInventory[], mode: PrGroupMode) {
  if (mode === "none") {
    return [{ key: "all", label: "", pulls }];
  }
  const source = mode === "created"
    ? [...pulls].sort((left, right) => String(right.created_at).localeCompare(String(left.created_at)))
    : pulls;
  const groups = new Map<string, PullRequestInventory[]>();
  for (const pull of source) {
    const label = pullGroupLabel(pull, mode);
    const rows = groups.get(label) ?? [];
    rows.push(pull);
    groups.set(label, rows);
  }
  const entries = [...groups].map(([label, grouped]) => ({
    key: `${mode}:${label}`,
    label,
    pulls: grouped,
  }));
  if (mode === "repository" || mode === "user") {
    entries.sort((left, right) => {
      if (left.label === "@unknown") return 1;
      if (right.label === "@unknown") return -1;
      return left.label.localeCompare(right.label, undefined, { sensitivity: "base" });
    });
  }
  return entries;
}

export function issueAuthorLabel(author?: string) {
  return pullAuthorLabel(author);
}

export function matchesIssueInventoryFilter(
  issue: { repository: string; author?: string },
  filter: IssueInventoryFilter,
) {
  if (filter.repository && issue.repository !== filter.repository) return false;
  if (filter.user && !sameWorkUser(issueAuthorLabel(issue.author), filter.user)) return false;
  return true;
}

export function groupIssues<T extends { repository: string; author?: string }>(
  issues: T[],
  mode: IssueGroupMode,
) {
  if (mode === "none") {
    return [{ key: "all", label: "", issues }];
  }
  const groups = new Map<string, T[]>();
  for (const issue of issues) {
    const label = mode === "user" ? `@${issueAuthorLabel(issue.author)}` : issue.repository;
    const rows = groups.get(label) ?? [];
    rows.push(issue);
    groups.set(label, rows);
  }
  return [...groups].map(([label, grouped]) => ({
    key: `issue:${mode}:${label}`,
    label,
    issues: grouped,
  })).sort((left, right) => {
    if (left.label === "@unknown") return 1;
    if (right.label === "@unknown") return -1;
    return left.label.localeCompare(right.label, undefined, { sensitivity: "base" });
  });
}

function pushPath(path: string) {
  if (window.location.pathname !== path) {
    window.history.pushState({}, "", path);
  }
}

function currentPath() {
  return typeof window === "undefined" ? "/" : window.location.pathname;
}

export function App({
  currentUser,
  initialPath = currentPath(),
}: {
  currentUser: PortalUser;
  initialPath?: string;
}) {
  const start = parseAppRoute(initialPath);
  const [previews, setPreviews] = useState<Preview[]>([]);
  const [healthy, setHealthy] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  // `loading` means the shell is still waiting; this means the history wave
  // behind it is. They stopped being the same thing when the two waves were
  // separated, and anything drawn from history has to watch this one instead:
  // clearing `loading` after the first wave left the coverage and test-quality
  // panels reading their own empty state -- "no release has reported tests or
  // coverage yet" -- as a statement of fact, when the request that would have
  // filled them had not been issued yet. Only the first pass sets it, so the
  // ten second poll never puts skeletons back over a populated page.
  const [historyLoading, setHistoryLoading] = useState(true);
  const [inventorySyncing, setInventorySyncing] = useState(false);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState<Preview | null>(null);
  const [previewDiagnostics, setPreviewDiagnostics] = useState<PreviewDiagnostics | null>(null);
  const [view, setView] = useState<View>(start.view);
  const [workTab, setWorkTab] = useState<WorkTab>(start.view === "work" ? start.workTab : "pulls");
  const [overviewReviewTab, setOverviewReviewTab] = useState<OverviewReviewTab>("ongoing");
  const [prGroupMode, setPrGroupMode] = useState<PrGroupMode>("repository");
  const [prFilterUser, setPrFilterUser] = useState("");
  const [prFilterStatus, setPrFilterStatus] = useState("");
  const [prFilterRepository, setPrFilterRepository] = useState("");
  const [prFilterCi, setPrFilterCi] = useState("");
  const [collapsedPrGroups, setCollapsedPrGroups] = useState<Set<string>>(() => new Set());
  const [issueGroupMode, setIssueGroupMode] = useState<IssueGroupMode>("none");
  const [issueFilterUser, setIssueFilterUser] = useState("");
  const [issueFilterRepository, setIssueFilterRepository] = useState("");
  const [insightTab, setInsightTab] = useState<InsightTab>(start.view === "insights" ? start.insightTab : "analytics");
  const [analyticsRange, setAnalyticsRange] = useState<AnalyticsRange>("14d");
  const [analyticsRepository, setAnalyticsRepository] = useState("");
  const [analyticsUser, setAnalyticsUser] = useState("");
  const [settingsTab, setSettingsTab] = useState<SettingsTab>(start.view === "settings" ? start.settingsTab : "general");
  const [statusWindow, setStatusWindow] = useState<StatusWindow>(start.view === "status" ? start.statusWindow : "30d");
  const [repositories, setRepositories] = useState<RepositorySetting[]>([]);
  const [repositoryPolicySaving, setRepositoryPolicySaving] = useState<Set<string>>(() => new Set());
  const repositoryPolicySavingRef = useRef<Set<string>>(new Set());
  const repositoryPolicyEpochRef = useRef(0);
  const [portalRepositories, setPortalRepositories] = useState<PortalRepository[]>([]);
  const [portalRepositorySaving, setPortalRepositorySaving] = useState<Set<string>>(() => new Set());
  const portalRepositorySavingRef = useRef<Set<string>>(new Set());
  const portalRepositoryEpochRef = useRef(0);
  const [repositoryInput, setRepositoryInput] = useState("");
  const [repositorySearch, setRepositorySearch] = useState("");
  const [repositoryPickerOpen, setRepositoryPickerOpen] = useState(false);
  const [githubRepositories, setGithubRepositories] = useState<GithubRepository[]>([]);
  const [reviewJobs, setReviewJobs] = useState<ReviewJob[]>([]);
  const [issueJobs, setIssueJobs] = useState<IssueJob[]>([]);
  const [runtime, setRuntime] = useState<RuntimeOverview | null>(null);
  const [providerUsageRefreshing, setProviderUsageRefreshing] = useState(false);
  const [inventory, setInventory] = useState<Inventory>({
    pull_requests: [],
    issues: [],
    errors: [],
  });
  const [notice, setNotice] = useState("");
  const dismissNotice = useCallback(() => setNotice(""), []);
  const [restartPending, setRestartPending] = useState(false);
  const [restarting, setRestarting] = useState(false);
  const [findings, setFindings] = useState<Finding[]>([]);
  const [events, setEvents] = useState<RuntimeEvent[]>([]);
  const [incidents, setIncidents] = useState<OperationalIncident[]>([]);
  const [acknowledgingIncident, setAcknowledgingIncident] = useState<string | null>(null);
  const [completedPulls, setCompletedPulls] = useState<CompletedPullRequest[]>([]);
  const [prDetail, setPrDetail] = useState<PullRequestDetail | null>(null);
  const [issueDetail, setIssueDetail] = useState<IssueDetail | null>(null);
  const [runtimeSettings, setRuntimeSettings] = useState<RuntimeSettings | null>(null);
  const [testQuality, setTestQuality] = useState<TestQuality>({
    releases: [],
    owners: [],
    repositories: [],
    failing: [],
  });
  const [qualityTab, setQualityTab] = useState<QualityTab>(start.view === "quality" ? start.qualityTab : "portfolio");
  const [testRepository, setTestRepository] = useState<string>(start.view === "quality" ? start.repository ?? "" : "");
  const [testDetail, setTestDetail] = useState<RepositoryTestDetail | null>(null);
  const [testRefreshing, setTestRefreshing] = useState(false);
  const [githubStatus, setGithubStatus] = useState<GithubStatus | null>(null);
  const [teamsStatus, setTeamsStatus] = useState<TeamsStatus | null>(null);
  const [teamsHistory, setTeamsHistory] = useState<TeamsRequestHistory>({
    summary: {
      total_requests: 0,
      issues_created: 0,
      active_requests: 0,
      cancelled_requests: 0,
      completion_rate_percent: 0,
    },
    requests: [],
  });
  const [githubSetupOpen, setGithubSetupOpen] = useState(false);
  const [githubSetupOrganization, setGithubSetupOrganization] = useState("");
  const [githubSetupName, setGithubSetupName] = useState("Netra PR Reviewer");
  const [githubImport, setGithubImport] = useState<GithubImportDraft>({
    app_id: "", installation_id: "", client_id: "", slug: "", private_key: "", webhook_secret: "",
  });
  const [githubSetupBusy, setGithubSetupBusy] = useState(false);
  const [githubInstallUrl, setGithubInstallUrl] = useState("");
  const githubManifestHandled = useRef(false);
  const refreshInFlight = useRef(false);
  const [resources, setResources] = useState<ResourceOverview | null>(null);
  const [expandedRepository, setExpandedRepository] = useState<string | null>(null);
  const [previewConfigs, setPreviewConfigs] = useState<Record<string, PreviewEnvironmentConfig>>({});
  const [repositoryPolicyConfigs, setRepositoryPolicyConfigs] = useState<Record<string, RepositoryPolicy>>({});
  const [prTab, setPrTab] = useState<"overview" | "timeline" | "runs" | "tests" | "feedback" | "preview">("overview");
  const [undeployingIds, setUndeployingIds] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    if (!selected) {
      setPreviewDiagnostics(null);
      return;
    }
    let cancelled = false;
    const load = async () => {
      const response = await fetch(`/api/v1/previews/${selected.id}/diagnostics`, { cache: "no-store" });
      if (!cancelled && response.ok) setPreviewDiagnostics((await response.json()) as PreviewDiagnostics);
    };
    void load();
    const active = !terminalStates.has(selected.status) && !["active", "stale"].includes(selected.status);
    const interval = active ? window.setInterval(() => void load(), 3000) : undefined;
    return () => {
      cancelled = true;
      if (interval !== undefined) window.clearInterval(interval);
    };
  }, [selected?.id, selected?.status]);

  // Loaded in two waves. The first is only what the shell needs to paint; the
  // second — history, host resources, the GitHub catalogue — starts once the
  // first has landed and fills in around a page that is already interactive.
  // Both the split and the ordering matter. Clearing one `loading` flag in a
  // `finally` after all nineteen responses let the slowest of them set the
  // time to first paint; issuing all nineteen at once then let the eleven
  // heavy ones occupy a connection budget the other eight were waiting on.
  const refresh = async (includeHistory = true) => {
    const repositoryPolicyEpoch = repositoryPolicyEpochRef.current;
    const portalRepositoryEpoch = portalRepositoryEpochRef.current;
    // The ten second poll can outrun a slow pass. Letting passes overlap piles
    // concurrent work onto a backend that serialises its database access, so
    // each one makes the next slower still.
    if (refreshInFlight.current) return;
    refreshInFlight.current = true;
    try {
      const healthPromise = fetchRuntimeHealth();
      const previewPromise = fetch("/api/v1/previews?limit=100");
      const repositoryPromise = fetch("/api/v1/repositories");
      const reviewsPromise = fetch(`/api/v1/review-jobs?limit=${includeHistory ? 2000 : 100}`);
      const issueJobsPromise = fetch(`/api/v1/issue-jobs?limit=${includeHistory ? 2000 : 100}`);
      const runtimePromise = fetch("/api/v1/runtime", { cache: "no-store" });
      const inventoryPromise = fetch("/api/v1/inventory");
      const portalRepositoryPromise = portalApi.listRepositories().catch(() => null);
      const [healthy, previewResponse, repositoryResponse, reviewsResponse, issueJobsResponse, runtimeResponse, inventoryResponse, portalRepositoryList] = await Promise.all([
        healthPromise,
        previewPromise,
        repositoryPromise,
        reviewsPromise,
        issueJobsPromise,
        runtimePromise,
        inventoryPromise,
        portalRepositoryPromise,
      ]);
      setHealthy(healthy);
      if (!previewResponse.ok) throw new Error(`API ${previewResponse.status}`);
      const data = (await previewResponse.json()) as Preview[];
      setPreviews(data);
      if (repositoryResponse.ok) {
        const refreshedRepositories = (await repositoryResponse.json()) as RepositorySetting[];
        if (
          repositoryPolicyEpoch === repositoryPolicyEpochRef.current
          && repositoryPolicySavingRef.current.size === 0
        ) {
          setRepositories(refreshedRepositories);
        }
      }
      if (
        portalRepositoryList
        && portalRepositoryEpoch === portalRepositoryEpochRef.current
        && portalRepositorySavingRef.current.size === 0
      ) {
        setPortalRepositories(portalRepositoryList);
      }
      if (reviewsResponse.ok) {
        const jobs = (await reviewsResponse.json()) as ReviewJob[];
        setReviewJobs((current) => includeHistory
          ? jobs
          : [...jobs, ...current.filter((prior) => !jobs.some((job) => job.id === prior.id))]);
      }
      if (issueJobsResponse.ok) {
        const jobs = (await issueJobsResponse.json()) as IssueJob[];
        setIssueJobs((current) => includeHistory
          ? jobs
          : [...jobs, ...current.filter((prior) => !jobs.some((job) => job.id === prior.id))]);
      }
      if (runtimeResponse.ok) {
        setRuntime((await runtimeResponse.json()) as RuntimeOverview);
      }
      if (inventoryResponse.ok) {
        setInventory((await inventoryResponse.json()) as Inventory);
      }
      if (selected) {
        setSelected(data.find((item) => item.id === selected.id) ?? selected);
      }
      setError("");
      // The shell has everything it needs; stop showing skeletons.
      setLoading(false);

      // Only started now that the shell has painted. Issuing them alongside the
      // batch above looked like it cost nothing -- the page still only awaited
      // the first eight -- but a browser opens about six connections per
      // origin, so eleven history requests asking for thousands of rows each
      // were taking those connections first and the shell's own eight queued
      // behind them. Held back, the responses first paint depends on get the
      // whole connection budget, and this second wave fills in around a page
      // that is already interactive.
      const [findingsResponse, eventsResponse, completedResponse, settingsResponse, githubResponse, githubRepositoriesResponse, resourceResponse, testQualityResponse, incidentsResponse, teamsResponse, teamsRequestsResponse] = await Promise.all([
        includeHistory ? fetch("/api/v1/findings?limit=2000").catch(() => null) : null,
        includeHistory ? fetch("/api/v1/events?limit=5000").catch(() => null) : null,
        includeHistory ? fetch("/api/v1/completed-prs?limit=500").catch(() => null) : null,
        includeHistory ? fetch("/api/v1/settings").catch(() => null) : null,
        includeHistory ? fetch("/api/v1/github/status").catch(() => null) : null,
        includeHistory ? fetch("/api/v1/github/repositories").catch(() => null) : null,
        fetch("/api/v1/resources", { cache: "no-store" }).catch(() => null),
        includeHistory ? fetch("/api/v1/test-quality?limit=1000").catch(() => null) : null,
        fetch("/api/v1/incidents", { cache: "no-store" }).catch(() => null),
        includeHistory ? fetch("/api/v1/integrations/teams/status").catch(() => null) : null,
        fetch("/api/v1/integrations/teams/requests?limit=2000", { cache: "no-store" }).catch(() => null),
      ]);
      if (findingsResponse?.ok) {
        setFindings((await findingsResponse.json()) as Finding[]);
      }
      if (eventsResponse?.ok) {
        setEvents((await eventsResponse.json()) as RuntimeEvent[]);
      }
      if (completedResponse?.ok) {
        setCompletedPulls((await completedResponse.json()) as CompletedPullRequest[]);
      }
      if (settingsResponse?.ok) {
        setRuntimeSettings((await settingsResponse.json()) as RuntimeSettings);
      }
      if (githubResponse?.ok) {
        setGithubStatus((await githubResponse.json()) as GithubStatus);
      }
      if (githubRepositoriesResponse?.ok) {
        setGithubRepositories((await githubRepositoriesResponse.json()) as GithubRepository[]);
      }
      if (resourceResponse?.ok) {
        setResources((await resourceResponse.json()) as ResourceOverview);
      }
      if (testQualityResponse?.ok) {
        setTestQuality((await testQualityResponse.json()) as TestQuality);
      }
      if (incidentsResponse?.ok) {
        setIncidents((await incidentsResponse.json()) as OperationalIncident[]);
      }
      if (teamsResponse?.ok) {
        setTeamsStatus((await teamsResponse.json()) as TeamsStatus);
      }
      if (teamsRequestsResponse?.ok) {
        setTeamsHistory((await teamsRequestsResponse.json()) as TeamsRequestHistory);
      }
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Could not reach Netra");
    } finally {
      setLoading(false);
      // In `finally` for the same reason `loading` is: a pass that failed has
      // stopped loading, and a panel that waits forever on a request that is
      // never coming back is worse than one showing what it has.
      setHistoryLoading(false);
      refreshInFlight.current = false;
    }
  };

  useEffect(() => {
    void refresh();
    const timer = window.setInterval(() => void refresh(false), 10_000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    if (githubManifestHandled.current) return;
    const query = new URLSearchParams(window.location.search);
    const code = query.get("code");
    const state = query.get("github_manifest_state");
    if (!code || !state) return;
    githubManifestHandled.current = true;
    setGithubSetupBusy(true);
    void fetch("/api/v1/github/manifest/convert", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ code, state }),
    }).then(async (response) => {
      const body = await response.json().catch(() => ({})) as { error?: string; install_url?: string; app_id?: string; slug?: string };
      if (!response.ok) throw new Error(body.error ?? `GitHub setup failed (${response.status})`);
      window.history.replaceState({}, "", "/settings/integrations");
      setSettingsTab("integrations");
      setView("settings");
      setGithubSetupOpen(true);
      setGithubInstallUrl(body.install_url ?? "");
      setGithubImport((current) => ({ ...current, app_id: body.app_id ?? current.app_id, slug: body.slug ?? current.slug }));
      setNotice(body.install_url
        ? "GitHub App created securely. Install it, enter the installation ID below, then restart Netra."
        : "GitHub App credentials saved. Restart Netra to apply them.");
      setRestartPending(true);
    }).catch((cause) => setError(cause instanceof Error ? cause.message : "GitHub setup failed"))
      .finally(() => setGithubSetupBusy(false));
  }, []);

  useEffect(() => {
    let cancelled = false;
    const applyRoute = async () => {
      const route = parseAppRoute(window.location.pathname);
      if (route.view === "pr" || route.view === "issue") {
        const endpoint = route.view === "pr" ? "prs" : "issues";
        const response = await fetch(
          `/api/v1/${endpoint}/${encodeURIComponent(route.owner)}/${encodeURIComponent(route.repository)}/${route.number}`,
        );
        if (cancelled) return;
        if (!response.ok) {
          setError(`Could not load ${route.view === "pr" ? "PR" : "Issue"} details (${response.status})`);
          return;
        }
        if (route.view === "pr") {
          setPrDetail((await response.json()) as PullRequestDetail);
          setPrTab("overview");
        } else {
          setIssueDetail((await response.json()) as IssueDetail);
        }
        setView(route.view);
        return;
      }
      setView(route.view);
      if (route.view === "work") setWorkTab(route.workTab);
      if (route.view === "insights") setInsightTab(route.insightTab);
      if (route.view === "quality") {
        setQualityTab(route.qualityTab);
        if (route.repository) setTestRepository(route.repository);
        if (window.location.pathname.startsWith("/quality")) {
          const canonicalPath = route.qualityTab === "repositories"
            ? qualityRepositoryPath(route.repository ?? "")
            : `/insights/quality/${route.qualityTab}`;
          window.history.replaceState(null, "", canonicalPath);
        }
      }
      if (route.view === "settings") setSettingsTab(route.settingsTab);
    };
    const onPopState = () => { void applyRoute(); };
    void applyRoute();
    window.addEventListener("popstate", onPopState);
    return () => {
      cancelled = true;
      window.removeEventListener("popstate", onPopState);
    };
  }, []);

  const stats = useMemo(() => {
    const active = previews.filter((item) =>
      ["active", "stale"].includes(item.status),
    ).length;
    const building = previews.filter((item) =>
      ["reserved", "queued", "building", "starting", "health_checking"].includes(
        item.status,
      ),
    ).length;
    const failed = previews.filter((item) => item.status === "failed").length;
    return { active, building, failed, total: previews.length };
  }, [previews]);
  const livePreviews = useMemo(
    () => previews.filter((preview) => [
      "reserved",
      "queued",
      "building",
      "starting",
      "health_checking",
      "active",
      "stale",
      "undeploying",
    ].includes(preview.status)),
    [previews],
  );
  const previewsByPull = useMemo(() => {
    const byPull = new Map<string, Preview[]>();
    const newestFirst = [...previews].sort((left, right) =>
      String(right.updated_at).localeCompare(String(left.updated_at))
    );
    for (const preview of newestFirst) {
      const key = `${repoName(preview)}#${preview.number}`;
      const current = byPull.get(key) ?? [];
      current.push(preview);
      byPull.set(key, current);
    }
    return byPull;
  }, [previews]);
  const repositoryPolicies = useMemo(
    () => new Map(repositories.map((repository) => [repository.repository, repository])),
    [repositories],
  );

  const orderedPulls = useMemo(() => {
    const rank: Record<string, number> = {
      act_now: 0,
      problem: 1,
      waiting: 2,
      fine: 3,
      ignore: 4,
    };
    return [...inventory.pull_requests].sort((left, right) => {
      const leftRank = rank[left.health?.bucket ?? "waiting"] ?? 5;
      const rightRank = rank[right.health?.bucket ?? "waiting"] ?? 5;
      if (leftRank !== rightRank) return leftRank - rightRank;
      return String(right.updated_at).localeCompare(String(left.updated_at));
    });
  }, [inventory.pull_requests]);
  const prInventoryFilter = useMemo(
    () => ({
      user: prFilterUser,
      status: prFilterStatus,
      repository: prFilterRepository,
      ci: prFilterCi,
    }),
    [prFilterCi, prFilterRepository, prFilterStatus, prFilterUser],
  );
  const filteredPulls = useMemo(
    () => orderedPulls.filter((pull) => matchesPullInventoryFilter(pull, prInventoryFilter)),
    [orderedPulls, prInventoryFilter],
  );
  const groupedPulls = useMemo(
    () => groupPullRequests(filteredPulls, prGroupMode),
    [filteredPulls, prGroupMode],
  );
  const prFilterUsers = useMemo(
    () => uniqueWorkLabels(inventory.pull_requests.map((pull) => pullAuthorLabel(pull.author))),
    [inventory.pull_requests],
  );
  const prFilterStatuses = useMemo(
    () => uniqueWorkLabels(inventory.pull_requests.map((pull) => pullStatusLabel(pull))),
    [inventory.pull_requests],
  );
  const prFilterRepositories = useMemo(
    () => uniqueWorkLabels(inventory.pull_requests.map((pull) => pull.repository)),
    [inventory.pull_requests],
  );
  const prFilterCiStates = useMemo(
    () => uniqueWorkLabels(inventory.pull_requests.map((pull) => pull.check_summary?.state ?? "none")),
    [inventory.pull_requests],
  );
  const prFiltersActive = Boolean(prFilterUser || prFilterStatus || prFilterRepository || prFilterCi);
  const issueInventoryFilter = useMemo(
    () => ({ user: issueFilterUser, repository: issueFilterRepository }),
    [issueFilterRepository, issueFilterUser],
  );
  const filteredIssues = useMemo(
    () => inventory.issues.filter((issue) => matchesIssueInventoryFilter(issue, issueInventoryFilter)),
    [inventory.issues, issueInventoryFilter],
  );
  const groupedIssues = useMemo(
    () => groupIssues(filteredIssues, issueGroupMode),
    [filteredIssues, issueGroupMode],
  );
  const issueFilterUsers = useMemo(
    () => uniqueWorkLabels(inventory.issues.map((issue) => issueAuthorLabel(issue.author))),
    [inventory.issues],
  );
  const issueFilterRepositories = useMemo(
    () => uniqueWorkLabels(inventory.issues.map((issue) => issue.repository)),
    [inventory.issues],
  );
  const issueFiltersActive = Boolean(issueFilterUser || issueFilterRepository);
  function togglePrGroup(groupKey: string) {
    setCollapsedPrGroups((current) => {
      const next = new Set(current);
      if (next.has(groupKey)) next.delete(groupKey);
      else next.add(groupKey);
      return next;
    });
  }

  const allRuns = useMemo(
    () => [...reviewJobs.map((job) => ({ ...job, entity_type: "pr" as const })), ...issueJobs]
      .sort((left, right) => right.created_at.localeCompare(left.created_at)),
    [reviewJobs, issueJobs],
  );
  const activeRuns = useMemo(
    () => allRuns.filter((job) => ["queued", "running", "finalizing"].includes(job.status)),
    [allRuns],
  );
  const recentCompletedRuns = useMemo(
    () => recentTerminalReviewRuns(allRuns, 20),
    [allRuns],
  );
  const activeIncidents = useMemo(
    () => incidents.filter((incident) => !incident.acknowledged_at),
    [incidents],
  );
  const activeRunAuthors = useMemo(() => {
    const authors = new Map<string, string>();
    for (const pull of inventory.pull_requests) {
      if (pull.author) authors.set(`pr:${pull.repository}#${pull.number}`, pull.author);
    }
    for (const issue of inventory.issues) {
      if (issue.author) authors.set(`issue:${issue.repository}#${issue.number}`, issue.author);
    }
    return authors;
  }, [inventory.pull_requests, inventory.issues]);
  const unlistedActivePullRuns = useMemo(() => {
    const openPulls = new Set(
      inventory.pull_requests.map((pull) => `${pull.repository}#${pull.number}`),
    );
    return activeRuns.filter((job) =>
      job.entity_type === "pr" && !openPulls.has(`${job.repository}#${job.number}`)
    );
  }, [activeRuns, inventory.pull_requests]);
  const throughputData = useMemo(
    () => reviewThroughput(reviewJobs),
    [reviewJobs],
  );
  const outcomeData = useMemo(
    () => outcomeBreakdown(reviewJobs),
    [reviewJobs],
  );
  const providerUsageMeters = useMemo(() => {
    const reviewerSettings = resolveReviewerSettings(runtimeSettings);
    return usageMeters(
      runtime?.legacy_analytics?.live_usage ?? {},
      dispatchReviewers(
        reviewerSettings.natives,
        unionReviewerModels(
          reviewerSettings.models.deep,
          reviewerSettings.models.standard,
          reviewerSettings.models.fast,
        ),
      ),
    );
  }, [runtime?.legacy_analytics?.live_usage, runtimeSettings]);
  const allTimeAnalyticsDays = useMemo(
    () => analyticsHistoryDays([
      ...inventory.pull_requests.map((pull) => pull.created_at),
      ...completedPulls.flatMap((pull) => [
        pull.created_at,
        pull.merged_at,
        pull.completed_at,
      ]),
      ...reviewJobs.map((job) => job.created_at),
      ...findings.map((finding) => finding.created_at),
      ...teamsHistory.requests.flatMap((request) => [request.created_at, request.issue_created_at]),
    ]),
    [completedPulls, findings, inventory.pull_requests, reviewJobs, teamsHistory.requests],
  );
  const analyticsDays = analyticsRange === "all" ? allTimeAnalyticsDays : 14;
  const analyticsRangeLabel = analyticsRange === "all" ? "ALL TIME" : "14 DAY";
  const analyticsRangeShortLabel = analyticsRange === "all" ? "all time" : "14d";
  const analyticsFilter = useMemo(
    () => ({ repository: analyticsRepository, user: analyticsUser }),
    [analyticsRepository, analyticsUser],
  );
  const analyticsAuthorByPull = useMemo(
    () => analyticsAuthorIndex(inventory.pull_requests, completedPulls),
    [completedPulls, inventory.pull_requests],
  );
  const analyticsFilterIdentities = useMemo(() => [
    ...inventory.pull_requests.map((pull) => ({ repository: pull.repository, author: pull.author })),
    ...completedPulls.map((pull) => ({ repository: pull.repo, author: pull.author })),
    ...teamsHistory.requests.map((request) => ({
      repository: request.repository,
      author: request.requester,
    })),
  ], [completedPulls, inventory.pull_requests, teamsHistory.requests]);
  const analyticsRepositoryOptions = useMemo(
    () => analyticsRepositoryChoices([
      ...repositories.map((repository) => repository.repository),
      ...analyticsFilterIdentities.map((item) => item.repository),
      ...reviewJobs.map((job) => job.repository),
      analyticsRepository,
    ]),
    [analyticsFilterIdentities, analyticsRepository, repositories, reviewJobs],
  );
  const analyticsUserOptions = useMemo(() => {
    const names = analyticsUserChoices(analyticsFilterIdentities, analyticsFilter);
    if (analyticsUser && !names.some((name) => name.toLocaleLowerCase() === analyticsUser.toLocaleLowerCase())) {
      return [...names, analyticsUser].sort((left, right) =>
        left.localeCompare(right, undefined, { sensitivity: "base" })
      );
    }
    return names;
  }, [analyticsFilter, analyticsFilterIdentities, analyticsUser]);
  const analyticsOpenPulls = useMemo(
    () => filterAnalyticsItems(
      inventory.pull_requests,
      analyticsFilter,
      (pull) => ({ repository: pull.repository, author: pull.author }),
    ),
    [analyticsFilter, inventory.pull_requests],
  );
  const analyticsCompletedPulls = useMemo(
    () => filterAnalyticsItems(
      completedPulls,
      analyticsFilter,
      (pull) => ({ repository: pull.repo, author: pull.author }),
    ),
    [analyticsFilter, completedPulls],
  );
  const analyticsReviewJobs = useMemo(
    () => filterAnalyticsItems(
      reviewJobs,
      analyticsFilter,
      (job) => ({
        repository: job.repository,
        author: analyticsAuthorByPull.get(`${job.repository}#${job.number}`),
      }),
    ),
    [analyticsAuthorByPull, analyticsFilter, reviewJobs],
  );
  const analyticsFindings = useMemo(
    () => filterAnalyticsItems(
      findings,
      analyticsFilter,
      (finding) => ({
        repository: finding.repository,
        author: analyticsAuthorByPull.get(`${finding.repository}#${finding.number}`),
      }),
    ),
    [analyticsAuthorByPull, analyticsFilter, findings],
  );
  const analyticsEvents = useMemo(
    () => filterAnalyticsItems(
      events,
      analyticsFilter,
      (event) => ({
        repository: event.repository,
        author: event.repository != null && event.number != null
          ? analyticsAuthorByPull.get(`${event.repository}#${event.number}`)
          : undefined,
      }),
    ),
    [analyticsAuthorByPull, analyticsFilter, events],
  );
  const analyticsTeamsRequests = useMemo(
    () => filterAnalyticsItems(
      teamsHistory.requests,
      analyticsFilter,
      (request) => ({ repository: request.repository, author: request.requester }),
    ),
    [analyticsFilter, teamsHistory.requests],
  );
  const analyticsFilterSummary = analyticsRepository || analyticsUser
    ? ` · ${analyticsRepository || "all repositories"} · ${analyticsUser || "all users"}`
    : "";
  const teamsAnalytics = useMemo(() => {
    const now = new Date();
    const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
    start.setUTCDate(start.getUTCDate() - Math.max(0, analyticsDays - 1));
    const rows = Array.from({ length: analyticsDays }, (_, index) => {
      const day = new Date(start);
      day.setUTCDate(start.getUTCDate() + index);
      return {
        day: day.toISOString().slice(0, 10),
        label: day.toLocaleDateString(undefined, { month: "short", day: "numeric", timeZone: "UTC" }),
        requests: 0,
        issues: 0,
        cancelled: 0,
      };
    });
    const byDay = new Map(rows.map((row) => [row.day, row]));
    const completionSeconds: number[] = [];
    const requesters = new Set<string>();
    for (const request of analyticsTeamsRequests) {
      const createdAt = Date.parse(request.created_at);
      if (!Number.isFinite(createdAt) || createdAt < start.getTime()) continue;
      const row = byDay.get(new Date(createdAt).toISOString().slice(0, 10));
      if (!row) continue;
      row.requests += 1;
      if (request.state === "cancelled") row.cancelled += 1;
      requesters.add(request.requester);
      if (request.issue_created_at) {
        row.issues += 1;
        const completedAt = Date.parse(request.issue_created_at);
        if (Number.isFinite(completedAt) && completedAt >= createdAt) {
          completionSeconds.push((completedAt - createdAt) / 1000);
        }
      }
    }
    const requests = rows.reduce((total, row) => total + row.requests, 0);
    const issues = rows.reduce((total, row) => total + row.issues, 0);
    return {
      rows,
      requests,
      issues,
      cancelled: rows.reduce((total, row) => total + row.cancelled, 0),
      requesters: requesters.size,
      active: analyticsTeamsRequests.filter((request) =>
        request.state !== "completed" && request.state !== "cancelled"
      ).length,
      completionRate: requests ? issues / requests * 100 : 0,
      averageTimeToIssueSeconds: completionSeconds.length
        ? completionSeconds.reduce((total, seconds) => total + seconds, 0) / completionSeconds.length
        : 0,
    };
  }, [analyticsDays, analyticsTeamsRequests]);
  const prFlowData = useMemo(
    () => pullRequestFlow(analyticsOpenPulls, analyticsCompletedPulls, new Date(), analyticsDays),
    [analyticsDays, analyticsCompletedPulls, analyticsOpenPulls],
  );
  const reviewDurationData = useMemo(
    () => reviewDurationTrend(analyticsReviewJobs, new Date(), analyticsDays),
    [analyticsDays, analyticsReviewJobs],
  );
  const reviewImpactData = useMemo(
    () => reviewImpactTrend(analyticsReviewJobs, analyticsFindings, new Date(), analyticsDays),
    [analyticsDays, analyticsFindings, analyticsReviewJobs],
  );
  const mergeLeadTimeData = useMemo(
    () => mergeLeadTimeTrend(analyticsCompletedPulls, new Date(), analyticsDays),
    [analyticsDays, analyticsCompletedPulls],
  );
  const priorityTrendData = useMemo(
    () => findingPriorityTrend(analyticsFindings, new Date(), analyticsDays),
    [analyticsDays, analyticsFindings],
  );
  const providerAttemptData = useMemo(
    () => providerAttemptTrend(analyticsEvents, new Date(), analyticsDays),
    [analyticsDays, analyticsEvents],
  );
  const reviewCycleData = useMemo(
    () => reviewCycleDistribution(
      analyticsOpenPulls,
      analyticsCompletedPulls,
      new Date(),
      analyticsDays,
    ),
    [analyticsDays, analyticsCompletedPulls, analyticsOpenPulls],
  );
  const threadHealthData = useMemo(
    () => threadHealthByRepository(
      analyticsOpenPulls,
      analyticsReviewJobs,
      new Date(),
      analyticsDays,
    ),
    [analyticsDays, analyticsOpenPulls, analyticsReviewJobs],
  );
  const testRepositories = useMemo(
    () => testTotalsByRepository(testQuality.releases),
    [testQuality.releases],
  );
  const testRepositoryOptions = useMemo(() => {
    const reported = new Map(testRepositories.map((row) => [row.repository, row]));
    const names = new Set([
      ...repositories.map((repository) => repository.repository),
      ...reported.keys(),
    ]);
    return [...names]
      .sort((left, right) => left.localeCompare(right))
      .map((repository) => ({ repository, report: reported.get(repository) ?? null }));
  }, [repositories, testRepositories]);
  const repositoriesAwaitingReports = useMemo(
    () => testRepositoryOptions.filter((option) => option.report == null),
    [testRepositoryOptions],
  );
  // Default to the repository with the most release history so the release
  // charts are never empty when data exists for some other repository.
  const selectedTestRepository = useMemo(() => {
    if (testRepository && testRepositoryOptions.some((row) => row.repository === testRepository)) {
      return testRepository;
    }
    return [...testRepositories]
      .sort((left, right) => right.releases - left.releases || right.total - left.total)
      .at(0)?.repository ?? testRepositoryOptions.at(0)?.repository ?? "";
  }, [testRepositories, testRepository, testRepositoryOptions]);
  const testReleaseData = useMemo(
    () => testReleaseSeries(testQuality.releases, selectedTestRepository),
    [selectedTestRepository, testQuality.releases],
  );
  const testOwnerData = useMemo(
    () => testOwnershipLeaderboard(testQuality.owners),
    [testQuality.owners],
  );
  const coverageRepositories = useMemo(
    () => coverageByRepository(testQuality.releases),
    [testQuality.releases],
  );
  const coverageReleaseData = useMemo(
    () => coverageReleaseSeries(testQuality.releases, selectedTestRepository),
    [selectedTestRepository, testQuality.releases],
  );
  const portfolioData = useMemo(
    () => portfolioTrend(testQuality.releases),
    [testQuality.releases],
  );
  /// Overview's condensed coverage read. Derived from the same releases as the
  /// Test quality portfolio so the two pages cannot drift apart.
  const coverageDigest = useMemo(
    () => overviewCoverageDigest(testQuality.releases),
    [testQuality.releases],
  );
  const portfolio = useMemo(
    () => portfolioSummary(
      testQuality.releases,
      testQuality.repositories,
      testQuality.owners,
    ),
    [testQuality.owners, testQuality.releases, testQuality.repositories],
  );
  const testSummary = useMemo(
    () => testQualitySummary(
      testQuality.releases,
      testQuality.repositories,
      testQuality.owners,
    ),
    [testQuality.owners, testQuality.releases, testQuality.repositories],
  );
  const repositoryCoverageAreas = testDetail?.coverage_areas ?? [];
  // Read straight off the server rollup. Deriving these by summing
  // `coverage_files` understated the rate by design: that list is capped and
  // sorted worst-first, so the sum only ever saw the least-covered files.
  const repositoryCoverageSummary = useMemo(() => {
    const summary = testDetail?.coverage_summary ?? null;
    return {
      files: summary?.files ?? 0,
      shards: summary?.shards ?? 0,
      directories: summary?.directories ?? 0,
      linesValid: summary?.lines_valid ?? 0,
      linesCovered: summary?.lines_covered ?? 0,
      uncoveredLines: summary?.uncovered_lines ?? 0,
      lineRate: summary && summary.lines_valid > 0
        ? Math.round(summary.line_rate * 10) / 10
        : null,
      releaseTag: summary?.release_tag ?? null,
      listed: testDetail?.coverage_files?.length ?? 0,
    };
  }, [testDetail?.coverage_summary, testDetail?.coverage_files]);
  const repositoryTestOwners = useMemo(
    () => testOwners(testDetail?.tests ?? []),
    [testDetail?.tests],
  );
  const repositoryTestStatus = useMemo(() => {
    const tests = testDetail?.tests ?? [];
    const passed = tests.filter((test) => test.status === "passed").length;
    const failing = tests.filter((test) => test.status === "failed" || test.status === "errored").length;
    return {
      total: tests.length,
      passed,
      failing,
      skipped: Math.max(0, tests.length - passed - failing),
      attributed: tests.filter((test) => test.owner != null).length,
      failures: tests.filter((test) => test.status === "failed" || test.status === "errored"),
    };
  }, [testDetail?.tests]);
  const analyticsSummary = useMemo(() => {
    const created = prFlowData.reduce((total, row) => total + row.created, 0);
    const merged = prFlowData.reduce((total, row) => total + row.merged, 0);
    const reviewedRuns = reviewDurationData.reduce((total, row) => total + row.runs, 0);
    const reviewMinutes = reviewDurationData.reduce(
      (total, row) => total + row.averageMinutes * row.runs,
      0,
    );
    const reviewedPulls = reviewImpactData.reduce(
      (total, row) => total + row.pullRequests,
      0,
    );
    const findingCount = reviewImpactData.reduce(
      (total, row) => total + row.findings,
      0,
    );
    const mergeCount = mergeLeadTimeData.reduce((total, row) => total + row.merges, 0);
    const mergeHours = mergeLeadTimeData.reduce(
      (total, row) => total + row.averageHours * row.merges,
      0,
    );
    const providerSucceeded = providerAttemptData.reduce(
      (total, row) => total + row.succeeded,
      0,
    );
    const providerFailed = providerAttemptData.reduce(
      (total, row) => total + row.failed,
      0,
    );
    const resolvedThreads = threadHealthData.reduce(
      (total, row) => total + row.resolved,
      0,
    );
    const unresolvedThreads = analyticsOpenPulls.reduce(
      (total, pull) => total + (pull.unresolved_threads ?? 0),
      0,
    );
    return {
      created,
      merged,
      averageReviewMinutes: reviewedRuns ? reviewMinutes / reviewedRuns : 0,
      averageMergeHours: mergeCount ? mergeHours / mergeCount : 0,
      findingsPerPr: reviewedPulls ? findingCount / reviewedPulls : 0,
      providerSuccessRate: providerSucceeded + providerFailed
        ? providerSucceeded / (providerSucceeded + providerFailed) * 100
        : 0,
      resolvedThreads,
      unresolvedThreads,
    };
  }, [
    analyticsOpenPulls,
    mergeLeadTimeData,
    prFlowData,
    providerAttemptData,
    reviewDurationData,
    reviewImpactData,
    threadHealthData,
  ]);

  const repositoryWorkload = useMemo(() => {
    const workload = new Map<string, {
      pulls: number;
      issues: number;
      threads: number;
      runs: number;
      findings: number;
    }>();
    const row = (repository: string) => {
      const current = workload.get(repository) ?? {
        pulls: 0,
        issues: 0,
        threads: 0,
        runs: 0,
        findings: 0,
      };
      workload.set(repository, current);
      return current;
    };
    for (const pull of inventory.pull_requests) {
      const current = row(pull.repository);
      current.pulls += 1;
      current.threads += pull.unresolved_threads ?? 0;
    }
    for (const issue of inventory.issues) row(issue.repository).issues += 1;
    for (const job of [...reviewJobs, ...issueJobs]) row(job.repository).runs += 1;
    for (const finding of findings) row(finding.repository).findings += 1;
    return workload;
  }, [findings, inventory.issues, inventory.pull_requests, issueJobs, reviewJobs]);

  const issueRunSummary = useMemo(() => {
    const summaries = new Map<string, { count: number; latest: IssueJob }>();
    for (const job of issueJobs) {
      const key = `${job.repository}#${job.number}`;
      const current = summaries.get(key);
      if (!current) {
        summaries.set(key, { count: 1, latest: job });
      } else {
        current.count += 1;
        if (job.created_at > current.latest.created_at) current.latest = job;
      }
    }
    return summaries;
  }, [issueJobs]);

  // The per-test list is only fetched when a repository's tests are opened;
  // a full suite is far larger than the cross-repository rollup.
  useEffect(() => {
    if (view !== "quality" || qualityTab !== "repositories" || !selectedTestRepository) {
      return;
    }
    if (testDetail?.repository === selectedTestRepository) return;
    let cancelled = false;
    const load = async () => {
      const [owner, name] = selectedTestRepository.split("/");
      const response = await fetch(
        `/api/v1/test-quality/${encodeURIComponent(owner)}/${encodeURIComponent(name)}`,
      );
      if (cancelled || !response.ok) return;
      setTestDetail((await response.json()) as RepositoryTestDetail);
    };
    void load();
    return () => { cancelled = true; };
  }, [qualityTab, selectedTestRepository, testDetail?.repository, view]);

  const refreshTestQuality = async () => {
    setTestRefreshing(true);
    setError("");
    try {
      const response = await fetch("/api/v1/test-quality/refresh", { method: "POST" });
      if (!response.ok) throw new Error(`Refresh failed (${response.status})`);
      const summary = (await response.json()) as {
        ingested?: unknown[];
        attributed?: { attributed?: number };
        errors?: string[];
      };
      const ingested = summary.ingested?.length ?? 0;
      const attributed = summary.attributed?.attributed ?? 0;
      setNotice(
        `Ingested ${ingested} release ${ingested === 1 ? "report" : "reports"} and attributed ${attributed} ${attributed === 1 ? "test" : "tests"}.`
        + (summary.errors?.length ? ` ${summary.errors.length} repository issue(s) reported.` : ""),
      );
      setTestDetail(null);
      await refresh();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Could not refresh test quality");
    } finally {
      setTestRefreshing(false);
    }
  };

  const refreshProviderUsage = async () => {
    setProviderUsageRefreshing(true);
    setError("");
    try {
      const refreshedRuntime = await requestProviderUsageRefresh<RuntimeOverview>();
      setRuntime(refreshedRuntime);
      setNotice(providerUsageRefreshNotice(refreshedRuntime));
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Could not refresh provider usage");
    } finally {
      setProviderUsageRefreshing(false);
    }
  };

  const syncGithubInventory = async () => {
    setInventorySyncing(true);
    setError("");
    try {
      const response = await fetch("/api/v1/inventory/refresh", { method: "POST" });
      const body = await response.json().catch(() => ({})) as Inventory & { error?: string };
      if (!response.ok) {
        setError(body.error ?? `GitHub sync failed (${response.status})`);
        return;
      }
      setInventory(body);
      setNotice(
        `GitHub inventory synced: ${body.pull_requests.length} pull requests and ${body.issues.length} issues.`,
      );
      await refresh(true);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "GitHub sync failed");
    } finally {
      setInventorySyncing(false);
    }
  };

  const openPrTarget = async (repository: string, number: number) => {
    const [owner, name] = repository.split("/");
    if (!owner || !name) return;
    const response = await fetch(
      `/api/v1/prs/${encodeURIComponent(owner)}/${encodeURIComponent(name)}/${number}`,
    );
    if (!response.ok) {
      setError(`Could not load PR details (${response.status})`);
      return;
    }
    setPrDetail((await response.json()) as PullRequestDetail);
    setPrTab("overview");
    setView("pr");
    pushPath(pullRequestPath(repository, number));
  };

  const openPrDetail = async (pull: PullRequestInventory) => {
    await openPrTarget(pull.repository, pull.number);
  };

  const undeploy = async (target: UndeployTarget) => {
    if (!window.confirm(`Undeploy ${target.repository} PR #${target.number}?`))
      return;
    setError("");
    setNotice(`Undeploying ${target.repository} PR #${target.number}…`);
    setUndeployingIds((current) => new Set(current).add(target.id));
    setPreviews((current) => current.map((preview) => preview.id === target.id
      ? { ...preview, status: "undeploying" }
      : preview));
    setSelected((current) => current?.id === target.id
      ? { ...current, status: "undeploying" }
      : current);
    setResources((current) => current
      ? {
          ...current,
          previews: current.previews.map((preview) => preview.preview_id === target.id
            ? { ...preview, status: "undeploying" }
            : preview),
        }
      : current);
    try {
      const response = await fetch(
        `/api/v1/previews/${target.id}/undeploy`,
        { method: "POST" },
      );
      const body = await response.json().catch(() => ({})) as { error?: string };
      if (!response.ok) {
        setError(body.error ?? `Undeploy failed (${response.status})`);
        setNotice("");
        return;
      }
      setNotice(`Undeployed ${target.repository} PR #${target.number}.`);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Undeploy failed");
      setNotice("");
    } finally {
      setUndeployingIds((current) => {
        const next = new Set(current);
        next.delete(target.id);
        return next;
      });
      await refresh(false);
    }
  };

  const deploy = async (preview: Preview) => {
    if (!window.confirm(
      `Redeploy ${repoName(preview)} PR #${preview.number} at ${shortSha(preview.head_sha)}?`,
    )) return;
    setError("");
    const response = await fetch(`/api/v1/previews/${preview.id}/deploy`, {
      method: "POST",
    });
    const body = await response.json().catch(() => ({})) as { error?: string };
    if (!response.ok) {
      setError(body.error ?? `Deploy failed (${response.status})`);
      return;
    }
    setNotice(
      `Redeployment queued for ${repoName(preview)} PR #${preview.number} at ${shortSha(preview.head_sha)}.`,
    );
    setPrDetail((current) => current
      ? {
          ...current,
          previews: current.previews.map((candidate) => candidate.id === preview.id
            ? { ...candidate, status: "queued", error: undefined, expires_at: undefined }
            : candidate),
        }
      : current);
    setSelected((current) => current?.id === preview.id
      ? { ...current, status: "queued", error: undefined, expires_at: undefined }
      : current);
    await refresh(false);
  };

  const retry = async (preview: Preview) => {
    const response = await fetch(`/api/v1/previews/${preview.id}/retry`, {
      method: "POST",
    });
    if (!response.ok) {
      setError(`Retry failed (${response.status})`);
    }
    await refresh(false);
  };

  const setFindingFeedback = async (finding: Finding, status: NonNullable<Finding["feedback_status"]>) => {
    const reason = ["accepted", "dismissed"].includes(status)
      ? window.prompt(`Optional reason for marking this finding ${status}:`) ?? undefined
      : undefined;
    const response = await fetch(`/api/v1/findings/${finding.id}/feedback`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ status, reason }),
    });
    const body = await response.json().catch(() => ({})) as { error?: string };
    if (!response.ok) {
      setError(body.error ?? `Could not update finding (${response.status})`);
      return;
    }
    const update = (candidate: Finding) => candidate.id === finding.id
      ? { ...candidate, feedback_status: status, feedback_reason: reason }
      : candidate;
    setFindings((current) => current.map(update));
    setPrDetail((current) => current ? { ...current, findings: current.findings.map(update) } : current);
    setNotice(`Finding marked ${status}.`);
  };

  const acknowledgeIncident = async (incident: OperationalIncident) => {
    setError("");
    setAcknowledgingIncident(incident.fingerprint);
    try {
      const response = await fetch(`/api/v1/incidents/${encodeURIComponent(incident.fingerprint)}/ack`, { method: "POST" });
      if (!response.ok) {
        setError(`Could not acknowledge incident (${response.status})`);
        return;
      }
      setIncidents((current) => current.map((candidate) => candidate.fingerprint === incident.fingerprint
        ? { ...candidate, acknowledged_at: new Date().toISOString() }
        : candidate));
      setNotice(`Acknowledged: ${incident.title}`);
    } finally {
      setAcknowledgingIncident(null);
    }
  };

  const setRepositoryPolicy = async (
    repository: string,
    update: Partial<Pick<RepositorySetting, "reviews_enabled" | "previews_enabled" | "review_level">>,
  ) => {
    const [owner, name, ...extra] = repository.trim().split("/");
    if (!owner || !name || extra.length) {
      setError("Repository must use owner/name format");
      return;
    }
    const previous = repositories.find((candidate) => candidate.repository === repository);
    const optimistic = {
      ...update,
      ...((update.reviews_enabled === false) ? { previews_enabled: false } : {}),
    };
    repositoryPolicyEpochRef.current += 1;
    repositoryPolicySavingRef.current.add(repository);
    setError("");
    setRepositories((current) => current.map((candidate) =>
      candidate.repository === repository ? { ...candidate, ...optimistic } : candidate
    ));
    setRepositoryPolicySaving((current) => new Set(current).add(repository));
    try {
      const response = await fetch(
        `/api/v1/repositories/${encodeURIComponent(owner)}/${encodeURIComponent(name)}`,
        {
          method: "PUT",
          headers: { "content-type": "application/json" },
          body: JSON.stringify(update),
        },
      );
      if (!response.ok) throw new Error(`Repository update failed (${response.status})`);
      const saved = (await response.json()) as Pick<
        RepositorySetting,
        "repository" | "reviews_enabled" | "previews_enabled" | "review_level"
      >;
      setRepositories((current) => {
        const found = current.some((candidate) => candidate.repository === repository);
        const next = found
          ? current.map((candidate) =>
            candidate.repository === repository ? { ...candidate, ...saved } : candidate
          )
          : [...current, {
            ...saved,
            updated_at: new Date().toISOString(),
            revision_count: 0,
            active_count: 0,
          }];
        return next.sort((left, right) => left.repository.localeCompare(right.repository));
      });
      setRepositoryInput("");
      setGithubRepositories((current) => current.map((candidate) =>
        candidate.full_name === repository ? { ...candidate, selected: true } : candidate
      ));
    } catch (cause) {
      if (previous) {
        setRepositories((current) => current.map((candidate) =>
          candidate.repository === repository ? previous : candidate
        ));
      }
      setError(cause instanceof Error ? cause.message : "Repository update failed");
    } finally {
      repositoryPolicySavingRef.current.delete(repository);
      repositoryPolicyEpochRef.current += 1;
      setRepositoryPolicySaving((current) => {
        const next = new Set(current);
        next.delete(repository);
        return next;
      });
    }
  };

  const setStaffRequestsEnabled = async (
    repository: string,
    enabled: boolean,
    announce = true,
  ) => {
    const previous = portalRepositories.find((candidate) => candidate.repository === repository);
    const optimistic = portalRepositoryWithEnabled(repository, previous, enabled);
    portalRepositoryEpochRef.current += 1;
    portalRepositorySavingRef.current.add(repository);
    setPortalRepositorySaving((current) => new Set(current).add(repository));
    setPortalRepositories((current) => {
      const found = current.some((candidate) => candidate.repository === repository);
      const next = found
        ? current.map((candidate) => candidate.repository === repository ? optimistic : candidate)
        : [...current, optimistic];
      return next.sort((left, right) => left.repository.localeCompare(right.repository));
    });
    setError("");
    try {
      const saved = await portalApi.updateRepository(repository, {
        enabled,
        qa_config_path: optimistic.qa_config_path ?? ".netra/qa.toml",
        secret_aliases: optimistic.secret_aliases ?? [],
        retention_days: optimistic.retention_days ?? 30,
        max_concurrency: optimistic.max_concurrency ?? 1,
        updated_at: optimistic.updated_at,
      });
      setPortalRepositories((current) => {
        const next = current.filter((candidate) => candidate.repository !== repository);
        next.push(saved);
        return next.sort((left, right) => left.repository.localeCompare(right.repository));
      });
      if (announce) {
        setNotice(enabled
          ? `${repository} can now receive staff requests.`
          : `New staff requests are disabled for ${repository}.`);
      }
      return true;
    } catch (reason) {
      setPortalRepositories((current) => previous
        ? current.map((candidate) => candidate.repository === repository ? previous : candidate)
        : current.filter((candidate) => candidate.repository !== repository));
      setError(reason instanceof Error ? reason.message : "Could not update staff request access");
      return false;
    } finally {
      portalRepositorySavingRef.current.delete(repository);
      setPortalRepositorySaving((current) => {
        const next = new Set(current);
        next.delete(repository);
        return next;
      });
    }
  };

  const openPreviewConfig = async (repository: string) => {
    if (expandedRepository === repository) {
      setExpandedRepository(null);
      return;
    }
    const [owner, name] = repository.split("/");
    const [response, policyResponse] = await Promise.all([
      fetch(`/api/v1/repositories/${encodeURIComponent(owner)}/${encodeURIComponent(name)}/preview-config`),
      fetch(`/api/v1/repositories/${encodeURIComponent(owner)}/${encodeURIComponent(name)}/policy`),
    ]);
    if (!response.ok) {
      setError(`Could not load preview environment (${response.status})`);
      return;
    }
    const config = (await response.json()) as PreviewEnvironmentConfig;
    config.manifest ??= "";
    config.build_values ??= {};
    config.build_bindings ??= [];
    setPreviewConfigs((current) => ({ ...current, [repository]: config }));
    if (policyResponse.ok) {
      const policy = await policyResponse.json() as { local_override: RepositoryPolicy };
      policy.local_override.protected_paths ??= [];
      setRepositoryPolicyConfigs((current) => ({ ...current, [repository]: policy.local_override }));
    }
    setExpandedRepository(repository);
  };

  const saveRepositoryPolicy = async (repository: string, policy: RepositoryPolicy) => {
    const [owner, name] = repository.split("/");
    const response = await fetch(`/api/v1/repositories/${encodeURIComponent(owner)}/${encodeURIComponent(name)}/policy`, {
      method: "PUT",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(policy),
    });
    const body = await response.json().catch(() => ({})) as { error?: string; local_override?: RepositoryPolicy };
    if (!response.ok) {
      setError(body.error ?? `Could not save repository policy (${response.status})`);
      return;
    }
    if (body.local_override) setRepositoryPolicyConfigs((current) => ({ ...current, [repository]: body.local_override! }));
    setNotice(`Policy saved for ${repository}. Local overrides take precedence over .netra/policy.toml.`);
  };

  const savePreviewConfig = async (
    repository: string,
    config: PreviewEnvironmentConfig,
  ) => {
    const [owner, name] = repository.split("/");
    const response = await fetch(
      `/api/v1/repositories/${encodeURIComponent(owner)}/${encodeURIComponent(name)}/preview-config`,
      {
        method: "PUT",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(config),
      },
    );
    const body = await response.json().catch(() => ({})) as PreviewEnvironmentConfig & { error?: string };
    if (!response.ok) {
      setError(body.error ?? `Could not save preview environment (${response.status})`);
      return;
    }
    setPreviewConfigs((current) => ({ ...current, [repository]: body }));
    setNotice(`Preview environment saved for ${repository}. It applies to the next build.`);
  };

  const importPreviewEnv = async (
    repository: string,
    contents: string,
  ): Promise<PreviewEnvironmentImport | null> => {
    const [owner, name] = repository.split("/");
    const response = await fetch(
      `/api/v1/repositories/${encodeURIComponent(owner)}/${encodeURIComponent(name)}/preview-config/import-env`,
      {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ contents }),
      },
    );
    const body = await response.json().catch(() => ({})) as PreviewEnvironmentImport & { error?: string };
    if (!response.ok) {
      setError(body.error ?? `Could not import env file (${response.status})`);
      return null;
    }
    setPreviewConfigs((current) => ({ ...current, [repository]: body.config }));
    setNotice(
      `Imported ${body.imported_values} values and ${body.imported_secrets} secrets for ${repository}. ` +
      `${body.imported_build_values} VITE values will also be used during the next build.`,
    );
    return body;
  };

  const deselectRepository = async (repository: string) => {
    if (!window.confirm(`Stop monitoring ${repository}? Active previews will be undeployed.`)) return;
    const portalRepository = portalRepositories.find((candidate) => candidate.repository === repository);
    if (portalRepository?.enabled && !(await setStaffRequestsEnabled(repository, false, false))) return;
    const [owner, name] = repository.split("/");
    const response = await fetch(
      `/api/v1/repositories/${encodeURIComponent(owner)}/${encodeURIComponent(name)}`,
      { method: "DELETE" },
    );
    if (!response.ok) {
      setError(`Could not deselect repository (${response.status})`);
      return;
    }
    setNotice(`${repository} was removed from Netra.`);
    setGithubRepositories((current) => current.map((candidate) =>
      candidate.full_name === repository ? { ...candidate, selected: false } : candidate
    ));
    await refresh(false);
  };

  const openIssueTarget = async (repository: string, number: number) => {
    const [owner, name] = repository.split("/");
    if (!owner || !name) return;
    const response = await fetch(
      `/api/v1/issues/${encodeURIComponent(owner)}/${encodeURIComponent(name)}/${number}`,
    );
    if (!response.ok) {
      setError(`Could not load Issue details (${response.status})`);
      return;
    }
    setIssueDetail((await response.json()) as IssueDetail);
    setView("issue");
    pushPath(issuePath(repository, number));
  };

  const openIssueDetail = async (issue: IssueInventory) => {
    await openIssueTarget(issue.repository, issue.number);
  };

  const enqueuePr = async (
    repository: string,
    number: number,
    mode: "review" | "fix" | "recheck",
  ) => {
    if (!window.confirm(mode === "recheck"
      ? `Recheck the saved evidence for ${repository} PR #${number} without calling a model?`
      : `Start a Netra ${mode} run for ${repository} PR #${number}?`)) {
      return;
    }
    const response = await fetch("/api/v1/commands/pr", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ repository, number, mode }),
    });
    if (!response.ok) {
      const detail = await response.json().catch(() => ({})) as { error?: string };
      setError(detail.error || `Could not enqueue ${mode} (${response.status})`);
      return;
    }
    const result = (await response.json()) as { accepted: boolean };
    setNotice(result.accepted
      ? `${repository} #${number} was queued for ${mode}.`
      : `${repository} #${number} already has an active run.`);
    await refresh(false);
  };

  const deployCurrentPreview = async (repository: string, number: number) => {
    if (!window.confirm(`Deploy the current head of ${repository} PR #${number}?`)) return;
    setError("");
    setNotice(`Preparing an exact-SHA preview for ${repository} PR #${number}…`);
    const response = await fetch("/api/v1/commands/preview", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ repository, number }),
    });
    const body = await response.json().catch(() => ({})) as {
      error?: string;
      public_url?: string;
      reused?: boolean;
    };
    if (!response.ok) {
      setError(body.error ?? `Could not deploy preview (${response.status})`);
      setNotice("");
      return;
    }
    setNotice(body.reused
      ? `The current preview for ${repository} PR #${number} is already available.`
      : `Preview deployment queued for ${repository} PR #${number}.`);
    await refresh(false);
    const [owner, name] = repository.split("/");
    if (prDetail && owner && name) {
      const detail = await fetch(
        `/api/v1/prs/${encodeURIComponent(owner)}/${encodeURIComponent(name)}/${number}`,
      );
      if (detail.ok) setPrDetail((await detail.json()) as PullRequestDetail);
    }
  };

  const openPreviewSettings = (repository: string) => {
    setExpandedRepository(repository);
    setSettingsTab("repositories");
    setView("settings");
    pushPath("/settings/repositories");
    setNotice(`Enable previews for ${repository} to build a preview site.`);
  };

  const enqueueIssue = async (
    repository: string,
    number: number,
    mode: "review" | "fix",
  ) => {
    if (!window.confirm(`Start a Netra ${mode} run for ${repository} Issue #${number}?`)) {
      return;
    }
    const response = await fetch("/api/v1/commands/issue", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ repository, number, mode }),
    });
    const result = await response.json().catch(() => ({})) as { accepted?: boolean; error?: string };
    if (!response.ok) {
      setError(result.error ?? `Could not enqueue Issue ${mode} (${response.status})`);
      return;
    }
    setNotice(result.accepted
      ? `${repository} Issue #${number} was queued for ${mode}.`
      : `${repository} Issue #${number} already has an active run.`);
    await refresh(false);
  };

  const setMute = async (pull: PullRequestInventory, mode: "off" | "4h" | "permanent") => {
    const [owner, name] = pull.repository.split("/");
    const response = await fetch(
      `/api/v1/prs/${encodeURIComponent(owner)}/${encodeURIComponent(name)}/${pull.number}/mute`,
      {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ mode }),
      },
    );
    if (!response.ok) {
      setError(`Mute update failed (${response.status})`);
      return;
    }
    await refresh(false);
    await openPrDetail(pull);
  };

  const abortRun = async (job: ReviewJob | IssueJob, entity: "review" | "issue") => {
    if (!window.confirm(`Abort this ${job.mode} run?`)) return;
    const response = await fetch(`/api/v1/${entity}-jobs/${job.id}/abort`, { method: "POST" });
    if (!response.ok) {
      setError(`Abort failed (${response.status})`);
      return;
    }
    setNotice("Run aborted.");
    await refresh(false);
  };

  const updateSettings = async (update: Partial<RuntimeSettings> & { forget_reviewer_models?: string[] }) => {
    const response = await fetch("/api/v1/settings", {
      method: "PUT",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(update),
    });
    if (!response.ok) {
      setError(`Settings update failed (${response.status})`);
      return;
    }
    setRuntimeSettings((await response.json()) as RuntimeSettings);
    setNotice("Runtime policy updated.");
  };

  const updateTypedSetting = async (setting: SettingDescriptor, value: boolean | number | string) => {
    const response = await fetch("/api/v1/settings", {
      method: "PUT",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ values: { [setting.key]: value } }),
    });
    const body = await response.json().catch(() => ({})) as RuntimeSettings & { error?: string };
    if (!response.ok) {
      setError(body.error ?? `Setting update failed (${response.status})`);
      return;
    }
    setRuntimeSettings(body);
    setError("");
    if (setting.restart_required) setRestartPending(true);
    setNotice(setting.restart_required
      ? `${setting.label} saved. Restart Netra to apply it.`
      : `${setting.label} updated.`);
  };

  const restartNetra = async () => {
    if (!window.confirm("Restart Netra now? Active work is durable and will resume after the service returns.")) return;
    setRestarting(true);
    setError("");
    try {
      const response = await fetch("/api/v1/runtime/restart", { method: "POST" });
      const body = await response.json().catch(() => ({})) as { error?: string };
      if (!response.ok) throw new Error(body.error ?? `Restart failed (${response.status})`);
      setNotice("Restarting Netra…");

      let observedOffline = false;
      const deadline = Date.now() + 45_000;
      while (Date.now() < deadline) {
        await new Promise((resolve) => window.setTimeout(resolve, 500));
        try {
          const health = await fetch("/api/v1/health", { cache: "no-store" });
          if (!health.ok) {
            observedOffline = true;
          } else if (observedOffline) {
            setRestartPending(false);
            setRestarting(false);
            setHealthy(true);
            setNotice("Netra restarted. Saved settings are now active.");
            await refresh(true);
            return;
          }
        } catch {
          observedOffline = true;
          setHealthy(false);
        }
      }
      throw new Error(observedOffline
        ? "Netra did not become healthy within 45 seconds. Check the Runtime page and service logs."
        : "Netra stayed online, so the restart could not be confirmed.");
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not restart Netra");
      setNotice("Restart is still required to apply the saved settings.");
    } finally {
      setRestarting(false);
    }
  };

  const startGithubManifest = async () => {
    setGithubSetupBusy(true);
    setError("");
    try {
      const response = await fetch("/api/v1/github/manifest/start", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ organization: githubSetupOrganization, name: githubSetupName }),
      });
      const body = await response.json() as { url?: string; manifest?: string; error?: string };
      if (!response.ok || !body.url || !body.manifest) {
        throw new Error(body.error ?? `Could not begin GitHub setup (${response.status})`);
      }
      const form = document.createElement("form");
      form.method = "post";
      form.action = body.url;
      const manifest = document.createElement("input");
      manifest.type = "hidden";
      manifest.name = "manifest";
      manifest.value = body.manifest;
      form.appendChild(manifest);
      document.body.appendChild(form);
      form.submit();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not begin GitHub setup");
      setGithubSetupBusy(false);
    }
  };

  const importGithubApp = async () => {
    setGithubSetupBusy(true);
    setError("");
    try {
      const response = await fetch("/api/v1/github/import", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(githubImport),
      });
      const body = await response.json().catch(() => ({})) as { error?: string };
      if (!response.ok) throw new Error(body.error ?? `GitHub import failed (${response.status})`);
      setGithubImport((current) => ({ ...current, private_key: "", webhook_secret: "" }));
      setNotice("GitHub App credentials saved in private local files. Restart Netra to validate the installation.");
      setRestartPending(true);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "GitHub import failed");
    } finally {
      setGithubSetupBusy(false);
    }
  };

  const abortAllRuns = async () => {
    if (!window.confirm("Abort every queued, running, or finalizing Netra run?")) return;
    const response = await fetch("/api/v1/review-jobs/abort-all", { method: "POST" });
    if (!response.ok) {
      setError(`Abort all failed (${response.status})`);
      return;
    }
    const result = await response.json() as { count: number };
    setNotice(`${result.count} active runs aborted.`);
    await refresh(false);
  };

  const signOut = async () => {
    await fetch("/auth/logout", {
      method: "POST",
      credentials: "same-origin",
      headers: {
        Origin: window.location.origin,
        "X-Netra-Portal-CSRF": "1",
      },
    }).catch(() => undefined);
    window.location.assign("/auth/login?return_to=%2F");
  };

  const pageTitle = view === "pr"
    ? (prDetail ? `${prDetail.pull_request.repository.split("/").slice(-1)[0]} #${prDetail.pull_request.number}` : "Pull request")
    : view === "issue"
      ? (issueDetail ? `${issueDetail.issue.repository} #${issueDetail.issue.number}` : "Issue")
      : view === "requests" || view === "request" ? "Requests"
      : view === "work"
        ? ({ pulls: "Pull requests", issues: "Issues", teams: "Teams requests", merge: "Merge desk" }[workTab])
        : view === "insights"
          ? ({
            activity: "Activity",
            completed: "Completed work",
            analytics: "Analytics",
            reviews: "Review quality",
          }[insightTab])
          : view === "quality"
          ? ({
            portfolio: "Test quality",
            repositories: "Repository test quality",
            ownership: "Test ownership",
          }[qualityTab])
          : view === "settings"
            ? ({
              general: "General settings",
              repositories: "Repositories",
              integrations: "Integrations",
              runtime: "Runtime resources",
              memories: "Memories",
              team: "Team access",
            }[settingsTab])
            : "Review operations";

  const pageDescription = view === "pr"
    ? prDetail?.pull_request.repository ?? "Review history and pull request health."
    : view === "issue"
      ? issueDetail?.issue.title ?? "Issue review, fix runs, and linked pull requests."
      : view === "requests" || view === "request" ? "Raise engineering work and follow every plan, build, preview, and proof cycle."
      : view === "work"
        ? (workTab === "pulls"
          ? `GitHub inventory across ${runtime?.repositories ?? 0} configured repositories.`
          : workTab === "issues"
            ? "Open GitHub Issues across configured repositories."
            : workTab === "teams" ? "Guided requests raised through Netra in personal, group, and channel chats."
            : "PR readiness, exact-head evidence, conflicts, and human merge handoff.")
        : view === "insights"
          ? ({
            activity: "One durable timeline for GitHub, review, provider, and preview events.",
            completed: "Merged and closed pull requests retained by Netra.",
            analytics: "Review outcomes, finding severity, and current workload.",
            reviews: "Repeated reviews, source-validation failures, and later findings from existing results.",
          }[insightTab])
          : view === "quality"
          ? ({
            portfolio: `Unit tests and coverage across ${portfolio.reportingRepositories} reporting ${portfolio.reportingRepositories === 1 ? "repository" : "repositories"}.`,
            repositories: "One repository's releases, suite, and least-covered files.",
            ownership: "Who owns, wrote, and last edited each unit test.",
          }[qualityTab])
          : view === "settings"
            ? ({
              general: "Automation behavior and provider policy.",
              repositories: "Select repositories and configure review and preview policy.",
              integrations: "GitHub connectivity and future task-intake channels.",
              runtime: "Host, Netra process, and per-preview resource utilization.",
              memories: "Browse and forget SuperMemory documents used in review prompts.",
              team: "Microsoft 365 access and Netra administrator roles.",
            }[settingsTab])
            : "Exact-head code review, active work, and ephemeral preview activity.";

  // The trend charts the analytics skeleton stands in for, in render order.
  const analyticsTrendSkeletons = [
    { eyebrow: `${analyticsRangeLabel} DELIVERY FLOW`, title: "PRs created vs merged", legend: 2 },
    { eyebrow: `${analyticsRangeLabel} REVIEW SPEED`, title: "Review duration", legend: 2 },
    { eyebrow: `${analyticsRangeLabel} REVIEW IMPACT`, title: "Findings per reviewed PR", legend: 0 },
    { eyebrow: `${analyticsRangeLabel} REVIEW IMPACT`, title: "Resolutions per reviewed PR", legend: 0 },
    { eyebrow: `${analyticsRangeLabel} DELIVERY SPEED`, title: "Time to merge", legend: 0 },
    { eyebrow: `${analyticsRangeLabel} REVIEW DEPTH`, title: "Review cycles per PR", legend: 0 },
    { eyebrow: `${analyticsRangeLabel} PRIORITY MIX`, title: "Findings by priority", legend: 4 },
    { eyebrow: `${analyticsRangeLabel} PROVIDER OUTCOMES`, title: "Provider attempt success", legend: 2 },
    { eyebrow: "THREAD HEALTH", title: "Resolved in range vs currently unresolved", legend: 2, wide: true },
  ] as Array<{ eyebrow: string; title: string; legend: number; wide?: boolean }>;

  // Anything drawn from the history wave rather than the shell. Panels that
  // watch `loading` alone come out of their skeleton while their own data is
  // still in flight, and an empty-state message is a claim about the data --
  // not something to show before it has arrived.
  const historyPending = loading || historyLoading;

  // These descriptions quote a total, so they stay a skeleton until it is known.
  const pageDescriptionPending = (loading && view === "work" && workTab === "pulls")
    || (historyPending && view === "quality");

  return (
    <div className="app-shell">
      <aside>
        <div className="brand">
          <NetraBrandMark />
          <div>
            <strong>NETRA</strong>
            <span>CONTROL ROOM</span>
          </div>
        </div>
        <nav>
          <button className={view === "requests" || view === "request" ? "active" : ""} onClick={() => { setView("requests"); pushPath("/requests"); }}><span>◇</span> Requests</button>
          <button className={view === "overview" ? "active" : ""} onClick={() => { setView("overview"); pushPath("/"); }}><span>◫</span> Overview</button>
          <button className={view === "work" || view === "pr" || view === "issue" ? "active" : ""} onClick={() => { setWorkTab("pulls"); setView("work"); pushPath("/work/pulls"); }}><span>⑂</span> Work</button>
          <button className={view === "insights" || view === "quality" ? "active" : ""} onClick={() => { setInsightTab("analytics"); setView("insights"); pushPath("/insights/analytics"); }}><span>⌁</span> Insights</button>
          <button className={view === "status" ? "active" : ""} onClick={() => { setStatusWindow("30d"); setView("status"); pushPath("/status/30d"); }}><span>▥</span> Status</button>
          <button className={view === "settings" ? "active" : ""} onClick={() => { setSettingsTab("general"); setView("settings"); pushPath("/settings/general"); }}><span>⚙</span> Settings</button>
        </nav>
        <div className="sidebar-telemetry">
          <div className="runtime-card">
            <div className={`status-light ${healthy ? "online" : ""}`} />
            <div>
              <strong>{healthy === null ? "Checking runtime" : healthy ? "Runtime online" : "Runtime offline"}</strong>
              <span>{resources?.host.name ?? "host"} · {previewRuntimeLabel(resources?.host.preview_backend)}</span>
            </div>
          </div>
          {resources ? <div className="sidebar-host">
            <SidebarMeter
              label="CPU"
              value={resources.host.cpu_percent}
              detail={`${resources.host.cpu_percent.toFixed(0)}%`}
            />
            <SidebarMeter
              label="Memory"
              value={resources.host.memory_total_bytes
                ? resources.host.memory_used_bytes / resources.host.memory_total_bytes * 100
                : 0}
              detail={resources.host.memory_total_bytes
                ? `${bytes(resources.host.memory_used_bytes)} used of ${bytes(resources.host.memory_total_bytes)}`
                : `${bytes(resources.host.memory_used_bytes)} used`}
            />
            <SidebarMeter
              label="Disk"
              value={resources.host.disk?.total_bytes
                ? (1 - (resources.host.disk.available_bytes / resources.host.disk.total_bytes)) * 100
                : 0}
              detail={resources.host.disk?.total_bytes
                ? `${bytes(resources.host.disk.available_bytes)} free of ${bytes(resources.host.disk.total_bytes)}`
                : `${bytes(resources.host.disk?.available_bytes)} free`}
            />
          </div> : loading ? <div className="sidebar-host">
            <SkeletonMeters label="Loading host utilization" />
          </div> : null}
          <div className="sidebar-providers">
            <div className="sidebar-provider-heading">
              <span className="sidebar-label">PROVIDER USAGE</span>
              <ProviderUsageRefreshButton
                refreshing={providerUsageRefreshing}
                onRefresh={() => void refreshProviderUsage()}
              />
            </div>
            {loading
              ? <SkeletonMeters label="Loading provider usage" />
              : providerUsageMeters.map((meter) => (
                <SidebarMeter
                  key={meter.key}
                  label={meter.label}
                  value={meter.value}
                  detail={meter.detail}
                  remaining
                />
              ))}
          </div>
        </div>
      </aside>

      <main>
        <header>
          <div>
            {(view === "pr" || view === "issue") && <button
              className="back-button"
              onClick={() => {
                setWorkTab(view === "pr" ? "pulls" : "issues");
                setView("work");
                pushPath(view === "pr" ? "/work/pulls" : "/work/issues");
              }}
            >← Back to {view === "pr" ? "pull requests" : "issues"}</button>}
            <h1>{pageTitle}</h1>
            <p>{pageDescriptionPending ? <SkeletonBar width={320} height={11} /> : pageDescription}</p>
          </div>
          <div className="account-menu">
            <span>{currentUser.name}</span>
            <small>{currentUser.email}</small>
            {currentUser.id !== "local-development" && <button onClick={() => void signOut()}>Sign out</button>}
          </div>
        </header>

        {error && <div className="error-banner">{error}</div>}
        <StatusToast
          message={notice}
          onDismiss={dismissNotice}
          persistent={restartPending}
          action={restartPending ? {
            label: restarting ? "Restarting…" : "Restart Netra",
            disabled: restarting,
            onClick: () => void restartNetra(),
          } : undefined}
        />

        {(view === "requests" || view === "request") && <PortalApp embedded actor={currentUser} />}

        {view === "status" && (
          <StatusPage
            statusWindow={statusWindow}
            onWindowChange={(next) => { setStatusWindow(next); pushPath(`/status/${next}`); }}
          />
        )}

        {view === "work" && <div className="detail-tabs section-tabs">
          <button className={workTab === "pulls" ? "active" : ""} onClick={() => { setWorkTab("pulls"); pushPath("/work/pulls"); }}>Pull requests {loading ? <SkeletonCount /> : <span>{inventory.pull_requests.length}</span>}</button>
          <button className={workTab === "issues" ? "active" : ""} onClick={() => { setWorkTab("issues"); pushPath("/work/issues"); }}>Issues {loading ? <SkeletonCount /> : <span>{inventory.issues.length}</span>}</button>
          <button className={workTab === "teams" ? "active" : ""} onClick={() => { setWorkTab("teams"); pushPath("/work/teams"); }}>Teams requests {historyPending ? <SkeletonCount /> : <span>{teamsHistory.requests.length}</span>}</button>
          <button className={workTab === "merge" ? "active" : ""} onClick={() => { setWorkTab("merge"); pushPath("/work/merge"); }}>Merge desk</button>
        </div>}

        {(view === "insights" || view === "quality") && <div className="detail-tabs section-tabs">
          <button className={view === "insights" && insightTab === "analytics" ? "active" : ""} onClick={() => { setInsightTab("analytics"); setView("insights"); pushPath("/insights/analytics"); }}>Analytics</button>
          <button className={view === "insights" && insightTab === "reviews" ? "active" : ""} onClick={() => { setInsightTab("reviews"); setView("insights"); pushPath("/insights/reviews"); }}>Review quality</button>
          <button className={view === "insights" && insightTab === "activity" ? "active" : ""} onClick={() => { setInsightTab("activity"); setView("insights"); pushPath("/insights/activity"); }}>Activity</button>
          <button className={view === "insights" && insightTab === "completed" ? "active" : ""} onClick={() => { setInsightTab("completed"); setView("insights"); pushPath("/insights/completed"); }}>Completed {historyPending ? <SkeletonCount /> : <span>{completedPulls.length}</span>}</button>
          <button className={view === "quality" ? "active" : ""} onClick={() => { setQualityTab("portfolio"); setView("quality"); pushPath("/insights/quality/portfolio"); }}>Test quality</button>
        </div>}

        {view === "settings" && <div className="detail-tabs section-tabs">
          <button className={settingsTab === "general" ? "active" : ""} onClick={() => { setSettingsTab("general"); pushPath("/settings/general"); }}>General</button>
          <button className={settingsTab === "repositories" ? "active" : ""} onClick={() => { setSettingsTab("repositories"); pushPath("/settings/repositories"); }}>Repositories {loading ? <SkeletonCount /> : <span>{repositories.length}</span>}</button>
          <button className={settingsTab === "integrations" ? "active" : ""} onClick={() => { setSettingsTab("integrations"); pushPath("/settings/integrations"); }}>Integrations</button>
          <button className={settingsTab === "runtime" ? "active" : ""} onClick={() => { setSettingsTab("runtime"); pushPath("/settings/runtime"); }}>Runtime</button>
          <button className={settingsTab === "memories" ? "active" : ""} onClick={() => { setSettingsTab("memories"); pushPath("/settings/memories"); }}>Memories</button>
          <button className={settingsTab === "team" ? "active" : ""} onClick={() => { setSettingsTab("team"); pushPath("/settings/team"); }}>Team</button>
        </div>}

        {view === "overview" && (loading
          ? <MetricsSkeleton label="Loading review operations metrics" />
          : <section className="metrics">
            <Metric label="Routed previews" value={stats.active} tone="green" />
            <Metric label="Build pipeline" value={stats.building} tone="amber" />
            <Metric label="Failed revisions" value={stats.failed} tone="red" />
            <Metric label="Open pull requests" value={inventory.pull_requests.length} tone="blue" />
          </section>
        )}

        {view === "overview" && loading && <section className="dashboard-charts">
          <ChartCardSkeleton
            label="Loading review throughput"
            eyebrow="14 DAY FLOW"
            title="Review throughput"
            className="chart-wide"
          />
          <ChartCardSkeleton
            label="Loading run outcome mix"
            eyebrow="RUN OUTCOMES"
            title="Current mix"
            shape="donut"
            legend={3}
          />
        </section>}

        {view === "overview" && !loading && <section className="dashboard-charts">
          <article className="activity-card chart-card chart-wide">
            <div className="chart-heading">
              <div><p className="eyebrow">14 DAY FLOW</p><h2>Review throughput</h2></div>
              <span>{throughputData.reduce((total, row) => total + row.reviews, 0)} runs</span>
            </div>
            <ResponsiveContainer width="100%" height={240}>
              <AreaChart data={throughputData} margin={{ top: 8, right: 18, left: -25, bottom: 0 }}>
                <defs>
                  <linearGradient id="reviewFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#4f9f6a" stopOpacity={0.42} />
                    <stop offset="95%" stopColor="#4f9f6a" stopOpacity={0.03} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#e4e9e4" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 9, fill: "#758078" }} axisLine={false} tickLine={false} />
                <YAxis allowDecimals={false} tick={{ fontSize: 9, fill: "#758078" }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ border: "1px solid #d7ded8", borderRadius: 8, fontSize: 11 }} />
                <Area type="monotone" dataKey="reviews" stroke="#347b50" strokeWidth={2} fill="url(#reviewFill)" />
              </AreaChart>
            </ResponsiveContainer>
          </article>
          <article className="activity-card chart-card">
            <div className="chart-heading">
              <div><p className="eyebrow">RUN OUTCOMES</p><h2>Current mix</h2></div>
            </div>
            {outcomeData.length === 0
              ? <div className="chart-empty">No review runs yet</div>
              : <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie data={outcomeData} dataKey="value" nameKey="name" innerRadius={58} outerRadius={86} paddingAngle={3}>
                    {outcomeData.map((row) => <Cell key={row.name} fill={row.color} />)}
                  </Pie>
                  <Tooltip contentStyle={{ border: "1px solid #d7ded8", borderRadius: 8, fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>}
            <div className="chart-legend">
              {outcomeData.map((row) => <span key={row.name}><i style={{ background: row.color }} />{row.name} <strong>{row.value}</strong></span>)}
            </div>
          </article>
        </section>}

        {/* Coverage is a standing operational number, not something to go
            looking for, so Overview carries the portfolio headline and the two
            charts behind it. Every figure here is the Test quality portfolio's
            own, and the header links through for the rest of it. */}
        {view === "overview" && <div className="quality-toolbar overview-coverage-heading">
          <div>
            <p className="eyebrow">TEST COVERAGE</p>
            <h2 className="quality-heading">{historyPending
              ? <SkeletonBar width={268} height={15} />
              : portfolio.coverageRepositories === 0
                ? "No coverage reported yet"
                : `${portfolio.lineRate}% of measured lines across ${counted(portfolio.coverageRepositories, "repository", "repositories")}`}</h2>
          </div>
          <button
            className="secondary-action"
            onClick={() => {
              setQualityTab("portfolio");
              setView("quality");
              pushPath("/insights/quality/portfolio");
            }}
          >
            Open test quality
          </button>
        </div>}

        {view === "overview" && (historyPending
          ? <MetricsSkeleton label="Loading portfolio coverage metrics" variant="eyebrow" />
          // Four dashes and two empty plots say nothing four times over. With
          // no coverage ingested, the one useful thing to say is how to get
          // some, so the tiles and charts below stand down for this note.
          : portfolio.coverageRepositories === 0
          ? <div className="quality-note">
            {portfolio.reportingRepositories === 0
              ? "No release has reported tests or coverage yet. Test quality lists what Netra reads and where a pipeline posts it."
              : `${counted(portfolio.reportingRepositories, "repository", "repositories")} ${verbFor(portfolio.reportingRepositories, "reports", "report")} tests, but no coverage yet.`}
          </div>
          : <section className="metrics">
            <article className={`metric ${portfolio.lineRate >= 80 ? "" : "amber"}`}>
              <p className="eyebrow">LINE COVERAGE</p>
              <strong>{portfolio.lineRate}%</strong>
              {/* Dated from the trend's opening point rather than counted in
                  days, so the caption names the same window the chart plots. */}
              <span>{coverageDigest.rateChange === null
                ? "no earlier report to compare against"
                : `${coverageDigest.rateChange > 0 ? "+" : ""}${coverageDigest.rateChange} pts since ${coverageDigest.trend[0].label}`}</span>
              <div className="metric-line" />
            </article>
            <article className="metric blue">
              <p className="eyebrow">UNCOVERED LINES</p>
              <strong>{portfolio.uncoveredLines.toLocaleString()}</strong>
              <span>of {portfolio.linesValid.toLocaleString()} measured</span>
              <div className="metric-line" />
            </article>
            <article className={`metric ${portfolio.repositoriesWithoutCoverage > 0 ? "amber" : ""}`}>
              <p className="eyebrow">REPORTING COVERAGE</p>
              <strong>{portfolio.coverageRepositories}<small>/{portfolio.reportingRepositories}</small></strong>
              {/* The gap is the actionable half of this tile: a repository
                  reporting tests and no coverage is absent from the rate
                  above, not counted in it at zero percent. */}
              <span>{portfolio.repositoriesWithoutCoverage > 0
                ? `${counted(portfolio.repositoriesWithoutCoverage, "repository", "repositories")} ${verbFor(portfolio.repositoriesWithoutCoverage, "reports", "report")} tests only`
                : "every reporting repository measured"}</span>
              <div className="metric-line" />
            </article>
            <article className={`metric ${portfolio.worstCovered && portfolio.worstCovered.lineRate < 80 ? "red" : ""}`}>
              <p className="eyebrow">LEAST COVERED</p>
              {/* `worstCovered` is non-null whenever a repository reports
                  coverage, which the block above has already established; the
                  guard is here for the type, not for a state that reaches it. */}
              <strong>{portfolio.worstCovered ? `${portfolio.worstCovered.lineRate}%` : "—"}</strong>
              <span>{portfolio.worstCovered
                ? `${portfolio.worstCovered.label} · ${portfolio.worstCovered.uncoveredLines.toLocaleString()} lines uncovered`
                : "—"}</span>
              <div className="metric-line" />
            </article>
          </section>
        )}

        {view === "overview" && historyPending && <section className="dashboard-charts">
          <ChartCardSkeleton
            label="Loading portfolio coverage trend"
            eyebrow="PORTFOLIO OVER TIME"
            title="Line coverage"
            className="chart-wide"
            height={220}
          />
          <ChartCardSkeleton
            label="Loading least covered repositories"
            eyebrow="COVERAGE"
            title="Least covered repositories"
            height={220}
          />
        </section>}

        {view === "overview" && !historyPending && portfolio.coverageRepositories > 0 && <section className="dashboard-charts">
          <article className="activity-card chart-card chart-wide">
            <div className="chart-heading">
              <div><p className="eyebrow">PORTFOLIO OVER TIME</p><h2>Line coverage</h2></div>
              <span>weighted by measured lines</span>
            </div>
            {coverageDigest.trend.length < 2
              ? <div className="chart-empty">A trend needs at least two coverage reports</div>
              : <ResponsiveContainer width="100%" height={220}>
                <LineChart data={coverageDigest.trend} margin={{ top: 12, right: 16, left: -20, bottom: 0 }}>
                  <CartesianGrid stroke="#e4e9e4" vertical={false} />
                  <XAxis dataKey="label" minTickGap={18} tick={{ fontSize: 9, fill: "#758078" }} axisLine={false} tickLine={false} />
                  <YAxis domain={["auto", "auto"]} unit="%" tick={{ fontSize: 9, fill: "#758078" }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ border: "1px solid #d7ded8", borderRadius: 8, fontSize: 11 }} />
                  <Line type="monotone" dataKey="lineRate" name="Line coverage %" stroke={TEST_COVERAGE_COLOR} strokeWidth={2} dot={{ r: 4, fill: TEST_COVERAGE_COLOR, stroke: "#f9faf7", strokeWidth: 2 }} activeDot={{ r: 5 }} />
                </LineChart>
              </ResponsiveContainer>}
          </article>
          <article className="activity-card chart-card">
            <div className="chart-heading">
              <div><p className="eyebrow">COVERAGE</p><h2>Least covered repositories</h2></div>
              <span>{coverageDigest.moreRepositories > 0
                ? `${coverageDigest.moreRepositories} more in test quality`
                : "worst first"}</span>
            </div>
            <ResponsiveContainer width="100%" height={Math.max(220, coverageDigest.lowestCovered.length * 34)}>
              <BarChart layout="vertical" data={coverageDigest.lowestCovered} margin={{ top: 8, right: 30, left: 25, bottom: 0 }}>
                <CartesianGrid stroke="#e4e9e4" horizontal={false} />
                <XAxis type="number" domain={[0, 100]} unit="%" tick={{ fontSize: 9, fill: "#758078" }} axisLine={false} tickLine={false} />
                <YAxis type="category" dataKey="label" width={110} tick={{ fontSize: 9, fill: "#58645c" }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ border: "1px solid #d7ded8", borderRadius: 8, fontSize: 11 }} />
                <Bar dataKey="lineRate" name="Line coverage %" fill={TEST_COVERAGE_COLOR} radius={[0, 3, 3, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </article>
        </section>}

        {view === "overview" && <section className="activity-card active-work-card">
          <div className="section-heading">
            <div>
              <p className="eyebrow">REVIEW RUNS</p>
              <h2>{overviewReviewTab === "ongoing"
                ? "Active review and fix runs"
                : "Recent terminal runs"}</h2>
            </div>
            <div className="review-run-tabs" role="tablist" aria-label="Review run history">
              <button
                type="button"
                role="tab"
                aria-selected={overviewReviewTab === "ongoing"}
                className={overviewReviewTab === "ongoing" ? "active" : ""}
                onClick={() => setOverviewReviewTab("ongoing")}
              >Ongoing</button>
              <button
                type="button"
                role="tab"
                aria-selected={overviewReviewTab === "completed"}
                className={overviewReviewTab === "completed" ? "active" : ""}
                onClick={() => setOverviewReviewTab("completed")}
              >Completed</button>
            </div>
          </div>
          {overviewReviewTab === "ongoing" ? <div className="table-wrap" role="tabpanel">
            <table>
              <thead><tr><th>Target</th><th>Mode</th><th>State</th><th>Provider</th><th>Started</th><th>Duration</th></tr></thead>
              <tbody>{loading
                ? <SkeletonRows label="Loading active review runs" columns={6} />
                : activeRuns.length === 0
                ? <tr><td colSpan={6} className="empty">No review or fix runs are currently active.</td></tr>
                : activeRuns.map((job) => <tr className="clickable-row" key={job.id} onClick={() => {
                if (job.entity_type === "issue") void openIssueTarget(job.repository, job.number);
                else void openPrTarget(job.repository, job.number);
              }}>
                <td>
                  <strong>{job.repository}</strong>
                  <span>
                    {job.entity_type === "issue" ? "Issue" : "PR"} #{job.number}
                    {` · @${activeRunAuthors.get(`${job.entity_type}:${job.repository}#${job.number}`) ?? "unknown"}`}
                  </span>
                </td>
                <td>{job.mode}</td>
                <td><span className={`state ${job.status}`}>{job.status}</span></td>
                <td>{job.provider ?? "Selecting…"}</td>
                <td>{relativeTime(job.started_at ?? job.created_at)}</td>
                <td>{durationBetween(job.started_at ?? job.created_at)}</td>
              </tr>)}</tbody>
            </table>
          </div> : <div className="table-wrap" role="tabpanel">
            <table className="completed-review-table">
              <thead><tr><th>Target</th><th>Mode</th><th>Outcome</th><th>Provider</th><th>Finished</th><th>Duration</th><th>Diagnostics</th></tr></thead>
              <tbody>{loading
                ? <SkeletonRows label="Loading completed review runs" columns={7} />
                : recentCompletedRuns.length === 0
                ? <tr><td colSpan={7} className="empty">No completed review or fix runs have been recorded.</td></tr>
                : recentCompletedRuns.map((job) => {
                  const diagnostic = reviewRunDiagnostics(job);
                  return <tr className="clickable-row" key={job.id} onClick={() => {
                    if (job.entity_type === "issue") void openIssueTarget(job.repository, job.number);
                    else void openPrTarget(job.repository, job.number);
                  }}>
                    <td>
                      <strong>{job.repository}</strong>
                      <span>{job.entity_type === "issue" ? "Issue" : "PR"} #{job.number} · <code>{shortSha(job.entity_type === "issue" ? job.base_sha : job.head_sha)}</code></span>
                    </td>
                    <td>{job.mode}</td>
                    <td><span className={`state ${job.status}`}>{job.status}</span></td>
                    <td>{job.provider ?? "—"}</td>
                    <td>{relativeTime(job.completed_at ?? job.updated_at ?? job.provider_finished_at)}</td>
                    <td>{durationBetween(job.created_at, job.completed_at ?? job.updated_at)}</td>
                    <td><span className={`run-diagnostic${job.error ? " has-error" : ""}`} title={job.error ?? diagnostic}>{diagnostic}</span></td>
                  </tr>;
                })}</tbody>
            </table>
          </div>}
        </section>}

        {view === "work" && workTab === "pulls" && unlistedActivePullRuns.length > 0 && <section className="activity-card active-work-card">
          <div className="section-heading">
            <div>
              <p className="eyebrow">ACTIVE OUTSIDE OPEN INVENTORY</p>
              <h2>{unlistedActivePullRuns.length} review or fix run{unlistedActivePullRuns.length === 1 ? "" : "s"}</h2>
            </div>
            <span className="policy">PR may have merged or closed after enqueue</span>
          </div>
          <div className="table-wrap">
            <table>
              <thead><tr><th>Target</th><th>Mode</th><th>State</th><th>Provider</th><th>Started</th><th>Duration</th></tr></thead>
              <tbody>{unlistedActivePullRuns.map((job) => <tr
                className="clickable-row"
                key={job.id}
                onClick={() => void openPrTarget(job.repository, job.number)}
              >
                <td><strong>{job.repository}</strong><span>PR #{job.number}</span></td>
                <td>{job.mode}</td>
                <td><span className={`state ${job.status}`}>{job.status}</span></td>
                <td>{job.provider ?? "Selecting…"}</td>
                <td>{relativeTime(job.started_at ?? job.created_at)}</td>
                <td>{durationBetween(job.started_at ?? job.created_at)}</td>
              </tr>)}</tbody>
            </table>
          </div>
        </section>}

        {view === "work" && workTab === "pulls" && <section className="activity-card">
          <div className="section-heading">
            <div>
              <p className="eyebrow">GITHUB INVENTORY</p>
              <h2>{loading
                ? <SkeletonBar width={216} height={17} />
                : prFiltersActive
                ? `${filteredPulls.length} of ${inventory.pull_requests.length} open pull requests`
                : `${inventory.pull_requests.length} open pull requests`}</h2>
            </div>
            <div className="inventory-actions">
              <label className="pr-group-control">
                <span>Group by</span>
                <select
                  aria-label="Group pull requests"
                  value={prGroupMode}
                  onChange={(event) => setPrGroupMode(event.target.value as PrGroupMode)}
                >
                  <option value="status">Status</option>
                  <option value="created">Created date</option>
                  <option value="repository">Repository</option>
                  <option value="user">User</option>
                  <option value="none">No grouping</option>
                </select>
              </label>
              <span className="policy">synced {relativeTime(inventory.synced_at)}</span>
              <button disabled={inventorySyncing} onClick={() => void syncGithubInventory()}>
                {inventorySyncing ? "Syncing GitHub…" : "Sync from GitHub"}
              </button>
            </div>
          </div>
          <div className="inventory-filters" role="search" aria-label="Filter pull requests">
            <label className="inventory-filter-control">
              <span>User</span>
              <select
                aria-label="Filter pull requests by user"
                value={prFilterUser}
                onChange={(event) => setPrFilterUser(event.target.value)}
              >
                <option value="">All users</option>
                {prFilterUsers.map((user) => <option key={user} value={user}>{user}</option>)}
              </select>
            </label>
            <label className="inventory-filter-control">
              <span>Status</span>
              <select
                aria-label="Filter pull requests by status"
                value={prFilterStatus}
                onChange={(event) => setPrFilterStatus(event.target.value)}
              >
                <option value="">All statuses</option>
                {prFilterStatuses.map((status) => <option key={status} value={status}>{status}</option>)}
              </select>
            </label>
            <label className="inventory-filter-control">
              <span>Repository</span>
              <select
                aria-label="Filter pull requests by repository"
                value={prFilterRepository}
                onChange={(event) => setPrFilterRepository(event.target.value)}
              >
                <option value="">All repositories</option>
                {prFilterRepositories.map((repository) => <option key={repository} value={repository}>{repository}</option>)}
              </select>
            </label>
            <label className="inventory-filter-control">
              <span>CI</span>
              <select
                aria-label="Filter pull requests by CI state"
                value={prFilterCi}
                onChange={(event) => setPrFilterCi(event.target.value)}
              >
                <option value="">All CI states</option>
                {prFilterCiStates.map((state) => <option key={state} value={state}>{state}</option>)}
              </select>
            </label>
            {prFiltersActive && (
              <button
                type="button"
                className="inventory-filter-clear"
                onClick={() => {
                  setPrFilterUser("");
                  setPrFilterStatus("");
                  setPrFilterRepository("");
                  setPrFilterCi("");
                }}
              >
                Clear filters
              </button>
            )}
          </div>
          <div className="table-wrap">
            <table className="pr-inventory-table">
              <thead><tr><th>Pull request</th><th>PR</th><th>Status</th><th>Feedback</th><th>CI</th><th>Last reviewed</th><th>Last updated</th><th>Preview</th><th>Actions</th></tr></thead>
              <tbody>
                {loading ? (
                  <SkeletonRows label="Loading open pull requests" columns={9} rows={6} />
                ) : inventory.pull_requests.length === 0 ? (
                  <tr><td colSpan={9} className="empty">GitHub inventory is syncing, or no open PRs were found.</td></tr>
                ) : filteredPulls.length === 0 ? (
                  <tr><td colSpan={9} className="empty">No pull requests match these filters.</td></tr>
                ) : groupedPulls.map((group) => {
                  const collapsed = collapsedPrGroups.has(group.key);
                  return <React.Fragment key={group.key}>
                  {prGroupMode !== "none" && <tr className={`pr-group-row${collapsed ? " collapsed" : ""}`}>
                    <td colSpan={9}>
                      <button
                        className="pr-group-toggle"
                        aria-expanded={!collapsed}
                        aria-label={`${collapsed ? "Expand" : "Collapse"} ${group.label} group`}
                        onClick={() => togglePrGroup(group.key)}
                      >
                        <span className="pr-group-label">
                          <strong>{group.label}</strong>
                          <span>{counted(group.pulls.length, "pull request")}</span>
                        </span>
                        <span className="pr-group-chevron" aria-hidden="true">⌄</span>
                      </button>
                    </td>
                  </tr>}
                  {!collapsed && group.pulls.map((pull) => {
                    const feedback = Math.max(pull.unresolved_threads ?? 0, pull.finding_count ?? 0);
                    const repositoryName = pull.repository.split("/").slice(-1)[0];
                    const pullPreviews = previewsByPull.get(`${pull.repository}#${pull.number}`) ?? [];
                    const exactPreview = pullPreviews.find((preview) => preview.head_sha === pull.head_sha);
                    const routedPreview = pullPreviews.find((preview) => ["active", "stale"].includes(preview.status));
                    const preview = exactPreview ?? routedPreview;
                    const previewsEnabled = repositoryPolicies.get(pull.repository)?.previews_enabled ?? false;
                    const details = [
                      `Status ${pull.health?.label ?? (pull.draft ? "draft" : "syncing")}`,
                      `Opened ${age(pull.created_at)} ago`,
                      `Head ${shortSha(pull.head_sha)}`,
                      `Reviewed ${pull.last_reviewed_at ? relativeTime(pull.last_reviewed_at) : "never"}`,
                      `${counted(pull.review_count ?? 0, "run")}`,
                      `${counted(pull.finding_count ?? 0, "finding")}`,
                      `CI ${pull.check_summary?.passing ?? 0} passing / ${pull.check_summary?.failing ?? 0} failing`,
                    ].join(" · ");
                    return <tr className="clickable-row pr-compact-row" key={`${pull.repository}#${pull.number}`} onClick={() => void openPrDetail(pull)}>
                      <td className="pr-summary-cell">
                        <div className="pr-row-primary">
                          {prGroupMode !== "repository" && (
                            <a href={pull.url} title={pull.repository} target="_blank" onClick={(event) => event.stopPropagation()}>{repositoryName}</a>
                          )}
                          <strong title={pull.title}>{pull.title}</strong>
                        </div>
                        <div className="pr-row-meta">
                          <span className="pr-row-info" title={details} tabIndex={0} aria-label={details}>ⓘ</span>
                          {prGroupMode !== "user" && <span>@{pull.author ?? "unknown"}</span>}
                          <span className="pr-branch" title={`${pull.head_ref} → ${pull.base_ref}`}>{pull.head_ref} → {pull.base_ref}</span>
                        </div>
                      </td>
                      <td className="pr-number-cell"><a href={pull.url} target="_blank" onClick={(event) => event.stopPropagation()}>#{pull.number}</a></td>
                      <td className="pr-status-cell">
                        <span className={`pr-health ${pull.health?.bucket ?? "waiting"}`}>{pull.health?.label ?? (pull.draft ? "draft" : "syncing")}</span>
                      </td>
                      <td className={`pr-feedback-cell ${feedback === 0 ? "none" : ""}`}>
                        <strong>{feedback === 0 ? "None" : feedback}</strong>
                        {feedback > 0 && <span>{feedback === 1 ? "item" : "items"}</span>}
                      </td>
                      <td className="pr-ci-cell">
                        <span className={`check-state ${pull.check_summary?.state ?? "none"}`}>{pull.check_summary?.state ?? "none"}</span>
                      </td>
                      <td className="pr-reviewed-cell">{pull.last_reviewed_at ? relativeTime(pull.last_reviewed_at) : "Never"}</td>
                      <td className="pr-updated-cell">{relativeTime(pull.updated_at)}</td>
                      <td className="pr-preview-cell">
                        {preview && ["active", "stale"].includes(preview.status) ? <>
                          <a href={preview.public_url} target="_blank" onClick={(event) => event.stopPropagation()}>Open ↗</a>
                          <span className={`preview-state ${preview.status}`}>{preview.status}</span>
                        </> : exactPreview && ["reserved", "queued", "building", "starting", "health_checking", "undeploying"].includes(exactPreview.status)
                          ? <span className={`state ${exactPreview.status}`}>{exactPreview.status.replaceAll("_", " ")}</span>
                          : exactPreview?.status === "failed"
                            ? <button title="Retry this exact-SHA preview build" onClick={(event) => { event.stopPropagation(); void retry(exactPreview); }}>Retry</button>
                            : exactPreview && canManuallyDeploy(exactPreview)
                              ? <button title="Rebuild and redeploy the existing exact-SHA revision" onClick={(event) => { event.stopPropagation(); void deploy(exactPreview); }}>Redeploy</button>
                              : previewsEnabled
                                ? <button title="Deploy the current PR head" onClick={(event) => { event.stopPropagation(); void deployCurrentPreview(pull.repository, pull.number); }}>Deploy</button>
                                : <button className="preview-off" title="Enable previews in repository settings" onClick={(event) => { event.stopPropagation(); openPreviewSettings(pull.repository); }}>Off</button>}
                      </td>
                      <td className="pr-row-actions">
                        <button
                          className="pr-action-icon review"
                          disabled={pull.draft}
                          aria-label={`Review ${pull.repository} pull request ${pull.number}`}
                          data-tooltip="Review current head"
                          title="Review the current head"
                          onClick={(event) => { event.stopPropagation(); void enqueuePr(pull.repository, pull.number, "review"); }}
                        ><ReviewActionIcon /></button>
                        <button
                          className="pr-action-icon fix"
                          disabled={pull.draft}
                          aria-label={`Fix ${pull.repository} pull request ${pull.number}`}
                          data-tooltip="Fix this PR with Netra"
                          title="Ask Netra to fix the current pull request"
                          onClick={(event) => { event.stopPropagation(); void enqueuePr(pull.repository, pull.number, "fix"); }}
                        ><FixActionIcon /></button>
                      </td>
                    </tr>;
                  })}
                </React.Fragment>;
                })}
              </tbody>
            </table>
          </div>
        </section>}

        {view === "pr" && prDetail && (() => {
          const pull = prDetail.pull_request;
          const pullOpen = pull.state.toLowerCase() === "open";
          const openFeedback = pull.unresolved_threads ?? 0;
          const feedbackCount = Math.max(openFeedback, prDetail.findings.length);
          const githubOnlyFeedback = Math.max(0, openFeedback - prDetail.findings.length);
          const previewsEnabled = repositoryPolicies.get(pull.repository)?.previews_enabled ?? false;
          return <section className="pr-detail-page">
            <div className="pr-hero activity-card">
              <div>
                <div className="pr-hero-title">
                  <span className={`pr-health ${pull.health?.bucket ?? "waiting"}`}>{pull.health?.label ?? "unknown"}</span>
                  {!pullOpen && <span className="state inactive">{pull.state.toLowerCase()}</span>}
                  {pull.draft && <span className="state stale">draft</span>}
                </div>
                <h2>{pull.title}</h2>
                <p>
                  @{pull.author ?? "unknown"} · opened {relativeTime(pull.created_at)} · updated {relativeTime(pull.updated_at)}
                </p>
                <p><code>{pull.base_ref} ← {pull.head_ref}</code> · <code>{pull.head_sha}</code></p>
              </div>
              <div className="pr-hero-actions">
                <a href={pull.url} target="_blank">Open on GitHub ↗</a>
                <button disabled={pull.draft || !pullOpen} onClick={() => void enqueuePr(pull.repository, pull.number, "review")}>Review</button>
                <button disabled={pull.draft || !pullOpen} title="Revalidate a complete saved source report without another model call" onClick={() => void enqueuePr(pull.repository, pull.number, "recheck")}>Recheck evidence</button>
                {previewsEnabled && <button disabled={!pullOpen} onClick={() => void deployCurrentPreview(pull.repository, pull.number)}>Deploy preview</button>}
                <button className="primary" disabled={pull.draft || !pullOpen} onClick={() => void enqueuePr(pull.repository, pull.number, "fix")}>Fix</button>
                {pull.mute
                  ? <button disabled={!pullOpen} onClick={() => void setMute(pull, "off")}>Unmute</button>
                  : <>
                    <button disabled={!pullOpen} onClick={() => void setMute(pull, "4h")}>Mute 4h</button>
                    <button disabled={!pullOpen} onClick={() => void setMute(pull, "permanent")}>Mute</button>
                  </>}
              </div>
            </div>

            <div className="health-grid">
              <HealthFact label="CI" value={pull.check_summary?.state ?? "none"} tone={pull.check_summary?.state ?? "none"} />
              <HealthFact label="Merge" value={(pull.mergeable ?? "unknown").toLowerCase()} tone={pull.mergeable === "CONFLICTING" ? "failed" : "passing"} />
              <HealthFact label="Decision" value={(pull.review_decision ?? "review required").replaceAll("_", " ").toLowerCase()} tone={pull.review_decision === "CHANGES_REQUESTED" ? "failed" : "none"} />
              <HealthFact label="Review feedback" value={reviewFeedbackSummary(pull)} tone={openFeedback > 0 || (pull.blocking_findings ?? 0) > 0 ? "failed" : "passing"} />
              <HealthFact label="Last reviewed" value={pull.last_reviewed_at ? relativeTime(pull.last_reviewed_at) : "never"} tone="none" />
            </div>

            <div className="detail-tabs">
              {(["overview", "timeline", "runs", "tests", "feedback", "preview"] as const).map((tabName) => (
                <button className={prTab === tabName ? "active" : ""} key={tabName} onClick={() => setPrTab(tabName)}>
                  {tabName}
                  {tabName === "runs" && <span>{prDetail.jobs.length}</span>}
                  {tabName === "tests" && <span>{prDetail.ui_tests?.length ?? 0}</span>}
                  {tabName === "feedback" && <span>{feedbackCount}</span>}
                  {tabName === "preview" && <span>{prDetail.previews.length}</span>}
                </button>
              ))}
            </div>

            {prTab === "overview" && <div className="detail-grid">
              <section className="activity-card detail-section">
                <p className="eyebrow">PULL REQUEST</p>
                <h2>Summary</h2>
                <div className="stat-strip">
                  <div><strong>+{pull.additions ?? 0}</strong><span>additions</span></div>
                  <div><strong>−{pull.deletions ?? 0}</strong><span>deletions</span></div>
                  <div><strong>{pull.changed_files ?? 0}</strong><span>files</span></div>
                  <div><strong>{pull.commits ?? 0}</strong><span>commits</span></div>
                  <div><strong>{pull.comments ?? 0}</strong><span>comments</span></div>
                </div>
                <pre className="pr-body">{pull.body?.trim() || "No pull request description."}</pre>
              </section>
              <section className="activity-card detail-section">
                <p className="eyebrow">CHECKS</p>
                <h2>{pull.check_summary?.total ?? 0} CI checks</h2>
                <div className="check-list">
                  {(pull.checks ?? []).length === 0
                    ? <p>No CI checks reported.</p>
                    : (pull.checks ?? []).map((check) => (
                      <a key={check.name} href={check.url} target="_blank">
                        <span>{check.name}</span>
                        <strong className={`check-state ${check.conclusion ?? check.status ?? "none"}`}>{check.conclusion ?? check.status}</strong>
                      </a>
                    ))}
                </div>
              </section>
              {(prDetail.ui_tests?.length ?? 0) > 0 && <PullRequestTests runs={prDetail.ui_tests ?? []} headSha={pull.head_sha} />}
              <section className="activity-card detail-section pr-quality-evidence">
                <p className="eyebrow">EXACT-HEAD QUALITY EVIDENCE</p>
                {prDetail.quality_evidence ? <>
                  <div className="section-heading">
                    <div>
                      <h2>Tests and changed-line coverage</h2>
                      <p className="section-description">Report for <code>{shortSha(prDetail.quality_evidence.head_sha)}</code> · {relativeTime(prDetail.quality_evidence.ingested_at)}</p>
                    </div>
                    {prDetail.quality_evidence.provenance && <a href={prDetail.quality_evidence.provenance} target="_blank">CI run ↗</a>}
                  </div>
                  <div className="quality-evidence-summary">
                    <div><strong>{prDetail.quality_evidence.tests.total}</strong><span>tests</span><small>{prDetail.quality_evidence.tests.failed + prDetail.quality_evidence.tests.errored} failing</small></div>
                    <div><strong>+{prDetail.quality_evidence.tests.added_since_release}</strong><span>tests added</span><small>−{prDetail.quality_evidence.tests.removed_since_release} removed</small></div>
                    <div><strong>{prDetail.quality_evidence.coverage.percent == null ? "—" : `${prDetail.quality_evidence.coverage.percent.toFixed(1)}%`}</strong><span>changed-line coverage</span><small>{prDetail.quality_evidence.coverage.covered_lines}/{prDetail.quality_evidence.coverage.measured_lines} measured lines</small></div>
                    <div><strong>{prDetail.quality_evidence.coverage.unmeasured_lines}</strong><span>unmeasured changed lines</span><small>{prDetail.quality_evidence.coverage.changed_lines} changed total</small></div>
                  </div>
                  {prDetail.quality_evidence.tests.failures.length > 0 && <div className="quality-failures">
                    {prDetail.quality_evidence.tests.failures.slice(0, 8).map((failure) => <div key={`${failure.suite}:${failure.name}`}><strong>{failure.name}</strong><span>{failure.file ?? failure.suite}{failure.line ? `:${failure.line}` : ""} · {failure.status}</span></div>)}
                  </div>}
                  <div className="table-wrap"><table className="quality-table"><thead><tr><th>Changed file</th><th className="numeric">Changed</th><th className="numeric">Measured</th><th className="numeric">Covered</th><th className="numeric">Coverage</th></tr></thead><tbody>
                    {prDetail.quality_evidence.coverage.files.map((file) => <tr key={file.path}><td><code>{file.path}</code></td><td className="numeric">{file.changed_lines}</td><td className="numeric">{file.measured_lines}</td><td className="numeric">{file.covered_lines}</td><td className={`numeric ${file.coverage_percent != null && file.coverage_percent < 80 ? "value-bad" : ""}`}>{file.coverage_percent == null ? "—" : `${file.coverage_percent.toFixed(1)}%`}</td></tr>)}
                  </tbody></table></div>
                </> : <>
                  <h2>No report for this head</h2>
                  <p className="section-description">Upload JUnit and Cobertura or LCOV from this PR's CI. Netra fences the report against GitHub's live same-repository head before accepting it.</p>
                  <code className="quality-command">bin/netra-report-tests --pr {pull.number} --commit "$GITHUB_SHA" reports/junit.xml coverage/lcov.info</code>
                </>}
              </section>
            </div>}

            {prTab === "timeline" && <section className="activity-card detail-section">
              <p className="eyebrow">PR ACTIVITY</p>
              <h2>{prDetail.events.length} recorded events</h2>
              <div className="timeline">
                {prDetail.events.length === 0 ? <p>No events recorded for this PR.</p> : prDetail.events.map((event) => {
                  const description = humanEvent(event);
                  return <div key={`${event.sequence}-${event.created_at}`}>
                    <i />
                    <time>{relativeTime(event.created_at)}</time>
                    <strong>{description.title}</strong>
                    <p>{description.detail}</p>
                  </div>;
                })}
              </div>
            </section>}

            {prTab === "runs" && <section className="activity-card detail-section">
              <p className="eyebrow">DURABLE RUNS</p>
              <h2>{prDetail.jobs.length} review and fix runs</h2>
              <div className="run-cards">
                {prDetail.jobs.length === 0 ? <p>No runs recorded.</p> : prDetail.jobs.map((job) => (
                  <article key={job.id}>
                    <div className="run-card-head">
                      <div><span className={`state ${job.status}`}>{job.status}</span><strong>{job.mode} · {runProviderLabel(
                        job,
                        prDetail.jobs.some((other) => other.id !== job.id && ["running", "finalizing"].includes(other.status))
                          ? "waiting for active PR run"
                          : "waiting for worker slot",
                      )}</strong></div>
                      <code>{shortSha(job.head_sha)}</code>
                    </div>
                    <div className="run-timing">
                      <span>queued <strong>{durationBetween(job.created_at, job.started_at)}</strong></span>
                      <span>provider <strong>{durationBetween(job.started_at, job.provider_finished_at ?? (job.status === "running" ? undefined : job.updated_at))}</strong></span>
                      <span>finalize <strong>{job.provider_finished_at ? durationBetween(job.provider_finished_at, job.completed_at ?? job.updated_at) : "—"}</strong></span>
                      <span>total <strong>{durationBetween(job.created_at, job.completed_at ?? (["completed", "failed"].includes(job.status) ? job.updated_at : undefined))}</strong></span>
                    </div>
                    <p>{job.trigger} · attempt {job.attempt ?? 1} · started {relativeTime(job.started_at ?? job.created_at)}</p>
                    {job.error && <pre className="failure">{job.error}</pre>}
                    {job.log_tail && <details><summary>Provider log</summary><pre className="review-log">{job.log_tail}</pre></details>}
                    {["queued", "running", "finalizing"].includes(job.status) && <button onClick={() => void abortRun(job, "review")}>Abort run</button>}
                  </article>
                ))}
              </div>
            </section>}

            {prTab === "tests" && <PullRequestTests runs={prDetail.ui_tests ?? []} headSha={pull.head_sha} />}

            {prTab === "feedback" && <section className="activity-card detail-section">
              <p className="eyebrow">REVIEW FEEDBACK</p>
              <h2>{counted(feedbackCount, "feedback item")}</h2>
              <div className="finding-cards">
                {feedbackCount === 0 ? <p>No review feedback recorded for this PR.</p> : prDetail.findings.map((finding) => (
                  <article key={finding.id}>
                    <span className={`priority ${finding.priority.toLowerCase()}`}>{finding.priority}</span>
                    <div><strong>{finding.title || "Untitled finding"}</strong><code>{finding.path ?? "general"} · {shortSha(finding.head_sha)} · {finding.provider ?? "provider unknown"}</code><p>{finding.body}</p>
                      <div className="finding-feedback">
                        <span className={`feedback-status ${finding.feedback_status ?? "unresolved"}`}>{finding.feedback_status ?? "unresolved"}</span>
                        {(["fixed", "accepted", "dismissed", "stale"] as const).map((status) => <button key={status} disabled={finding.feedback_status === status} onClick={() => void setFindingFeedback(finding, status)}>{status}</button>)}
                      </div>
                      {finding.feedback_reason && <small>{finding.feedback_reason}</small>}
                    </div>
                  </article>
                ))}
                {githubOnlyFeedback > 0 && <p className="feedback-note">
                  {counted(githubOnlyFeedback, "additional unresolved GitHub discussion")} · <a href={pull.url} target="_blank">Open on GitHub ↗</a>
                </p>}
              </div>
            </section>}

            {prTab === "preview" && <section className="activity-card detail-section">
              <p className="eyebrow">EXACT-SHA PREVIEWS</p>
              <h2>{prDetail.previews.length} preview revisions</h2>
              <div className="run-cards">
                {prDetail.previews.length === 0 ? <p>No preview revision was created for this PR.</p> : prDetail.previews.map((preview) => (
                  <article key={preview.id}>
                    <div className="run-card-head"><span className={`state ${preview.status}`}>{preview.status}</span><code>{shortSha(preview.head_sha)}</code></div>
                    <p>Created {relativeTime(preview.created_at)} · expires {relativeTime(preview.expires_at)}</p>
                    <p>Active {shortSha(preview.active_head_sha)} · current {shortSha(preview.current_head_sha)}</p>
                    {(() => {
                      const usage = resources?.previews.find((candidate) => candidate.preview_id === preview.id)?.docker;
                      return usage && <div className="preview-usage">
                        <span><strong>{usage.CPUPerc ?? "—"}</strong> CPU</span>
                        <span><strong>{usage.MemPerc ?? "—"}</strong> memory</span>
                        <span><strong>{usage.NetIO ?? "—"}</strong> network</span>
                        <span><strong>{usage.PIDs ?? "—"}</strong> PIDs</span>
                      </div>;
                    })()}
                    {preview.error && <pre className="failure">{preview.error}</pre>}
                    <div className="drawer-actions">
                      {["active", "stale"].includes(preview.status) && <a className="primary" href={preview.public_url} target="_blank">Open preview ↗</a>}
                      {!terminalStates.has(preview.status) && <button
                        disabled={preview.status === "undeploying" || undeployingIds.has(preview.id)}
                        onClick={() => void undeploy({ id: preview.id, repository: repoName(preview), number: preview.number })}
                      >{preview.status === "undeploying" || undeployingIds.has(preview.id) ? "Undeploying…" : "Undeploy"}</button>}
                      {canManuallyDeploy(preview) && <button className="primary" onClick={() => void deploy(preview)}>Redeploy exact SHA</button>}
                      {preview.status === "failed" && <button onClick={() => void retry(preview)}>Retry</button>}
                    </div>
                  </article>
                ))}
              </div>
            </section>}
          </section>;
        })()}

        {view === "work" && workTab === "issues" && <section className="activity-card">
          <div className="section-heading">
            <div>
              <p className="eyebrow">GITHUB INVENTORY</p>
              <h2>{loading
                ? <SkeletonBar width={168} height={17} />
                : issueFiltersActive
                ? `${filteredIssues.length} of ${inventory.issues.length} open issues`
                : `${inventory.issues.length} open issues`}</h2>
            </div>
            <div className="inventory-actions">
              <label className="pr-group-control">
                <span>Group by</span>
                <select
                  aria-label="Group issues"
                  value={issueGroupMode}
                  onChange={(event) => setIssueGroupMode(event.target.value as IssueGroupMode)}
                >
                  <option value="none">No grouping</option>
                  <option value="user">User</option>
                  <option value="repository">Repository</option>
                </select>
              </label>
              <span className="policy">synced {relativeTime(inventory.synced_at)}</span>
              <button disabled={inventorySyncing} onClick={() => void syncGithubInventory()}>
                {inventorySyncing ? "Syncing GitHub…" : "Sync from GitHub"}
              </button>
            </div>
          </div>
          <div className="inventory-filters" role="search" aria-label="Filter issues">
            <label className="inventory-filter-control">
              <span>User</span>
              <select
                aria-label="Filter issues by user"
                value={issueFilterUser}
                onChange={(event) => setIssueFilterUser(event.target.value)}
              >
                <option value="">All users</option>
                {issueFilterUsers.map((user) => <option key={user} value={user}>{user}</option>)}
              </select>
            </label>
            <label className="inventory-filter-control">
              <span>Repository</span>
              <select
                aria-label="Filter issues by repository"
                value={issueFilterRepository}
                onChange={(event) => setIssueFilterRepository(event.target.value)}
              >
                <option value="">All repositories</option>
                {issueFilterRepositories.map((repository) => <option key={repository} value={repository}>{repository}</option>)}
              </select>
            </label>
            {issueFiltersActive && (
              <button
                type="button"
                className="inventory-filter-clear"
                onClick={() => {
                  setIssueFilterUser("");
                  setIssueFilterRepository("");
                }}
              >
                Clear filters
              </button>
            )}
          </div>
          <div className="table-wrap">
            <table>
              <thead><tr><th>Repository / Issue</th><th>Age</th><th>Title / Owner</th><th>Discussion</th><th>Netra status</th><th>Updated</th><th>Actions</th></tr></thead>
              <tbody>
                {loading ? (
                  <SkeletonRows label="Loading open issues" columns={7} rows={6} />
                ) : inventory.issues.length === 0 ? (
                  <tr><td colSpan={7} className="empty">GitHub inventory is syncing, or no open Issues were found.</td></tr>
                ) : filteredIssues.length === 0 ? (
                  <tr><td colSpan={7} className="empty">No issues match these filters.</td></tr>
                ) : groupedIssues.map((group) => {
                  const collapsed = collapsedPrGroups.has(group.key);
                  return <React.Fragment key={group.key}>
                  {issueGroupMode !== "none" && <tr className={`pr-group-row${collapsed ? " collapsed" : ""}`}>
                    <td colSpan={7}>
                      <button
                        className="pr-group-toggle"
                        aria-expanded={!collapsed}
                        aria-label={`${collapsed ? "Expand" : "Collapse"} ${group.label} group`}
                        onClick={() => togglePrGroup(group.key)}
                      >
                        <span className="pr-group-label">
                          <strong>{group.label}</strong>
                          <span>{counted(group.issues.length, "issue")}</span>
                        </span>
                        <span className="pr-group-chevron" aria-hidden="true">⌄</span>
                      </button>
                    </td>
                  </tr>}
                  {!collapsed && group.issues.map((issue) => (
                  <tr className="clickable-row" key={`${issue.repository}#${issue.number}`} onClick={() => void openIssueDetail(issue)}>
                    <td>{issueGroupMode === "repository"
                      ? <span>Issue #{issue.number}</span>
                      : <><a href={issue.url} target="_blank" onClick={(event) => event.stopPropagation()}>{issue.repository}</a><span>Issue #{issue.number}</span></>}</td>
                    <td>{age(issue.created_at)}</td>
                    <td><strong>{issue.title}</strong><span>{issueGroupMode === "user" ? "" : `@${issue.author ?? "unknown"} · `}{issue.assignees.length ? `assigned to ${issue.assignees.join(", ")}` : "unassigned"}</span></td>
                    <td><strong>{issue.comments ?? 0} comments</strong><span>{issue.labels.length ? issue.labels.join(", ") : "no labels"}</span></td>
                    <td>{(() => {
                      const summary = issueRunSummary.get(`${issue.repository}#${issue.number}`);
                      return summary
                        ? <><span className={`state ${summary.latest.status}`}>{summary.latest.status}</span><span>{summary.count} run{summary.count === 1 ? "" : "s"} · {summary.latest.mode}</span></>
                        : <><span className="state inactive">not reviewed</span><span>no runs</span></>;
                    })()}</td>
                    <td>{relativeTime(issue.updated_at)}</td>
                    <td className="run-actions">
                      <button onClick={(event) => { event.stopPropagation(); void enqueueIssue(issue.repository, issue.number, "review"); }}>Review</button>
                      <button onClick={(event) => { event.stopPropagation(); void enqueueIssue(issue.repository, issue.number, "fix"); }}>Fix</button>
                    </td>
                  </tr>
                  ))}
                  </React.Fragment>;
                })}
              </tbody>
            </table>
          </div>
        </section>}

        {view === "work" && workTab === "teams" && <>
          {historyPending ? <MetricsSkeleton
            label="Loading Teams intake metrics"
            className="teams-request-metrics"
          /> : <section className="metrics teams-request-metrics">
            <Metric label="Requests received" value={teamsHistory.summary.total_requests} tone="blue" />
            <Metric label="GitHub issues created" value={teamsHistory.summary.issues_created} tone="green" />
            <Metric label="Active conversations" value={teamsHistory.summary.active_requests} tone="amber" />
            <Metric label="Issue conversion" value={`${teamsHistory.summary.completion_rate_percent.toFixed(0)}%`} tone="green" />
          </section>}
          <section className="activity-card teams-request-history">
            <div className="section-heading">
              <div>
                <p className="eyebrow">MICROSOFT TEAMS INTAKE</p>
                <h2>{historyPending
                  ? <SkeletonBar width={188} height={17} />
                  : counted(teamsHistory.requests.length, "recorded request")}</h2>
              </div>
              <span className="policy">request summaries retained locally</span>
            </div>
            {historyPending ? <SkeletonStack label="Loading Teams requests" rows={3} lines={2} /> : <div className="teams-request-list">
              {teamsHistory.requests.length === 0
                ? <div className="teams-request-empty">
                  <strong>No Teams requests yet</strong>
                  <p>Open Netra in Teams and send <code>create issue</code>. Requests appear here as the guided conversation progresses.</p>
                </div>
                : teamsHistory.requests.map((request) => <article key={request.id} className="teams-request-row">
                  <div className="teams-request-avatar" aria-hidden="true">{request.requester.trim().charAt(0).toUpperCase() || "?"}</div>
                  <div className="teams-request-thread">
                    <div className="teams-request-author">
                      <strong>{request.requester}</strong>
                      <span>{request.channel} · {relativeTime(request.created_at)}</span>
                    </div>
                    <div className="teams-request-bubble">
                      <div className="teams-request-title">
                        <strong>{request.title || "Gathering request details…"}</strong>
                        <span className={`state ${request.state}`}>{request.issue_number ? "issue created" : request.state.replaceAll("_", " ")}</span>
                      </div>
                      {request.body && <p>{request.body}</p>}
                      <div className="teams-request-tags">
                        <span>{request.repository ?? "repository pending"}</span>
                        <span>{request.mode ?? "mode pending"}</span>
                        <span>{request.conversation_type.replaceAll("Chat", " chat")}</span>
                        {request.execution_status && <span>Netra {request.execution_status}</span>}
                      </div>
                    </div>
                    <div className="teams-request-links">
                      {request.source_message_url && <a href={request.source_message_url} target="_blank">Open Teams message ↗</a>}
                      {request.issue_url && <a href={request.issue_url} target="_blank">GitHub issue #{request.issue_number} ↗</a>}
                      {request.fix_pr_url && <a href={request.fix_pr_url} target="_blank">Fix pull request ↗</a>}
                      <span>updated {relativeTime(request.updated_at)}</span>
                    </div>
                  </div>
                </article>)}
            </div>}
          </section>
        </>}

        {view === "work" && workTab === "merge" && <PortalApp embedded actor={currentUser} />}

        {view === "issue" && issueDetail && <section className="pr-detail-page">
          <div className="pr-hero activity-card">
            <div>
              <div className="pr-hero-title"><span className="pr-health waiting">open issue</span></div>
              <h2>{issueDetail.issue.title}</h2>
              <p>@{issueDetail.issue.author ?? "unknown"} · opened {relativeTime(issueDetail.issue.created_at)} · updated {relativeTime(issueDetail.issue.updated_at)}</p>
              <p>
                {issueDetail.issue.assignees?.length
                  ? `Assigned to ${issueDetail.issue.assignees.join(", ")}`
                  : "Unassigned"}
                {" · "}{issueDetail.issue.comments ?? 0} comments
                {" · "}{issueDetail.issue.labels?.join(" · ") || "No labels"}
              </p>
            </div>
            <div className="pr-hero-actions">
              <a href={issueDetail.issue.url} target="_blank">Open on GitHub ↗</a>
              <button onClick={() => void enqueueIssue(issueDetail.issue.repository, issueDetail.issue.number, "review")}>Review</button>
              <button className="primary" onClick={() => void enqueueIssue(issueDetail.issue.repository, issueDetail.issue.number, "fix")}>Open fix PR</button>
            </div>
          </div>
          <div className="detail-grid">
            <section className="activity-card detail-section">
              <p className="eyebrow">ISSUE</p>
              <h2>Description</h2>
              <pre className="pr-body">{issueDetail.issue.body?.trim() || "No issue description."}</pre>
            </section>
            <section className="activity-card detail-section">
              <p className="eyebrow">DURABLE RUNS</p>
              <h2>{issueDetail.jobs.length} review and fix runs</h2>
              <div className="run-cards">
                {issueDetail.jobs.length === 0 ? <p>No Issue runs recorded.</p> : issueDetail.jobs.map((job) => <article key={job.id}>
                  <div className="run-card-head">
                    <div><span className={`state ${job.status}`}>{job.status}</span><strong>{job.mode} · {runProviderLabel(
                      job,
                      issueDetail.jobs.some((other) => other.id !== job.id && ["running", "finalizing"].includes(other.status))
                        ? "waiting for active issue run"
                        : "waiting for worker slot",
                    )}</strong></div>
                    <code>{shortSha(job.base_sha)}</code>
                  </div>
                  <div className="run-timing">
                    <span>queued <strong>{durationBetween(job.created_at, job.started_at)}</strong></span>
                    <span>provider <strong>{durationBetween(job.started_at, job.provider_finished_at)}</strong></span>
                    <span>total <strong>{durationBetween(job.created_at, job.completed_at ?? job.updated_at)}</strong></span>
                  </div>
                  <p>{job.trigger} · attempt {job.attempt ?? 1}</p>
                  {job.fix_pr_url && <a href={job.fix_pr_url} target="_blank">Linked fix PR #{job.fix_pr_number ?? ""} ↗</a>}
                  {job.error && <pre className="failure">{job.error}</pre>}
                  {job.log_tail && <details><summary>Provider log</summary><pre className="review-log">{job.log_tail}</pre></details>}
                  {["queued", "running", "finalizing"].includes(job.status) && <button onClick={() => void abortRun(job, "issue")}>Abort run</button>}
                </article>)}
              </div>
            </section>
          </div>
          <section className="activity-card detail-section">
            <p className="eyebrow">ACTIVITY</p>
            <h2>{issueDetail.events.length} recorded events</h2>
            <div className="timeline">
              {issueDetail.events.map((event) => {
                const description = humanEvent(event);
                return <div key={event.sequence}><i /><time>{relativeTime(event.created_at)}</time><strong>{description.title}</strong><p>{description.detail}</p></div>;
              })}
            </div>
          </section>
        </section>}

        {view === "overview" && <section className="activity-card">
          <div className="section-heading">
            <div>
              <p className="eyebrow">LIVE DEPLOYMENTS</p>
              <h2>On-demand PR previews</h2>
            </div>
          </div>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Repository / PR</th>
                  <th>State</th>
                  <th>Reviewed head</th>
                  <th>Route</th>
                  <th>Expiry</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <SkeletonRows label="Loading preview state" columns={6} rows={3} />
                ) : livePreviews.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="empty">
                      No live previews. Preview history remains available on each pull request.
                    </td>
                  </tr>
                ) : (
                  livePreviews.map((preview) => (
                    <tr key={preview.id} onClick={() => setSelected(preview)}>
                      <td>
                        <strong>{repoName(preview)}</strong>
                        <span>PR #{preview.number}</span>
                      </td>
                      <td><span className={`state ${preview.status}`}>{preview.status.replaceAll("_", " ")}</span></td>
                      <td>
                        <code>{shortSha(preview.head_sha)}</code>
                        {preview.current_head_sha &&
                          preview.current_head_sha !== preview.head_sha && (
                            <span className="stale-note">current {shortSha(preview.current_head_sha)}</span>
                          )}
                      </td>
                      <td>
                        {["active", "stale"].includes(preview.status) ? (
                          <a href={preview.public_url} target="_blank" onClick={(event) => event.stopPropagation()}>
                            Open ↗
                          </a>
                        ) : (
                          <span>Reserved</span>
                        )}
                      </td>
                      <td>{relativeTime(preview.expires_at)}</td>
                      <td>
                        {!terminalStates.has(preview.status) && (
                          <button
                            aria-label={`Undeploy ${repoName(preview)} PR #${preview.number}`}
                            className="icon-button"
                            disabled={preview.status === "undeploying" || undeployingIds.has(preview.id)}
                            title={preview.status === "undeploying" || undeployingIds.has(preview.id) ? "Undeploying…" : "Undeploy"}
                            onClick={(event) => {
                            event.stopPropagation();
                            void undeploy({ id: preview.id, repository: repoName(preview), number: preview.number });
                          }}>{preview.status === "undeploying" || undeployingIds.has(preview.id) ? "…" : "×"}</button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>}

        {view === "overview" && activeIncidents.length > 0 && <section className="activity-card incident-card">
          <div className="section-heading">
            <div>
              <p className="eyebrow">OPERATIONAL ATTENTION</p>
              <h2>{activeIncidents.length} active incident{activeIncidents.length === 1 ? "" : "s"}</h2>
            </div>
          </div>
          <div className="incident-list">
            {activeIncidents.map((incident) => <article key={incident.fingerprint}>
              <span className={`incident-severity ${incident.severity}`}>{incident.severity}</span>
              <div className="incident-copy">
                <strong>{incident.title}</strong>
                <p>{incidentDetailSummary(incident.detail || incident.kind.replaceAll("_", " "))}</p>
                {incident.detail && <details className="incident-diagnostics">
                  <summary>View diagnostics</summary>
                  <pre>{incident.detail}</pre>
                </details>}
                <small>{relativeTime(incident.created_at)}</small>
              </div>
              <button
                className="action-button incident-action"
                disabled={acknowledgingIncident === incident.fingerprint}
                onClick={() => void acknowledgeIncident(incident)}
                type="button"
              >
                {acknowledgingIncident === incident.fingerprint ? "Acknowledging…" : "Acknowledge"}
              </button>
            </article>)}
          </div>
        </section>}

        {view === "insights" && insightTab === "reviews" && <ReviewQualityPanel />}

        {view === "insights" && insightTab === "activity" && <section className="activity-card">
          <div className="section-heading">
            <div><p className="eyebrow">DURABLE EVENT LOG</p><h2>{historyPending
              ? <SkeletonBar width={166} height={17} />
              : `${events.length} recent events`}</h2></div>
            <span className="policy">newest first</span>
          </div>
          {historyPending ? <SkeletonTimeline label="Loading the event log" /> : <div className="timeline global-timeline">
            {events.map((event) => {
              const description = humanEvent(event);
              return <div key={event.sequence}>
                <i />
                <time>{relativeTime(event.created_at)}</time>
                <strong>{description.title}</strong>
                <p>{event.repository ? `${event.repository}${event.number ? ` #${event.number}` : ""} · ` : ""}{description.detail}</p>
              </div>;
            })}
          </div>}
        </section>}

        {view === "insights" && insightTab === "completed" && <section className="activity-card">
          <div className="section-heading">
            <div><p className="eyebrow">COMPLETED WORK</p><h2>{historyPending
              ? <SkeletonBar width={244} height={17} />
              : `${completedPulls.length} merged or closed PRs`}</h2></div>
            {historyPending
              ? <SkeletonBar width={72} height={11} />
              : <span className="policy">{completedPulls.filter((pull) => pull.state === "merged").length} merged</span>}
          </div>
          <div className="table-wrap">
            <table>
              <thead><tr><th>State</th><th>Repository / PR</th><th>Title</th><th>Author</th><th>Reviews</th><th>Findings</th><th>When</th></tr></thead>
              <tbody>{historyPending
                ? <SkeletonRows label="Loading completed pull requests" columns={7} rows={6} />
                : completedPulls.map((pull) => <tr key={`${pull.repo}#${pull.number}`}>
                <td><span className={`state ${pull.state}`}>{pull.state}</span></td>
                <td>{pull.url ? <a href={pull.url} target="_blank">{pull.repo}</a> : <strong>{pull.repo}</strong>}<span>PR #{pull.number}</span></td>
                <td><strong>{pull.title ?? "—"}</strong><span>{pull.branch && pull.base ? `${pull.branch} → ${pull.base}` : ""}</span></td>
                <td>{pull.author ?? "—"}</td>
                <td>{pull.review_count ?? 0}</td>
                <td>{pull.findings_count ?? 0}</td>
                <td>{relativeTime(pull.merged_at ?? pull.closed_at ?? pull.completed_at)}</td>
              </tr>)}</tbody>
            </table>
          </div>
        </section>}

        {view === "insights" && insightTab === "analytics" && <>
          <div className="analytics-toolbar">
            <div>
              <span>Time range</span>
              <p>{analyticsRange === "all"
                ? `${allTimeAnalyticsDays} days of recorded Netra history`
                : "The most recent 14 UTC calendar days"}{analyticsFilterSummary}</p>
            </div>
            <div className="analytics-toolbar-controls">
              <label className="analytics-filter-control">
                <span>Repository</span>
                <select
                  aria-label="Filter analytics by repository"
                  title="Limit charts and metrics to one repository"
                  value={analyticsRepository}
                  onChange={(event) => {
                    const repository = event.target.value;
                    setAnalyticsRepository(repository);
                    if (!analyticsUser) return;
                    const stillPresent = analyticsUserChoices(
                      analyticsFilterIdentities,
                      { repository, user: analyticsUser },
                    ).some((name) => name.toLocaleLowerCase() === analyticsUser.toLocaleLowerCase());
                    if (!stillPresent) setAnalyticsUser("");
                  }}
                >
                  <option value="">All repositories</option>
                  {analyticsRepositoryOptions.map((repository) => (
                    <option key={repository} value={repository}>{repository}</option>
                  ))}
                </select>
              </label>
              <label className="analytics-filter-control">
                <span>User</span>
                <select
                  aria-label="Filter analytics by user"
                  title="Limit charts and metrics to one PR author or Teams requester"
                  value={analyticsUser}
                  onChange={(event) => setAnalyticsUser(event.target.value)}
                >
                  <option value="">All users</option>
                  {analyticsUserOptions.map((user) => (
                    <option key={user} value={user}>{user}</option>
                  ))}
                </select>
              </label>
              {(analyticsRepository || analyticsUser) && (
                <button
                  type="button"
                  className="analytics-filter-clear"
                  onClick={() => {
                    setAnalyticsRepository("");
                    setAnalyticsUser("");
                  }}
                >
                  Clear filters
                </button>
              )}
              <div className="analytics-range-control" role="group" aria-label="Analytics time range">
                <button
                  className={analyticsRange === "14d" ? "active" : ""}
                  aria-pressed={analyticsRange === "14d"}
                  onClick={() => setAnalyticsRange("14d")}
                >
                  Last two weeks
                </button>
                <button
                  className={analyticsRange === "all" ? "active" : ""}
                  aria-pressed={analyticsRange === "all"}
                  onClick={() => setAnalyticsRange("all")}
                >
                  All time
                </button>
              </div>
            </div>
          </div>
          {historyPending && <>
            <MetricsSkeleton
              label="Loading analytics metrics"
              count={8}
              className="analytics-metrics"
            />
            <ChartCardSkeleton
              label="Loading Teams intake analytics"
              eyebrow={`${analyticsRangeLabel} TEAMS INTAKE`}
              title="Requests converted into GitHub work"
              className="teams-insights-card"
              height={230}
              legend={3}
            />
            <section className="analytics-trend-grid">
              {analyticsTrendSkeletons.map((chart) => <ChartCardSkeleton
                key={chart.title}
                label={`Loading ${chart.title.toLowerCase()}`}
                eyebrow={chart.eyebrow}
                title={chart.title}
                className={chart.wide ? "analytics-chart-wide" : ""}
                height={230}
                legend={chart.legend}
              />)}
            </section>
          </>}
          {!historyPending && <section className="metrics analytics-metrics">
            <Metric label={`PRs created · ${analyticsRangeShortLabel}`} value={analyticsSummary.created} tone="blue" />
            <Metric label={`PRs merged · ${analyticsRangeShortLabel}`} value={analyticsSummary.merged} tone="green" />
            <Metric
              label={`Average review time · ${analyticsRangeShortLabel}`}
              value={`${analyticsSummary.averageReviewMinutes.toFixed(1)}m`}
              tone="amber"
            />
            <Metric
              label={`Average merge time · ${analyticsRangeShortLabel}`}
              value={compactHours(analyticsSummary.averageMergeHours)}
              tone="blue"
            />
            <Metric
              label={`Findings per PR · ${analyticsRangeShortLabel}`}
              value={analyticsSummary.findingsPerPr.toFixed(1)}
              tone="red"
            />
            <Metric
              label={`Provider success · ${analyticsRangeShortLabel}`}
              value={`${analyticsSummary.providerSuccessRate.toFixed(0)}%`}
              tone="green"
            />
            <Metric
              label={`Threads resolved · ${analyticsRangeShortLabel}`}
              value={analyticsSummary.resolvedThreads}
              tone="blue"
            />
            <Metric
              label="Threads unresolved · now"
              value={analyticsSummary.unresolvedThreads}
              tone="red"
            />
          </section>}
          {!historyPending && <section className="activity-card teams-insights-card">
            <div className="section-heading">
              <div><p className="eyebrow">{analyticsRangeLabel} TEAMS INTAKE</p><h2>Requests converted into GitHub work</h2></div>
              <span className="policy">personal + group + channel chats</span>
            </div>
            <div className="metrics analytics-metrics teams-inline-metrics">
              <Metric label={`Teams requests · ${analyticsRangeShortLabel}`} value={teamsAnalytics.requests} tone="blue" />
              <Metric label={`Issues created · ${analyticsRangeShortLabel}`} value={teamsAnalytics.issues} tone="green" />
              <Metric label="Conversion rate" value={`${teamsAnalytics.completionRate.toFixed(0)}%`} tone="green" />
              <Metric label="Requesters" value={teamsAnalytics.requesters} tone="blue" />
              <Metric label="Average time to issue" value={compactDurationSeconds(teamsAnalytics.averageTimeToIssueSeconds)} tone="amber" />
              <Metric label="Active now" value={teamsAnalytics.active} tone="amber" />
            </div>
            <div className="teams-intake-chart">
              {teamsAnalytics.requests === 0
                ? <div className="chart-empty">No Teams requests recorded in this range</div>
                : <ResponsiveContainer width="100%" height={230}>
                  <BarChart data={teamsAnalytics.rows} margin={{ top: 8, right: 16, left: -25, bottom: 0 }}>
                    <CartesianGrid stroke="#e4e9e4" vertical={false} />
                    <XAxis dataKey="label" minTickGap={18} tick={{ fontSize: 9, fill: "#758078" }} axisLine={false} tickLine={false} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 9, fill: "#758078" }} axisLine={false} tickLine={false} />
                    <Tooltip contentStyle={{ border: "1px solid #d7ded8", borderRadius: 8, fontSize: 11 }} />
                    <Bar dataKey="requests" name="Requests" fill="#769eac" radius={[3, 3, 0, 0]} />
                    <Bar dataKey="issues" name="Issues created" fill="#4f9f6a" radius={[3, 3, 0, 0]} />
                    <Bar dataKey="cancelled" name="Cancelled" fill="#d6a75f" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>}
              <div className="chart-legend analytics-chart-legend">
                <span><i style={{ background: "#769eac" }} />Requests</span>
                <span><i style={{ background: "#4f9f6a" }} />Issues created</span>
                <span><i style={{ background: "#d6a75f" }} />Cancelled</span>
              </div>
            </div>
          </section>}
          {!historyPending && <section className="analytics-trend-grid">
            <article className="activity-card chart-card">
              <div className="chart-heading">
                <div><p className="eyebrow">{analyticsRangeLabel} DELIVERY FLOW</p><h2>PRs created vs merged</h2></div>
                <span>{analyticsSummary.created} created · {analyticsSummary.merged} merged</span>
              </div>
              <ResponsiveContainer width="100%" height={230}>
                <BarChart data={prFlowData} margin={{ top: 8, right: 10, left: -25, bottom: 0 }}>
                  <CartesianGrid stroke="#e4e9e4" vertical={false} />
                  <XAxis dataKey="label" minTickGap={18} tick={{ fontSize: 9, fill: "#758078" }} axisLine={false} tickLine={false} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 9, fill: "#758078" }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ border: "1px solid #d7ded8", borderRadius: 8, fontSize: 11 }} />
                  <Bar dataKey="created" name="Created" fill="#769eac" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="merged" name="Merged" fill="#4f9f6a" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
              <div className="chart-legend analytics-chart-legend">
                <span><i style={{ background: "#769eac" }} />Created</span>
                <span><i style={{ background: "#4f9f6a" }} />Merged</span>
              </div>
            </article>
            <article className="activity-card chart-card">
              <div className="chart-heading">
                <div><p className="eyebrow">{analyticsRangeLabel} REVIEW SPEED</p><h2>Review duration</h2></div>
                <span>average and p90 · minutes</span>
              </div>
              <ResponsiveContainer width="100%" height={230}>
                <AreaChart data={reviewDurationData} margin={{ top: 8, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid stroke="#e4e9e4" vertical={false} />
                  <XAxis dataKey="label" minTickGap={18} tick={{ fontSize: 9, fill: "#758078" }} axisLine={false} tickLine={false} />
                  <YAxis unit="m" tick={{ fontSize: 9, fill: "#758078" }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ border: "1px solid #d7ded8", borderRadius: 8, fontSize: 11 }} />
                  <Area type="monotone" dataKey="p90Minutes" name="P90" stroke="#d6a75f" fill="#d6a75f" fillOpacity={0.12} strokeWidth={1.5} />
                  <Area type="monotone" dataKey="averageMinutes" name="Average" stroke="#347b50" fill="#4f9f6a" fillOpacity={0.22} strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
              <div className="chart-legend analytics-chart-legend">
                <span><i style={{ background: "#347b50" }} />Average</span>
                <span><i style={{ background: "#d6a75f" }} />P90</span>
              </div>
            </article>
            <article className="activity-card chart-card">
              <div className="chart-heading">
                <div><p className="eyebrow">{analyticsRangeLabel} REVIEW IMPACT</p><h2>Findings per reviewed PR</h2></div>
                <span>daily average</span>
              </div>
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={reviewImpactData} margin={{ top: 8, right: 10, left: -25, bottom: 0 }}>
                  <CartesianGrid stroke="#e4e9e4" vertical={false} />
                  <XAxis dataKey="label" minTickGap={18} tick={{ fontSize: 9, fill: "#758078" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 9, fill: "#758078" }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ border: "1px solid #d7ded8", borderRadius: 8, fontSize: 11 }} />
                  <Area type="monotone" dataKey="findingsPerPr" name="Findings per PR" stroke="#c57b4b" fill="#d6a75f" fillOpacity={0.25} strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </article>
            <article className="activity-card chart-card">
              <div className="chart-heading">
                <div><p className="eyebrow">{analyticsRangeLabel} REVIEW IMPACT</p><h2>Resolutions per reviewed PR</h2></div>
                <span>daily average</span>
              </div>
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={reviewImpactData} margin={{ top: 8, right: 10, left: -25, bottom: 0 }}>
                  <CartesianGrid stroke="#e4e9e4" vertical={false} />
                  <XAxis dataKey="label" minTickGap={18} tick={{ fontSize: 9, fill: "#758078" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 9, fill: "#758078" }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ border: "1px solid #d7ded8", borderRadius: 8, fontSize: 11 }} />
                  <Area type="monotone" dataKey="resolutionsPerPr" name="Resolutions per PR" stroke="#5b82a1" fill="#769eac" fillOpacity={0.25} strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </article>
            <article className="activity-card chart-card">
              <div className="chart-heading">
                <div><p className="eyebrow">{analyticsRangeLabel} DELIVERY SPEED</p><h2>Time to merge</h2></div>
                <span>average and p90 · hours</span>
              </div>
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={mergeLeadTimeData} margin={{ top: 8, right: 10, left: -18, bottom: 0 }}>
                  <CartesianGrid stroke="#e4e9e4" vertical={false} />
                  <XAxis dataKey="label" minTickGap={18} tick={{ fontSize: 9, fill: "#758078" }} axisLine={false} tickLine={false} />
                  <YAxis unit="h" tick={{ fontSize: 9, fill: "#758078" }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ border: "1px solid #d7ded8", borderRadius: 8, fontSize: 11 }} />
                  <Area type="monotone" dataKey="p90Hours" name="P90" stroke="#769eac" fill="#769eac" fillOpacity={0.12} strokeWidth={1.5} />
                  <Area type="monotone" dataKey="averageHours" name="Average" stroke="#347b50" fill="#4f9f6a" fillOpacity={0.22} strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
              <div className="chart-legend analytics-chart-legend">
                <span><i style={{ background: "#347b50" }} />Average</span>
                <span><i style={{ background: "#769eac" }} />P90</span>
              </div>
            </article>
            <article className="activity-card chart-card">
              <div className="chart-heading">
                <div><p className="eyebrow">{analyticsRangeLabel} REVIEW DEPTH</p><h2>Review cycles per PR</h2></div>
                <span>reviewed PR distribution</span>
              </div>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={reviewCycleData} margin={{ top: 8, right: 10, left: -25, bottom: 0 }}>
                  <CartesianGrid stroke="#e4e9e4" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 9, fill: "#758078" }} axisLine={false} tickLine={false} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 9, fill: "#758078" }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ border: "1px solid #d7ded8", borderRadius: 8, fontSize: 11 }} />
                  <Bar dataKey="pulls" name="Pull requests" fill="#769eac" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </article>
            <article className="activity-card chart-card">
              <div className="chart-heading">
                <div><p className="eyebrow">{analyticsRangeLabel} PRIORITY MIX</p><h2>Findings by priority</h2></div>
                <span>daily stacked findings</span>
              </div>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={priorityTrendData} margin={{ top: 8, right: 10, left: -25, bottom: 0 }}>
                  <CartesianGrid stroke="#e4e9e4" vertical={false} />
                  <XAxis dataKey="label" minTickGap={18} tick={{ fontSize: 9, fill: "#758078" }} axisLine={false} tickLine={false} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 9, fill: "#758078" }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ border: "1px solid #d7ded8", borderRadius: 8, fontSize: 11 }} />
                  <Bar dataKey="P0" stackId="priority" fill="#963c35" />
                  <Bar dataKey="P1" stackId="priority" fill="#c45b4f" />
                  <Bar dataKey="P2" stackId="priority" fill="#d68e57" />
                  <Bar dataKey="P3" stackId="priority" fill="#d6b15f" />
                  <Bar dataKey="P4" stackId="priority" fill="#8eab89" />
                  <Bar dataKey="P5" stackId="priority" fill="#9aa7a0" />
                </BarChart>
              </ResponsiveContainer>
              <div className="chart-legend analytics-chart-legend">
                {[
                  ["P0", "#963c35"], ["P1", "#c45b4f"], ["P2", "#d68e57"],
                  ["P3", "#d6b15f"], ["P4", "#8eab89"], ["P5", "#9aa7a0"],
                ].map(([priority, color]) => <span key={priority}><i style={{ background: color }} />{priority}</span>)}
              </div>
            </article>
            <article className="activity-card chart-card">
              <div className="chart-heading">
                <div><p className="eyebrow">{analyticsRangeLabel} PROVIDER OUTCOMES</p><h2>Provider attempt success</h2></div>
                <span>daily stacked attempts</span>
              </div>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={providerAttemptData} margin={{ top: 8, right: 10, left: -25, bottom: 0 }}>
                  <CartesianGrid stroke="#e4e9e4" vertical={false} />
                  <XAxis dataKey="label" minTickGap={18} tick={{ fontSize: 9, fill: "#758078" }} axisLine={false} tickLine={false} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 9, fill: "#758078" }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ border: "1px solid #d7ded8", borderRadius: 8, fontSize: 11 }} />
                  <Bar dataKey="succeeded" name="Succeeded" stackId="outcome" fill="#4f9f6a" />
                  <Bar dataKey="failed" name="Failed" stackId="outcome" fill="#c45b4f" />
                </BarChart>
              </ResponsiveContainer>
              <div className="chart-legend analytics-chart-legend">
                <span><i style={{ background: "#4f9f6a" }} />Succeeded</span>
                <span><i style={{ background: "#c45b4f" }} />Failed</span>
              </div>
            </article>
            <article className="activity-card chart-card analytics-chart-wide">
              <div className="chart-heading">
                <div><p className="eyebrow">THREAD HEALTH</p><h2>Resolved in range vs currently unresolved</h2></div>
                <span>top repositories by thread activity</span>
              </div>
              {threadHealthData.length === 0
                ? <div className="chart-empty">No thread activity recorded</div>
                : <ResponsiveContainer width="100%" height={Math.max(220, threadHealthData.length * 38)}>
                  <BarChart layout="vertical" data={threadHealthData} margin={{ top: 8, right: 20, left: 25, bottom: 0 }}>
                    <CartesianGrid stroke="#e4e9e4" horizontal={false} />
                    <XAxis type="number" allowDecimals={false} tick={{ fontSize: 9, fill: "#758078" }} axisLine={false} tickLine={false} />
                    <YAxis type="category" dataKey="label" width={115} tick={{ fontSize: 9, fill: "#58645c" }} axisLine={false} tickLine={false} />
                    <Tooltip contentStyle={{ border: "1px solid #d7ded8", borderRadius: 8, fontSize: 11 }} />
                    <Bar dataKey="resolved" name="Resolved in range" fill="#4f9f6a" radius={[0, 3, 3, 0]} />
                    <Bar dataKey="unresolved" name="Currently unresolved" fill="#c45b4f" radius={[0, 3, 3, 0]} />
                  </BarChart>
                </ResponsiveContainer>}
              <div className="chart-legend analytics-chart-legend">
                <span><i style={{ background: "#4f9f6a" }} />Resolved in range</span>
                <span><i style={{ background: "#c45b4f" }} />Currently unresolved</span>
              </div>
            </article>
          </section>}
        </>}

        {view === "quality" && <div className="quality-subtabs" aria-label="Test quality views">
          <button className={qualityTab === "portfolio" ? "active" : ""} onClick={() => { setQualityTab("portfolio"); pushPath("/insights/quality/portfolio"); }}>Portfolio {historyPending ? <SkeletonCount /> : <span>{portfolio.reportingRepositories}</span>}</button>
          <button className={qualityTab === "repositories" ? "active" : ""} onClick={() => { setQualityTab("repositories"); pushPath(selectedTestRepository ? qualityRepositoryPath(selectedTestRepository) : "/insights/quality/repositories"); }}>Repositories</button>
          <button className={qualityTab === "ownership" ? "active" : ""} onClick={() => { setQualityTab("ownership"); pushPath("/insights/quality/ownership"); }}>Ownership {historyPending ? <SkeletonCount /> : <span>{testOwnerData.length}</span>}</button>
        </div>}

        {view === "quality" && (historyPending
          ? <>
            <MetricsSkeleton
              label="Loading test quality metrics"
              count={5}
              className="quality-metrics"
              variant="eyebrow"
            />
            <section className="analytics-grid skeleton-grid">
              <ChartCardSkeleton
                label="Loading coverage by release"
                eyebrow="RELEASE TREND"
                title="Coverage by release"
                legend={2}
              />
              <ChartCardSkeleton
                label="Loading tests by release"
                eyebrow="RELEASE TREND"
                title="Tests by release"
                legend={2}
              />
            </section>
            <SkeletonStack label="Loading repository test quality" rows={4} lines={1} className="skeleton-panel" />
          </>
          : testQuality.releases.length === 0
          ? <section className="quality-roadmap">
            <div className="activity-card detail-section">
              <p className="eyebrow">NO REPORTS YET</p>
              <h2>Release test intelligence</h2>
              <p>
                Netra never runs your test suites. It reads the JUnit XML and coverage
                report your CI already produces for a released commit, then attributes
                each test's owner from that repository's Git history.
              </p>
              <div className="quality-contract">
                <div>
                  <strong>From GitHub Actions</strong>
                  <span>
                    Upload your reports as a workflow artifact whose name contains{" "}
                    <code>{testQuality.artifact_match ?? "test-results"}</code>. Netra reads
                    every matching artifact for each release tag.
                  </span>
                </div>
                <div>
                  <strong>From any other CI</strong>
                  <span>
                    Post to <code>/api/v1/test-quality/reports</code> with the release tag
                    and commit SHA. <code>bin/netra-report-tests</code> does this for you.
                  </span>
                </div>
                <div>
                  <strong>Test counts</strong>
                  <span>JUnit XML — passing, failing, and total per release, per repository.</span>
                </div>
                <div>
                  <strong>Coverage</strong>
                  <span>Cobertura XML or LCOV — line and branch coverage, per file and per release.</span>
                </div>
              </div>
              <div className="quality-actions">
                <button
                  className="primary-action"
                  onClick={() => void refreshTestQuality()}
                  disabled={testRefreshing}
                >
                  {testRefreshing ? "Looking for reports…" : "Look for release reports now"}
                </button>
              </div>
            </div>
          </section>
          : <>
            {qualityTab === "portfolio" && <>
              <section className="metrics quality-metrics">
                <article className="metric">
                  <p className="eyebrow">UNIT TESTS</p>
                  <strong>{portfolio.total.toLocaleString()}</strong>
                  <span>across {counted(portfolio.reportingRepositories, "repository", "repositories")}</span>
                  <div className="metric-line" />
                </article>
                <article className={`metric ${portfolio.lineRate >= 80 ? "" : "amber"}`}>
                  <p className="eyebrow">LINE COVERAGE</p>
                  <strong>{portfolio.coverageRepositories === 0 ? "—" : `${portfolio.lineRate}%`}</strong>
                  <span>
                    {portfolio.coverageRepositories === 0
                      ? "no coverage reports yet"
                      : `${portfolio.uncoveredLines.toLocaleString()} lines uncovered`}
                  </span>
                  <div className="metric-line" />
                </article>
                <article className={`metric ${portfolio.passRate >= 99 ? "" : "amber"}`}>
                  <p className="eyebrow">PASS RATE</p>
                  <strong>{portfolio.passRate}%</strong>
                  <span>{portfolio.passed.toLocaleString()} passing</span>
                  <div className="metric-line" />
                </article>
                <article className={`metric ${portfolio.failing > 0 ? "red" : ""}`}>
                  <p className="eyebrow">FAILING</p>
                  <strong>{portfolio.failing.toLocaleString()}</strong>
                  <span>{portfolio.skipped.toLocaleString()} skipped</span>
                  <div className="metric-line" />
                </article>
                <article className={`metric ${portfolio.ownedPercent >= 90 ? "" : "amber"}`}>
                  <p className="eyebrow">TESTS OWNED</p>
                  <strong>{portfolio.ownedPercent}%</strong>
                  <span>
                    {counted(portfolio.contributors, "owner")}
                    {portfolio.unattributed > 0 && ` · ${portfolio.unattributed} pending`}
                  </span>
                  <div className="metric-line" />
                </article>
              </section>

              {portfolio.repositoriesWithoutCoverage > 0 && (
                <div className="quality-note">
                  {counted(portfolio.repositoriesWithoutCoverage, "repository", "repositories")} report
                  tests but no coverage, so the coverage figures above describe only the
                  {" "}{counted(portfolio.coverageRepositories, "repository", "repositories")} that do.
                </div>
              )}

              {repositoriesAwaitingReports.length > 0 && (
                <div className="awaiting-reports">
                  <div>
                    <p className="eyebrow">AWAITING FIRST REPORT</p>
                    <strong>{counted(repositoriesAwaitingReports.length, "selected repository")}</strong>
                    <span>Selected in Netra and visible in the repository view, but not included in portfolio metrics yet.</span>
                  </div>
                  <div className="awaiting-report-list">
                    {repositoriesAwaitingReports.map((option) => (
                      <button
                        key={option.repository}
                        onClick={() => {
                          setTestRepository(option.repository);
                          setTestDetail(null);
                          setQualityTab("repositories");
                          pushPath(qualityRepositoryPath(option.repository));
                        }}
                      >
                        {option.repository.split("/").at(-1)} <span>no report</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="quality-toolbar">
                <div>
                  <p className="eyebrow">CROSS-REPOSITORY</p>
                  <h2 className="quality-heading">Every reporting repository</h2>
                </div>
                <button
                  className="secondary-action"
                  onClick={() => void refreshTestQuality()}
                  disabled={testRefreshing}
                >
                  {testRefreshing ? "Refreshing…" : "Refresh reports"}
                </button>
              </div>

              <section className="analytics-grid">
                <article className="activity-card chart-card">
                  <div className="chart-heading">
                    <div><p className="eyebrow">COVERAGE</p><h2>Line coverage by repository</h2></div>
                    <span>newest report · worst first</span>
                  </div>
                  {coverageRepositories.length === 0
                    ? <div className="chart-empty">No coverage reports ingested yet</div>
                    : <ResponsiveContainer width="100%" height={Math.max(220, coverageRepositories.length * 34)}>
                      <BarChart layout="vertical" data={coverageRepositories} margin={{ top: 8, right: 30, left: 25, bottom: 0 }}>
                        <CartesianGrid stroke="#e4e9e4" horizontal={false} />
                        <XAxis type="number" domain={[0, 100]} unit="%" tick={{ fontSize: 9, fill: "#758078" }} axisLine={false} tickLine={false} />
                        <YAxis type="category" dataKey="label" width={125} tick={{ fontSize: 9, fill: "#58645c" }} axisLine={false} tickLine={false} />
                        <Tooltip contentStyle={{ border: "1px solid #d7ded8", borderRadius: 8, fontSize: 11 }} />
                        <Bar dataKey="lineRate" name="Line coverage %" fill={TEST_COVERAGE_COLOR} radius={[0, 3, 3, 0]} />
                      </BarChart>
                    </ResponsiveContainer>}
                </article>

                <article className="activity-card chart-card">
                  <div className="chart-heading">
                    <div><p className="eyebrow">SUITE SIZE</p><h2>Unit tests by repository</h2></div>
                    <span>newest reported release</span>
                  </div>
                  <ResponsiveContainer width="100%" height={Math.max(220, testRepositories.length * 34)}>
                    <BarChart layout="vertical" data={testRepositories} margin={{ top: 8, right: 30, left: 25, bottom: 0 }}>
                      <CartesianGrid stroke="#e4e9e4" horizontal={false} />
                      <XAxis type="number" allowDecimals={false} tick={{ fontSize: 9, fill: "#758078" }} axisLine={false} tickLine={false} />
                      <YAxis type="category" dataKey="label" width={125} tick={{ fontSize: 9, fill: "#58645c" }} axisLine={false} tickLine={false} />
                      <Tooltip contentStyle={{ border: "1px solid #d7ded8", borderRadius: 8, fontSize: 11 }} />
                      <Bar dataKey="total" name="Total tests" fill={TEST_TOTAL_COLOR} radius={[0, 3, 3, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </article>

                <article className="activity-card chart-card">
                  <div className="chart-heading">
                    <div><p className="eyebrow">PORTFOLIO OVER TIME</p><h2>Total unit tests</h2></div>
                    <span>all repositories combined</span>
                  </div>
                  {portfolioData.length < 2
                    ? <div className="chart-empty">A trend needs at least two release dates</div>
                    : <ResponsiveContainer width="100%" height={220}>
                      <LineChart data={portfolioData} margin={{ top: 12, right: 16, left: -20, bottom: 0 }}>
                        <CartesianGrid stroke="#e4e9e4" vertical={false} />
                        <XAxis dataKey="label" minTickGap={18} tick={{ fontSize: 9, fill: "#758078" }} axisLine={false} tickLine={false} />
                        <YAxis domain={["auto", "auto"]} allowDecimals={false} tick={{ fontSize: 9, fill: "#758078" }} axisLine={false} tickLine={false} />
                        <Tooltip contentStyle={{ border: "1px solid #d7ded8", borderRadius: 8, fontSize: 11 }} />
                        <Line type="monotone" dataKey="total" name="Total tests" stroke={TEST_TOTAL_COLOR} strokeWidth={2} dot={{ r: 4, fill: TEST_TOTAL_COLOR, stroke: "#f9faf7", strokeWidth: 2 }} activeDot={{ r: 5 }} />
                      </LineChart>
                    </ResponsiveContainer>}
                </article>

                <article className="activity-card chart-card">
                  <div className="chart-heading">
                    <div><p className="eyebrow">PORTFOLIO OVER TIME</p><h2>Line coverage</h2></div>
                    <span>weighted by measured lines</span>
                  </div>
                  {portfolioData.filter((row) => row.linesValid > 0).length < 2
                    ? <div className="chart-empty">A trend needs at least two coverage reports</div>
                    : <ResponsiveContainer width="100%" height={220}>
                      <LineChart data={portfolioData.filter((row) => row.linesValid > 0)} margin={{ top: 12, right: 16, left: -20, bottom: 0 }}>
                        <CartesianGrid stroke="#e4e9e4" vertical={false} />
                        <XAxis dataKey="label" minTickGap={18} tick={{ fontSize: 9, fill: "#758078" }} axisLine={false} tickLine={false} />
                        <YAxis domain={["auto", "auto"]} unit="%" tick={{ fontSize: 9, fill: "#758078" }} axisLine={false} tickLine={false} />
                        <Tooltip contentStyle={{ border: "1px solid #d7ded8", borderRadius: 8, fontSize: 11 }} />
                        <Line type="monotone" dataKey="lineRate" name="Line coverage %" stroke={TEST_COVERAGE_COLOR} strokeWidth={2} dot={{ r: 4, fill: TEST_COVERAGE_COLOR, stroke: "#f9faf7", strokeWidth: 2 }} activeDot={{ r: 5 }} />
                      </LineChart>
                    </ResponsiveContainer>}
                </article>
              </section>

              <section className="activity-card detail-section quality-table-card">
                <div className="section-heading">
                  <div>
                    <p className="eyebrow">REPOSITORY SCORECARD</p>
                    <h2>Tests, coverage, and ownership side by side</h2>
                    <p className="section-description">
                      Each repository on its newest reported release. Changes compare against
                      that repository's previous report, not against other repositories.
                    </p>
                  </div>
                </div>
                <div className="table-wrap"><table className="quality-table">
                  <thead>
                    <tr>
                      <th>Repository</th>
                      <th>Release</th>
                      <th className="numeric">Tests</th>
                      <th className="numeric">Change</th>
                      <th className="numeric">Failing</th>
                      <th className="numeric">Pass rate</th>
                      <th className="numeric">Coverage</th>
                      <th className="numeric">Change</th>
                      <th className="numeric">Uncovered</th>
                      <th className="numeric">Owned</th>
                    </tr>
                  </thead>
                  <tbody>
                    {testRepositories.map((row) => {
                      const coverage = coverageRepositories.find((item) => item.repository === row.repository);
                      const attribution = testQuality.repositories.find((item) => item.repository === row.repository);
                      const owned = attribution && attribution.tests > 0
                        ? Math.round((attribution.owned / attribution.tests) * 100)
                        : null;
                      return (
                        <tr
                          key={row.repository}
                          className="clickable-row"
                          onClick={() => {
                            setTestRepository(row.repository);
                            setTestDetail(null);
                            setQualityTab("repositories");
                            pushPath(qualityRepositoryPath(row.repository));
                          }}
                        >
                          <td><strong>{row.label}</strong><span>{row.repository}</span></td>
                          <td>
                            {row.releaseTag}
                            {/* A release whose pipelines have not all reported
                                is not comparable to the last one. Saying so
                                beats showing a drop that is not real. */}
                            {(row.gathering || row.missingShards > 0) && (
                              <span
                                className="shard-warning"
                                title={row.missingShards > 0
                                  ? `${row.missingShards} of ${row.shards + row.missingShards} reporting pipelines have not published for this release yet`
                                  : "still collecting reports for this release"}
                              >
                                partial
                              </span>
                            )}
                          </td>
                          <td className="numeric">{row.total.toLocaleString()}</td>
                          <td className={`numeric ${(row.netChange ?? 0) > 0 ? "value-good" : (row.netChange ?? 0) < 0 && row.missingShards === 0 ? "value-bad" : ""}`}>
                            {row.netChange === null ? "—" : row.netChange > 0 ? `+${row.netChange}` : row.netChange}
                          </td>
                          <td className={`numeric ${row.failing > 0 ? "value-bad" : ""}`}>{row.failing}</td>
                          <td className="numeric">{row.passRate}%</td>
                          <td className={`numeric ${coverage && coverage.lineRate < 60 ? "value-bad" : ""}`}>
                            {coverage ? `${coverage.lineRate}%` : <span className="subtle-cell">none</span>}
                          </td>
                          <td className={`numeric ${(coverage?.rateChange ?? 0) > 0 ? "value-good" : (coverage?.rateChange ?? 0) < 0 ? "value-bad" : ""}`}>
                            {coverage?.rateChange == null ? "—" : coverage.rateChange > 0 ? `+${coverage.rateChange}` : coverage.rateChange}
                          </td>
                          <td className="numeric">{coverage ? coverage.uncoveredLines.toLocaleString() : "—"}</td>
                          <td className="numeric">{owned === null ? "—" : `${owned}%`}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table></div>
              </section>

              {testQuality.failing.length > 0 && (
                <section className="activity-card detail-section quality-table-card">
                  <div className="section-heading">
                    <div>
                      <p className="eyebrow">NEEDS ATTENTION</p>
                      <h2>Failing tests across every repository</h2>
                      <p className="section-description">From each repository's newest reported release.</p>
                    </div>
                  </div>
                  <div className="table-wrap"><table className="quality-table">
                    <thead>
                      <tr>
                        <th>Test</th>
                        <th>Repository</th>
                        <th>Release</th>
                        <th>Owner</th>
                        <th>Last edited by</th>
                      </tr>
                    </thead>
                    <tbody>
                      {testQuality.failing.slice(0, 50).map((test) => (
                        <tr key={`${test.repository}-${test.suite}-${test.name}`}>
                          <td>
                            <strong>{test.name}</strong>
                            <span className="subtle-cell">{test.file ?? test.suite}</span>
                          </td>
                          <td>{test.repository.split("/").at(-1)}</td>
                          <td>{test.release_tag}</td>
                          <td>{test.owner ?? <span className="subtle-cell">unattributed</span>}</td>
                          <td>{test.last_edited_by ?? "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table></div>
                </section>
              )}
            </>}

            {qualityTab === "repositories" && <>
              <div className="quality-toolbar">
                <div className="quality-repository-filter">
                  <label htmlFor="test-repository">Repository</label>
                  <select
                    id="test-repository"
                    value={selectedTestRepository}
                    onChange={(event) => {
                      setTestRepository(event.target.value);
                      setTestDetail(null);
                      pushPath(qualityRepositoryPath(event.target.value));
                    }}
                  >
                    {testRepositoryOptions.map((option) => (
                      <option key={option.repository} value={option.repository}>
                        {option.repository} · {option.report ? counted(option.report.releases, "release") : "no reports yet"}
                      </option>
                    ))}
                  </select>
                </div>
                <button
                  className="secondary-action"
                  onClick={() => void refreshTestQuality()}
                  disabled={testRefreshing}
                >
                  {testRefreshing ? "Refreshing…" : "Refresh reports"}
                </button>
              </div>

              {selectedTestRepository && testReleaseData.length === 0 && (
                <div className="quality-note awaiting-report-note">
                  <strong>{selectedTestRepository}</strong> is selected in Netra and ready for reporting.
                  It will enter portfolio metrics after CI publishes its first matching JUnit or coverage artifact.
                </div>
              )}

              <section className="analytics-grid">
                <article className="activity-card chart-card analytics-chart-wide">
                  <div className="chart-heading">
                    <div><p className="eyebrow">SUITE GROWTH</p><h2>Unit tests per release</h2></div>
                    <span>{selectedTestRepository || "no repository selected"}</span>
                  </div>
                  {testReleaseData.length === 0
                    ? <div className="chart-empty">No test reports for this repository</div>
                    : <ResponsiveContainer width="100%" height={240}>
                      {/* A line with a fitted domain, not an area from zero: the
                          question here is the trend between releases, and a
                          zero-baselined area flattens it into a straight edge. */}
                      <LineChart data={testReleaseData} margin={{ top: 12, right: 16, left: -20, bottom: 0 }}>
                        <CartesianGrid stroke="#e4e9e4" vertical={false} />
                        <XAxis dataKey="label" minTickGap={12} tick={{ fontSize: 9, fill: "#758078" }} axisLine={false} tickLine={false} />
                        <YAxis domain={["auto", "auto"]} allowDecimals={false} tick={{ fontSize: 9, fill: "#758078" }} axisLine={false} tickLine={false} />
                        <Tooltip contentStyle={{ border: "1px solid #d7ded8", borderRadius: 8, fontSize: 11 }} />
                        <Line type="monotone" dataKey="total" name="Total tests" stroke={TEST_TOTAL_COLOR} strokeWidth={2} dot={{ r: 4, fill: TEST_TOTAL_COLOR, stroke: "#f9faf7", strokeWidth: 2 }} activeDot={{ r: 5 }} />
                      </LineChart>
                    </ResponsiveContainer>}
                </article>

                <article className="activity-card chart-card">
                  <div className="chart-heading">
                    <div><p className="eyebrow">COVERAGE TREND</p><h2>Line coverage per release</h2></div>
                    <span>{coverageReleaseData.at(-1)?.format ?? "no coverage"}</span>
                  </div>
                  {coverageReleaseData.length === 0
                    ? <div className="chart-empty">No coverage reports for this repository</div>
                    : <ResponsiveContainer width="100%" height={220}>
                      <LineChart data={coverageReleaseData} margin={{ top: 12, right: 16, left: -20, bottom: 0 }}>
                        <CartesianGrid stroke="#e4e9e4" vertical={false} />
                        <XAxis dataKey="label" minTickGap={12} tick={{ fontSize: 9, fill: "#758078" }} axisLine={false} tickLine={false} />
                        <YAxis domain={["auto", "auto"]} unit="%" tick={{ fontSize: 9, fill: "#758078" }} axisLine={false} tickLine={false} />
                        <Tooltip contentStyle={{ border: "1px solid #d7ded8", borderRadius: 8, fontSize: 11 }} />
                        <Line type="monotone" dataKey="lineRate" name="Line coverage %" stroke={TEST_COVERAGE_COLOR} strokeWidth={2} dot={{ r: 4, fill: TEST_COVERAGE_COLOR, stroke: "#f9faf7", strokeWidth: 2 }} activeDot={{ r: 5 }} />
                      </LineChart>
                    </ResponsiveContainer>}
                </article>

                <article className="activity-card chart-card">
                  <div className="chart-heading">
                    <div><p className="eyebrow">RELEASE OUTCOMES</p><h2>Not passing per release</h2></div>
                    <span>failing and skipped</span>
                  </div>
                  {testReleaseData.length === 0
                    ? <div className="chart-empty">No test reports for this repository</div>
                    : <ResponsiveContainer width="100%" height={220}>
                      <BarChart data={testReleaseData} margin={{ top: 8, right: 10, left: -25, bottom: 0 }}>
                        <CartesianGrid stroke="#e4e9e4" vertical={false} />
                        <XAxis dataKey="label" minTickGap={12} tick={{ fontSize: 9, fill: "#758078" }} axisLine={false} tickLine={false} />
                        <YAxis allowDecimals={false} tick={{ fontSize: 9, fill: "#758078" }} axisLine={false} tickLine={false} />
                        <Tooltip contentStyle={{ border: "1px solid #d7ded8", borderRadius: 8, fontSize: 11 }} />
                        {/* The surface-coloured stroke reads as a 2px gap between
                            stacked segments so the boundary never relies on hue. */}
                        <Bar dataKey="failing" name="Failing" stackId="outcome" fill={TEST_FAILING_COLOR} stroke="#f9faf7" strokeWidth={2} />
                        <Bar dataKey="skipped" name="Skipped" stackId="outcome" fill={TEST_SKIPPED_COLOR} stroke="#f9faf7" strokeWidth={2} />
                      </BarChart>
                    </ResponsiveContainer>}
                  <div className="chart-legend analytics-chart-legend">
                    <span><i style={{ background: TEST_FAILING_COLOR }} />Failing</span>
                    <span><i style={{ background: TEST_SKIPPED_COLOR }} />Skipped</span>
                  </div>
                </article>
              </section>

              <section className="activity-card detail-section quality-table-card">
                <div className="section-heading">
                  <div>
                    <p className="eyebrow">RELEASE HISTORY</p>
                    <h2>{selectedTestRepository}</h2>
                    <p className="section-description">
                      Every release with an ingested report, newest first. Counts come from the
                      report published for that release's exact commit.
                    </p>
                  </div>
                </div>
                <div className="table-wrap"><table className="quality-table">
                  <thead>
                    <tr>
                      <th>Release</th>
                      <th>Released</th>
                      <th className="numeric">Total</th>
                      <th className="numeric">Passing</th>
                      <th className="numeric">Failing</th>
                      <th className="numeric">Skipped</th>
                      <th className="numeric">Pass rate</th>
                      <th className="numeric">Change</th>
                      <th className="numeric">Coverage</th>
                      <th>Source</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...testReleaseData].reverse().map((row) => {
                      const coverage = coverageReleaseData.find((item) => item.label === row.label);
                      return (
                        <tr key={`${row.repository}-${row.label}`}>
                          <td>
                            <strong>{row.label}</strong>
                            {row.releaseKind !== "release" && <span className="state">{row.releaseKind}</span>}
                          </td>
                          <td>{new Date(row.releasedAt).toLocaleDateString()}</td>
                          <td className="numeric">{row.total.toLocaleString()}</td>
                          <td className="numeric">{row.passed.toLocaleString()}</td>
                          <td className={`numeric ${row.failing > 0 ? "value-bad" : ""}`}>{row.failing}</td>
                          <td className="numeric">{row.skipped}</td>
                          <td className="numeric">{row.passRate}%</td>
                          <td className={`numeric ${row.netChange > 0 ? "value-good" : row.netChange < 0 ? "value-bad" : ""}`}>
                            {row.netChange > 0 ? `+${row.netChange}` : row.netChange}
                          </td>
                          <td className="numeric">
                            {coverage ? `${coverage.lineRate}%` : <span className="subtle-cell">none</span>}
                          </td>
                          <td>{row.source === "github_actions" ? "Actions artifact" : "Upload"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table></div>
              </section>

              {testDetail && (testDetail.tests.length > 0 || testDetail.coverage_files.length > 0) && (
                <section className="repository-quality-dashboard">
                  <div className="repository-quality-heading">
                    <div>
                      <p className="eyebrow">LATEST RELEASE HEALTH</p>
                      <h2>{testDetail.repository}</h2>
                      <p>Suite outcomes, ownership, and coverage risk aggregated into one view.</p>
                    </div>
                    {testDetail.commit_sha && <code>{testDetail.commit_sha.slice(0, 8)}</code>}
                  </div>

                  <div className="suite-overview-grid">
                    <article>
                      <span>Pass rate</span>
                      <strong>{repositoryTestStatus.total > 0 ? `${Math.round((repositoryTestStatus.passed / repositoryTestStatus.total) * 1000) / 10}%` : "—"}</strong>
                      <small>{repositoryTestStatus.passed.toLocaleString()} of {repositoryTestStatus.total.toLocaleString()} tests</small>
                    </article>
                    <article className={repositoryTestStatus.failing > 0 ? "risk" : "healthy"}>
                      <span>Needs attention</span>
                      <strong>{repositoryTestStatus.failing.toLocaleString()}</strong>
                      <small>{repositoryTestStatus.skipped.toLocaleString()} skipped</small>
                    </article>
                    <article>
                      <span>Ownership</span>
                      <strong>{repositoryTestStatus.total > 0 ? `${Math.round((repositoryTestStatus.attributed / repositoryTestStatus.total) * 100)}%` : "—"}</strong>
                      <small>{repositoryTestStatus.attributed.toLocaleString()} attributed tests</small>
                    </article>
                    <article className={(repositoryCoverageSummary.lineRate ?? 100) < 60 ? "risk" : ""}>
                      <span>Line coverage</span>
                      <strong>{repositoryCoverageSummary.lineRate == null ? "—" : `${repositoryCoverageSummary.lineRate}%`}</strong>
                      <small>{repositoryCoverageSummary.uncoveredLines.toLocaleString()} uncovered lines</small>
                    </article>
                  </div>

                  <div className="quality-dashboard-grid">
                    {testDetail.tests.length > 0 && (
                      <article className="activity-card quality-dashboard-panel">
                        <div className="quality-panel-heading">
                          <div><p className="eyebrow">SUITE OUTCOMES</p><h3>{counted(repositoryTestStatus.total, "test")}</h3></div>
                          <span>{repositoryTestStatus.failing === 0 ? "All clear" : `${repositoryTestStatus.failing} failing`}</span>
                        </div>
                        <div className="outcome-meter" aria-label={`${repositoryTestStatus.passed} passed, ${repositoryTestStatus.failing} failing, ${repositoryTestStatus.skipped} skipped`}>
                          <i className="passed" style={{ width: `${(repositoryTestStatus.passed / repositoryTestStatus.total) * 100}%` }} />
                          <i className="failing" style={{ width: `${(repositoryTestStatus.failing / repositoryTestStatus.total) * 100}%` }} />
                          <i className="skipped" style={{ width: `${(repositoryTestStatus.skipped / repositoryTestStatus.total) * 100}%` }} />
                        </div>
                        <div className="outcome-legend">
                          <span><i className="passed" />Passed <strong>{repositoryTestStatus.passed}</strong></span>
                          <span><i className="failing" />Failing <strong>{repositoryTestStatus.failing}</strong></span>
                          <span><i className="skipped" />Skipped <strong>{repositoryTestStatus.skipped}</strong></span>
                        </div>
                      </article>
                    )}

                    {repositoryTestOwners.length > 0 && (
                      <article className="activity-card quality-dashboard-panel">
                        <div className="quality-panel-heading">
                          <div><p className="eyebrow">STEWARDSHIP</p><h3>Ownership concentration</h3></div>
                          <span>{counted(repositoryTestOwners.length, "owner")}</span>
                        </div>
                        <div className="owner-summary-list">
                          {repositoryTestOwners.slice(0, 6).map((owner) => (
                            <div key={owner.name}>
                              <div className="owner-summary-label">
                                <strong>{owner.name}</strong>
                                <span>
                                  {owner.tests} tests
                                  {owner.totalLines > 0 && ` · ${owner.ownerLines.toLocaleString()}/${owner.totalLines.toLocaleString()} lines`}
                                  {` · ${owner.created} created · ${owner.lastEdited} last edited`}
                                </span>
                              </div>
                              <div className="summary-bar"><i style={{ width: `${(owner.tests / repositoryTestStatus.total) * 100}%` }} /></div>
                              <b>{Math.round((owner.tests / repositoryTestStatus.total) * 100)}%</b>
                            </div>
                          ))}
                        </div>
                      </article>
                    )}
                  </div>

                  {repositoryCoverageAreas.length > 0 && (
                    <article className="activity-card quality-dashboard-panel coverage-areas-panel">
                      <div className="quality-panel-heading">
                        <div><p className="eyebrow">COVERAGE RISK BY AREA</p><h3>Where untested code is concentrated</h3></div>
                        <span>
                          {/* The real directory total, not the row count of a
                              capped list -- reading a truncated slice back as a
                              total is the exact error this panel was rebuilt to
                              remove. */}
                          {counted(repositoryCoverageSummary.directories, "directory")}
                          {` · ${counted(repositoryCoverageSummary.files, "measured file")}`}
                        </span>
                      </div>
                      <div className="coverage-area-list">
                        {repositoryCoverageAreas.slice(0, 25).map((area) => (
                          <div key={area.display_path}>
                            <div className="coverage-area-name">
                              {/* The full directory path, not a top-level
                                  bucket: this list exists to be assigned. */}
                              <strong title={area.display_path}>{area.display_path}</strong>
                              <span>{counted(area.files, "file")}</span>
                            </div>
                            <div className="coverage-area-meter"><i style={{ width: `${Math.max(0, Math.min(100, area.line_rate))}%` }} /></div>
                            <strong className={area.line_rate < 60 ? "value-bad" : ""}>{Math.round(area.line_rate * 10) / 10}%</strong>
                            <span>{area.uncovered_lines.toLocaleString()} uncovered</span>
                            <span>{area.branch_rate == null ? "no branches" : `${Math.round(area.branch_rate * 10) / 10}% branches`}</span>
                          </div>
                        ))}
                      </div>
                      {repositoryCoverageSummary.directories > Math.min(25, repositoryCoverageAreas.length) && (
                        <p className="coverage-area-note">
                          Showing the{" "}
                          {Math.min(25, repositoryCoverageAreas.length).toLocaleString()} directories
                          with the most uncovered lines, of{" "}
                          {repositoryCoverageSummary.directories.toLocaleString()} measured.
                          Repository totals above count every measured file.
                        </p>
                      )}
                    </article>
                  )}

                  {repositoryTestStatus.failures.length > 0 && (
                    <article className="activity-card quality-dashboard-panel failure-panel">
                      <div className="quality-panel-heading">
                        <div><p className="eyebrow">ACTION REQUIRED</p><h3>Failing tests</h3></div>
                        <span>{counted(repositoryTestStatus.failures.length, "failure")}</span>
                      </div>
                      <div className="failure-summary-list">
                        {repositoryTestStatus.failures.slice(0, 8).map((test) => (
                          <div key={test.test_key}>
                            <span className={`state ${testStateClass(test.status)}`}>{test.status}</span>
                            <p><strong>{test.name}</strong><span>{test.file ?? test.suite}</span></p>
                            <span>{test.owner ?? "Unattributed"}</span>
                          </div>
                        ))}
                      </div>
                    </article>
                  )}
                </section>
              )}
            </>}

            {qualityTab === "ownership" && <>
              <section className="analytics-grid">
                <article className="activity-card chart-card analytics-chart-wide">
                  <div className="chart-heading">
                    <div><p className="eyebrow">OWNERSHIP</p><h2>Tests owned per person</h2></div>
                    <span>top contributor by authored lines · all repositories</span>
                  </div>
                  {testOwnerData.length === 0
                    ? <div className="chart-empty">Ownership attribution has not completed yet</div>
                    : <ResponsiveContainer width="100%" height={Math.max(220, testOwnerData.length * 30)}>
                      <BarChart layout="vertical" data={testOwnerData} margin={{ top: 8, right: 30, left: 25, bottom: 0 }}>
                        <CartesianGrid stroke="#e4e9e4" horizontal={false} />
                        <XAxis type="number" allowDecimals={false} tick={{ fontSize: 9, fill: "#758078" }} axisLine={false} tickLine={false} />
                        <YAxis type="category" dataKey="label" width={150} tick={{ fontSize: 9, fill: "#58645c" }} axisLine={false} tickLine={false} />
                        <Tooltip contentStyle={{ border: "1px solid #d7ded8", borderRadius: 8, fontSize: 11 }} />
                        <Bar dataKey="testsOwned" name="Tests owned" fill={TEST_OWNER_COLOR} radius={[0, 3, 3, 0]} />
                      </BarChart>
                    </ResponsiveContainer>}
                </article>
              </section>

              <section className="activity-card detail-section quality-table-card">
                <div className="section-heading">
                  <div>
                    <p className="eyebrow">OWNERSHIP DETAIL</p>
                    <h2>Who owns the tests</h2>
                    <p className="section-description">
                      Owning a test means authoring most of the lines that are in it today.
                      Creating and last editing are counted separately, because they are
                      usually different people.
                    </p>
                  </div>
                </div>
                {testOwnerData.length === 0
                  ? <p className="empty">
                    No ownership attributed yet. Netra attributes a bounded batch of tests each
                    cycle from the managed clone's history.
                  </p>
                  : <div className="table-wrap"><table className="quality-table">
                    <thead>
                      <tr>
                        <th>Person</th>
                        <th className="numeric">Owns</th>
                        <th className="numeric">Created</th>
                        <th className="numeric">Last edited</th>
                        <th className="numeric">Failing owned</th>
                        <th className="numeric">Repositories</th>
                      </tr>
                    </thead>
                    <tbody>
                      {testOwnerData.map((row) => (
                        <tr key={row.owner}>
                          <td><strong>{row.owner}</strong></td>
                          <td className="numeric">{row.testsOwned}</td>
                          <td className="numeric">{row.testsCreated}</td>
                          <td className="numeric">{row.testsLastEdited}</td>
                          <td className={`numeric ${row.failingOwned > 0 ? "value-bad" : ""}`}>{row.failingOwned}</td>
                          <td className="numeric">{row.repositories}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table></div>}
              </section>

              <section className="activity-card detail-section quality-table-card">
                <div className="section-heading">
                  <div>
                    <p className="eyebrow">ATTRIBUTION COVERAGE</p>
                    <h2>How much of each suite has a known owner</h2>
                    <p className="section-description">
                      A test with no owner is either not attributed yet or could not be
                      located in its source file. Those are different problems.
                    </p>
                  </div>
                </div>
                <div className="table-wrap"><table className="quality-table">
                  <thead>
                    <tr>
                      <th>Repository</th>
                      <th>Release</th>
                      <th className="numeric">Tests</th>
                      <th className="numeric">Owned</th>
                      <th className="numeric">Pending</th>
                      <th className="numeric">No source file</th>
                      <th className="numeric">Not locatable</th>
                    </tr>
                  </thead>
                  <tbody>
                    {testQuality.repositories.map((row) => (
                      <tr key={row.repository}>
                        <td><strong>{row.repository.split("/").at(-1)}</strong><span>{row.repository}</span></td>
                        <td>{row.release_tag}</td>
                        <td className="numeric">{row.tests.toLocaleString()}</td>
                        <td className="numeric">
                          {row.tests > 0 ? `${Math.round((row.owned / row.tests) * 100)}%` : "—"}
                        </td>
                        <td className={`numeric ${row.unattributed > 0 ? "value-bad" : ""}`}>{row.unattributed}</td>
                        <td className="numeric">{row.without_file}</td>
                        <td className={`numeric ${row.attribution_errors > 0 ? "value-bad" : ""}`}>{row.attribution_errors}</td>
                      </tr>
                    ))}
                  </tbody>
                </table></div>
              </section>
            </>}
          </>)}

        {view === "settings" && settingsTab === "repositories" && (
          <section className="activity-card repositories-card">
            <div className="section-heading">
              <div>
                <p className="eyebrow">EXECUTION ALLOWLIST</p>
                <h2>Selected repositories</h2>
                <p className="section-description">
                  {loading ? <SkeletonBar width={420} height={11} /> : <>
                    {repositories.length} selected {repositories.length === 1 ? "repository" : "repositories"} can ask Netra to review code.
                    Reviews, preview builds, and staff requests remain explicit per-repository choices.
                  </>}
                </p>
              </div>
              <button
                className={repositoryPickerOpen ? "secondary-action" : "primary-action"}
                onClick={() => setRepositoryPickerOpen((open) => !open)}
                aria-expanded={repositoryPickerOpen}
              >
                {repositoryPickerOpen ? "Close" : "+ Add repositories"}
              </button>
            </div>
            {repositoryPickerOpen && <div className="github-repository-picker">
              <div className="repository-picker-heading">
                <div>
                  <p className="eyebrow">GITHUB INSTALLATION</p>
                  <h3>Add repositories</h3>
                  <p>
                    Select from repositories available to the GitHub App. Reviews start enabled;
                    previews stay off until you enable them below.
                  </p>
                </div>
                <button
                  className="secondary-action"
                  disabled={inventorySyncing}
                  onClick={() => void syncGithubInventory()}
                >
                  {inventorySyncing ? "Syncing…" : "Sync from GitHub"}
                </button>
              </div>
              <input
                value={repositorySearch}
                onChange={(event) => setRepositorySearch(event.target.value)}
                placeholder="Search accessible repositories"
                aria-label="Search GitHub repositories"
                autoFocus
              />
              <div className="github-repository-results">
                {githubRepositories
                  .filter((repository) => !repository.selected && !repository.archived)
                  .filter((repository) => repository.full_name.toLowerCase().includes(repositorySearch.trim().toLowerCase()))
                  .slice(0, 40)
                  .map((repository) => <div key={repository.full_name}>
                    <div>
                      <strong>{repository.full_name}</strong>
                      <span>{repository.private ? "Private" : "Public"} · Default branch {repository.default_branch}</span>
                    </div>
                    <button onClick={() => void setRepositoryPolicy(repository.full_name, {
                      reviews_enabled: true,
                      previews_enabled: false,
                      review_level: "standard",
                    })}>Add</button>
                  </div>)}
                {githubRepositories
                  .filter((repository) => !repository.selected && !repository.archived)
                  .filter((repository) => repository.full_name.toLowerCase().includes(repositorySearch.trim().toLowerCase()))
                  .length === 0 && <p className="repository-picker-empty">
                    No unselected repositories match this search.
                  </p>}
              </div>
              <details className="manual-repository-add">
                <summary>Add by owner/repository instead</summary>
                <form className="repo-add" onSubmit={(event) => {
                  event.preventDefault();
                  void setRepositoryPolicy(repositoryInput, {
                    reviews_enabled: true,
                    previews_enabled: false,
                    review_level: "standard",
                  });
                }}>
                  <input
                    value={repositoryInput}
                    onChange={(event) => setRepositoryInput(event.target.value)}
                    placeholder="owner/repository"
                    aria-label="GitHub repository"
                  />
                  <button type="submit">Add repository</button>
                </form>
              </details>
            </div>}
            <div className="repo-list">
              {repositories.length === 0 ? (
                <div className="repository-empty-state">
                  <strong>No repositories selected</strong>
                  <p>Add a repository to enable review automation. Preview execution remains off by default.</p>
                  <button className="primary-action" onClick={() => setRepositoryPickerOpen(true)}>
                    + Add your first repository
                  </button>
                </div>
              ) : repositories.map((repository) => {
                const policySaving = repositoryPolicySaving.has(repository.repository);
                const staffRequestsSaving = portalRepositorySaving.has(repository.repository);
                const rowSaving = policySaving || staffRequestsSaving;
                const portalRepository = portalRepositories.find((candidate) => candidate.repository === repository.repository);
                return (
                <div className={`repository-row${rowSaving ? " saving" : ""}`} aria-busy={rowSaving} key={repository.repository}>
                  <div className="repository-row-main">
                    <div className="repository-identity">
                      {(() => {
                        const workload = repositoryWorkload.get(repository.repository);
                        const [owner, ...nameParts] = repository.repository.split("/");
                        const name = nameParts.join("/") || repository.repository;
                        const details = [
                          counted(workload?.threads ?? 0, "open thread"),
                          counted(workload?.runs ?? 0, "run"),
                          counted(workload?.findings ?? 0, "finding"),
                          counted(repository.revision_count, "preview revision"),
                        ].join(" · ");
                        return <>
                          <div className="repository-name-line" title={repository.repository}>
                            <strong>{name}</strong>
                            {nameParts.length > 0 && <span>{owner}</span>}
                          </div>
                          <span className="repository-summary" title={details}>
                            {counted(workload?.pulls ?? 0, "open PR")} · {counted(workload?.issues ?? 0, "issue")}
                            {" · "}{counted(repository.active_count, "active preview")}
                            <i aria-label={details} tabIndex={0}>ⓘ</i>
                          </span>
                        </>;
                      })()}
                    </div>
                    <div className="repository-policy-controls">
                      <div className="repository-switches">
                        <label className="toggle-control" title={repository.reviews_enabled ? "Automatic reviews enabled" : "Reviews disabled"}>
                          <span className="toggle-copy"><strong>Reviews</strong></span>
                          <input
                            type="checkbox"
                            checked={repository.reviews_enabled}
                            disabled={rowSaving}
                            onChange={(event) =>
                              void setRepositoryPolicy(repository.repository, {
                                reviews_enabled: event.target.checked,
                                ...(!event.target.checked ? { previews_enabled: false } : {}),
                              })
                            }
                          />
                          <i aria-hidden="true" />
                        </label>
                        <label
                          className={`toggle-control ${!repository.reviews_enabled ? "disabled" : ""}`}
                          title={repository.previews_enabled ? "On-demand preview deployments enabled" : "Preview deployments disabled"}
                        >
                          <span className="toggle-copy"><strong>Previews</strong></span>
                          <input
                            type="checkbox"
                            checked={repository.previews_enabled}
                            disabled={!repository.reviews_enabled || rowSaving}
                            onChange={(event) =>
                              void setRepositoryPolicy(repository.repository, { previews_enabled: event.target.checked })
                            }
                          />
                          <i aria-hidden="true" />
                        </label>
                        <label
                          className="toggle-control"
                          title={portalRepository?.enabled ? "Staff requests enabled" : "Staff requests disabled"}
                        >
                          <span className="toggle-copy"><strong>Staff requests</strong></span>
                          <input
                            type="checkbox"
                            checked={portalRepository?.enabled ?? false}
                            disabled={rowSaving}
                            onChange={(event) => void setStaffRequestsEnabled(repository.repository, event.target.checked)}
                          />
                          <i aria-hidden="true" />
                        </label>
                      </div>
                      <div className="repository-row-actions">
                        <select
                          disabled={rowSaving}
                          className="review-level-select"
                          title="Auto uses the pull request diff: Fast up to 200 lines, Standard up to 800, Deep above that. Deep, Standard, and Fast always use that reviewer pool."
                          aria-label={`${repository.repository} review depth`}
                          value={repository.review_level}
                          onChange={(event) =>
                            void setRepositoryPolicy(repository.repository, { review_level: event.target.value as RepositorySetting["review_level"] })
                          }
                        >
                          <option value="auto">Auto</option>
                          <option value="deep">Deep</option>
                          <option value="standard">Standard</option>
                          <option value="fast">Fast</option>
                        </select>
                        <div className="repository-utility-actions">
                          <button
                            className={`environment-button ${expandedRepository === repository.repository ? "active" : ""}`}
                            aria-label={`${expandedRepository === repository.repository ? "Close" : "Open"} deployment settings for ${repository.repository}`}
                            aria-expanded={expandedRepository === repository.repository}
                            title={expandedRepository === repository.repository ? "Close deployment settings" : "Deployment settings"}
                            onClick={() => void openPreviewConfig(repository.repository)}
                          >⚙</button>
                          <button
                            className="deselect-button"
                            aria-label={`Remove ${repository.repository}`}
                            title="Remove repository"
                            onClick={() => void deselectRepository(repository.repository)}
                          >×</button>
                        </div>
                      </div>
                    </div>
                  </div>
                  {expandedRepository === repository.repository && previewConfigs[repository.repository] && (
                    <>
                      {repositoryPolicyConfigs[repository.repository] && <RepositoryPolicyEditor
                        repository={repository.repository}
                        policy={repositoryPolicyConfigs[repository.repository]}
                        providers={runtimeSettings?.providers ?? []}
                        onChange={(policy) => setRepositoryPolicyConfigs((current) => ({ ...current, [repository.repository]: policy }))}
                        onSave={(policy) => void saveRepositoryPolicy(repository.repository, policy)}
                      />}
                      <PreviewEnvironmentEditor
                        repository={repository.repository}
                        config={previewConfigs[repository.repository]}
                        onChange={(config) => setPreviewConfigs((current) => ({
                          ...current,
                          [repository.repository]: config,
                        }))}
                        onImport={(contents) => importPreviewEnv(repository.repository, contents)}
                        onSave={(config) => void savePreviewConfig(repository.repository, config)}
                      />
                    </>
                  )}
                </div>
                );
              })}
            </div>
          </section>
        )}

        {view === "settings" && settingsTab === "memories" && (
          <MemorySettings
            onPolicyChanged={(restartRequired, message) => {
              if (restartRequired) setRestartPending(true);
              setNotice(message);
            }}
          />
        )}

        {view === "settings" && settingsTab === "team" && <PortalApp embedded actor={currentUser} />}

        {view === "settings" && settingsTab === "general" && !runtimeSettings && loading && <section className="settings-grid">
          <SkeletonStack label="Loading runtime policy" rows={4} lines={1} className="skeleton-panel" />
          <SkeletonStack label="Loading provider policy" rows={4} lines={1} className="skeleton-panel" />
        </section>}

        {view === "settings" && settingsTab === "general" && runtimeSettings && <section className="settings-grid">
          <div className="activity-card detail-section">
            <p className="eyebrow">AUTOMATION</p>
            <h2>Runtime policy</h2>
            <div className="typed-settings">
              <label>
                <span>Scheduler</span>
                <select value={runtimeSettings.paused ? "paused" : "running"} onChange={(event) =>
                  void updateSettings({ paused: event.target.value === "paused" })
                }>
                  <option value="running">Running</option>
                  <option value="paused">Paused</option>
                </select>
              </label>
              <label>
                <span>Automatic mode</span>
                <select value={runtimeSettings.mode} onChange={(event) =>
                  void updateSettings({ mode: event.target.value as RuntimeSettings["mode"] })
                }>
                  <option value="observe">Observe only</option>
                  <option value="review">Review</option>
                  <option value="fix">Fix</option>
                </select>
              </label>
              <label>
                <span>Preferred reviewer</span>
                <select value={normalizeReviewerPin(runtimeSettings.provider)} onChange={(event) =>
                  void updateSettings({ provider: event.target.value as RuntimeSettings["provider"] })
                }>
                  <option value="auto">Automatic</option>
                  {providerPinOptions(resolveReviewerSettings(runtimeSettings).natives).map((option) => (
                    <option value={option.value} key={option.value}>{option.label}</option>
                  ))}
                </select>
              </label>
              <label className="toggle-control inversion-toggle" title={runtimeSettings.model_inversion === false ? "Adversarial inversion disabled" : "Adversarial inversion enabled"}>
                <span>Adversarial model inversion</span>
                <input
                  type="checkbox"
                  checked={runtimeSettings.model_inversion !== false}
                  onChange={(event) => void updateSettings({ model_inversion: event.target.checked })}
                />
                <i />
              </label>
            </div>
            <button className="danger-button" onClick={() => void abortAllRuns()}>Abort all active runs</button>
          </div>
          <div className="activity-card detail-section">
            <p className="eyebrow">PROVIDERS</p>
            <h2>Reviewer pool</h2>
            <p className="settings-note">Native CLIs apply to every review depth. Then pick models for Deep, Standard, and Fast. Type a model id into a depth list to add one the built-in catalogs do not have yet, such as claude-haiku-5-5. The numbered order is the review priority: first is tried first, skipping only exhausted or cooling-down models. A repository uses the list that matches its review depth. Auto picks that depth from the pull request: Fast up to 200 changed lines, Standard up to 800, and Deep above that.</p>
            <div className="reviewer-pool-stack">
              <label>
                <span>Native</span>
                <ReviewerPoolDropdown
                  catalog={NATIVE_REVIEWER_CATALOG.filter((entry) =>
                    runtimeSettings.providers.some((provider) => reviewerNative(provider) === entry.id))}
                  active={resolveReviewerSettings(runtimeSettings).natives}
                  searchable={false}
                  emptyLabel="Select natives"
                  onChange={(active_natives) => void updateSettings({
                    active_natives,
                    active_providers_deep: resolveReviewerSettings(runtimeSettings).models.deep,
                    active_providers_standard: resolveReviewerSettings(runtimeSettings).models.standard,
                    active_providers_fast: resolveReviewerSettings(runtimeSettings).models.fast,
                  })}
                />
              </label>
              {REVIEW_LEVELS.map((level) => {
                const pool = resolveReviewerSettings(runtimeSettings);
                const key = `active_providers_${level}` as const;
                const labels: Record<ReviewLevel, string> = { deep: "Deep", standard: "Standard", fast: "Fast" };
                return <label key={level}>
                  <span>{labels[level]}</span>
                  <ReviewerPoolDropdown
                    catalog={runtimeSettings.reviewer_catalog?.length
                      ? runtimeSettings.reviewer_catalog
                      : reviewerCatalogFallback(runtimeSettings.providers)}
                    active={pool.models[level]}
                    addNatives={NATIVE_REVIEWER_CATALOG.map((entry) => entry.id).filter((native) =>
                      runtimeSettings.providers.some((provider) => reviewerNative(provider) === native))}
                    allowEmpty={pool.natives.some((native) => native !== "cursor")}
                    emptyLabel="Native defaults"
                    onForget={(id) => {
                      const strip = (models: string[]) => models.filter((model) => model !== id);
                      void updateSettings({
                        forget_reviewer_models: [id],
                        active_natives: pool.natives,
                        active_providers_deep: strip(pool.models.deep),
                        active_providers_standard: strip(pool.models.standard),
                        active_providers_fast: strip(pool.models.fast),
                      });
                    }}
                    onChange={(models) => {
                      const natives = new Set(pool.natives);
                      for (const model of models) {
                        const native = reviewerNative(model);
                        if (native) natives.add(native);
                      }
                      void updateSettings({
                        active_natives: NATIVE_REVIEWER_CATALOG.map((entry) => entry.id).filter((native) => natives.has(native)),
                        [key]: models,
                      });
                    }}
                  />
                </label>;
              })}
            </div>
          </div>
          {Array.from(new Set((runtimeSettings.schema ?? []).map((setting) => setting.section))).map((section) => (
            <div className="activity-card detail-section typed-settings-card" key={section}>
              <p className="eyebrow">TYPED CONFIGURATION</p>
              <h2>{section}</h2>
              <div className="typed-settings advanced-settings">
                {(runtimeSettings.schema ?? []).filter((setting) => setting.section === section).map((setting) => (
                  <label key={setting.key}>
                    <span>
                      <strong>{setting.label}</strong>
                      <small>{setting.description}</small>
                      <em>{setting.source}{setting.restart_required ? " · restart required" : ""}</em>
                    </span>
                    {setting.kind === "boolean"
                      ? <input
                          type="checkbox"
                          checked={Boolean(setting.value)}
                          onChange={(event) => void updateTypedSetting(setting, event.target.checked)}
                        />
                      : setting.kind === "choice"
                        ? <select
                            value={String(setting.value)}
                            onChange={(event) => void updateTypedSetting(setting, event.target.value)}
                          >
                            {setting.options.map((option) => <option key={option} value={option}>{option}</option>)}
                          </select>
                        : <input
                            type={setting.kind === "integer" ? "number" : "text"}
                            min={setting.min}
                            max={setting.max}
                            value={String(setting.value)}
                            onChange={(event) => {
                              const value = setting.kind === "integer" ? Number(event.target.value) : event.target.value;
                              if (setting.kind !== "integer" || Number.isInteger(value)) {
                                void updateTypedSetting(setting, value);
                              }
                            }}
                          />}
                  </label>
                ))}
              </div>
            </div>
          ))}
        </section>}

        {view === "settings" && settingsTab === "integrations" && <section className="settings-grid">
          <div className="activity-card detail-section github-setup-card">
            <div className="integration-title">
              <div><p className="eyebrow">GUIDED ONBOARDING</p><h2>Connect GitHub</h2></div>
              <button className="secondary-action" onClick={() => setGithubSetupOpen((open) => !open)}>
                {githubSetupOpen ? "Close setup" : githubStatus?.connected ? "Reconfigure" : "Start setup"}
              </button>
            </div>
            <p className="settings-note">Create a correctly scoped private GitHub App, or import an existing one. Private keys and webhook secrets are written to mode-0600 local files and never returned by the API.</p>
            {githubSetupOpen && <div className="github-setup-grid">
              <section>
                <strong>Create with GitHub Manifest</strong>
                <span>GitHub creates the App, private key, and webhook secret in one signed browser handshake.</span>
                <label><span>Organization</span><input value={githubSetupOrganization} onChange={(event) => setGithubSetupOrganization(event.target.value)} placeholder="Streamoid-Technologies" /></label>
                <label><span>App name</span><input value={githubSetupName} onChange={(event) => setGithubSetupName(event.target.value)} /></label>
                <button className="primary-action" disabled={githubSetupBusy} onClick={() => void startGithubManifest()}>{githubSetupBusy ? "Working…" : "Create GitHub App"}</button>
                {githubInstallUrl && <a className="primary-action" href={githubInstallUrl} target="_blank" rel="noreferrer">Install GitHub App ↗</a>}
              </section>
              <section>
                <strong>Import existing App</strong>
                <span>Use this after installing a Manifest-created App to record its installation ID, or import all fields for an existing App.</span>
                {(["app_id", "installation_id", "client_id", "slug"] as const).map((key) => <label key={key}><span>{key.replaceAll("_", " ")}</span><input value={githubImport[key]} onChange={(event) => setGithubImport((current) => ({ ...current, [key]: event.target.value }))} /></label>)}
                <label><span>Private key PEM</span><textarea value={githubImport.private_key} onChange={(event) => setGithubImport((current) => ({ ...current, private_key: event.target.value }))} placeholder="Optional when the Manifest flow already saved it" /></label>
                <label><span>Webhook secret</span><input type="password" value={githubImport.webhook_secret} onChange={(event) => setGithubImport((current) => ({ ...current, webhook_secret: event.target.value }))} /></label>
                <button className="primary-action" disabled={githubSetupBusy || !githubImport.app_id || !githubImport.installation_id} onClick={() => void importGithubApp()}>{githubSetupBusy ? "Saving…" : "Save and validate after restart"}</button>
              </section>
            </div>}
          </div>
          <div className="activity-card detail-section">
            <p className="eyebrow">GITHUB APP</p>
            <div className="integration-title">
              <div>
                <h2>{githubStatus?.app_name ?? (githubStatus?.connected ? "Connected" : "Needs attention")}</h2>
                {githubStatus?.app_owner && <p>Owned by {githubStatus.app_owner}</p>}
              </div>
              <span className={`connection-state ${githubStatus?.connected ? "connected" : ""}`}>
                {githubStatus?.connected ? "connected" : "attention"}
              </span>
            </div>
            <dl className="settings-facts">
              <dt>Authentication</dt><dd>{githubStatus?.authentication ?? "checking"}</dd>
              <dt>App slug</dt><dd>{githubStatus?.app_slug ?? "—"}</dd>
              <dt>Installation</dt><dd>{githubStatus?.installation_id ?? "not set"}</dd>
              <dt>Private key</dt><dd>{githubStatus?.private_key_configured ? "ready" : "missing"}</dd>
              <dt>Webhook secret</dt><dd>{githubStatus?.webhook_secret_configured ? "ready" : "missing"}</dd>
              <dt>Configured repos</dt><dd>{githubStatus?.configured_repositories ?? 0}</dd>
              <dt>Accessible repos</dt><dd>{githubStatus?.accessible_repositories ?? "—"}</dd>
            </dl>
            {githubStatus?.error && <pre className="failure">{githubStatus.error}</pre>}
            {githubStatus?.configuration_error && <pre className="failure">{githubStatus.configuration_error}</pre>}
            <p className="settings-note">Netra owns the local credentials, repository policy, and runtime health. GitHub owns the app name, logo, visibility, permissions, and event subscriptions.</p>
            <div className="integration-actions">
              {githubStatus?.app_settings_url && <a href={githubStatus.app_settings_url} target="_blank" rel="noreferrer">Manage app on GitHub ↗</a>}
              {githubStatus?.app_url && <a className="secondary" href={githubStatus.app_url} target="_blank" rel="noreferrer">View public app</a>}
            </div>
          </div>
          <div className="activity-card detail-section">
            <p className="eyebrow">GITHUB CONFIGURATION</p>
            <h2>Effective setup</h2>
            <dl className="settings-facts integration-config">
              <dt>Webhook URL</dt><dd>{githubStatus?.webhook_url ?? "not available"}</dd>
              <dt>Events</dt><dd>{githubStatus?.subscribed_events?.join(", ") || "not available"}</dd>
              <dt>Permissions</dt><dd>{Object.entries(githubStatus?.app_permissions ?? {}).map(([permission, level]) => `${permission}: ${level}`).join(", ") || "not available"}</dd>
            </dl>
            <p className="settings-note">Changing the app name or permission contract requires an app owner or GitHub App manager and is completed on GitHub. Permission changes may require the installation owner to approve the update.</p>
          </div>
          <div className="activity-card detail-section planned-integration">
            <div className="planned-heading"><p className="eyebrow">MICROSOFT TEAMS</p><span className={`connection-state ${teamsStatus?.configured ? "connected" : ""}`}>{teamsStatus?.configured ? "ready" : "bot setup needed"}</span></div>
            <h2>Conversational task intake</h2>
            <p>Mention <strong>@Netra</strong> or open its personal chat. The bot asks for each missing detail, lets the requester edit or cancel, and creates a GitHub Issue only after an explicit confirmation.</p>
            <div className="flow vertical-flow">
              <span>@Netra in Teams</span><i>→</i><span>GitHub Issue</span><i>→</i><span>Execution</span>
            </div>
            <dl className="settings-facts"><dt>Endpoint</dt><dd><code>{teamsStatus?.endpoint ?? "/api/v1/integrations/teams/bot/messages"}</code></dd><dt>Authentication</dt><dd>Microsoft Bot Framework JWT</dd><dt>App registration</dt><dd>{teamsStatus?.app_id_present ? "ready" : "missing"}</dd><dt>Tenant</dt><dd>{teamsStatus?.tenant_id_present ? "ready" : "missing"}</dd><dt>Client secret</dt><dd>{teamsStatus?.client_secret_present ? "ready" : "missing"}</dd><dt>Scopes</dt><dd>{teamsStatus?.scopes?.join(", ") || "personal, team, groupChat"}</dd><dt>Idempotency</dt><dd>Teams activity ID</dd></dl>
            <p className="settings-note">Bot Framework signatures are validated before any conversation state is read. Active conversation progress expires after 24 hours; request summaries and issue links remain in the local dashboard.</p>
            <div className="integration-actions"><a href="/work/teams">View Teams requests</a></div>
          </div>
        </section>}

        {view === "settings" && settingsTab === "runtime" && !resources && loading && <>
          <SkeletonStack label="Loading host utilization" rows={2} lines={2} className="skeleton-panel" />
          <SkeletonStack label="Loading preview container utilization" rows={3} lines={1} className="skeleton-panel" />
        </>}

        {view === "settings" && settingsTab === "runtime" && resources && <>
          <section className="resource-summary runtime-summary">
            <div><span>Host CPU</span><strong>{resources.host.cpu_percent.toFixed(1)}%</strong><small>{resources.host.cpu_count} cores · load {resources.host.load_average.one.toFixed(2)}</small></div>
            <div><span>Host memory</span><strong>{bytes(resources.host.memory_used_bytes)}</strong><small>of {bytes(resources.host.memory_total_bytes)}</small></div>
            <div><span>Disk available</span><strong>{bytes(resources.host.disk?.available_bytes)}</strong><small>of {bytes(resources.host.disk?.total_bytes)}</small></div>
            <div><span>Sample</span><strong>{relativeTime(resources.sampled_at)}</strong><small>{resources.host.name} · {resources.host.os}</small></div>
          </section>
          <section className="settings-grid runtime-grid">
            <div className="activity-card detail-section">
              <p className="eyebrow">NETRA PROCESSES</p>
              <h2>Service utilization</h2>
              <div className="resource-rows">
                {resources.processes.length === 0 ? <p>No Netra processes were visible in this sample.</p> : resources.processes.map((process) => <div key={process.pid}>
                  <div><strong>{process.name}</strong><span>PID {process.pid}</span></div>
                  <span>{process.cpu_percent.toFixed(1)}% CPU</span>
                  <span>{bytes(process.memory_bytes)}</span>
                </div>)}
              </div>
            </div>
          </section>
          <section className="activity-card preview-resources">
            <div className="section-heading">
              <div><p className="eyebrow">PR / PREVIEW RESOURCES</p><h2>Live container utilization</h2></div>
              <span className="policy">Docker · Netra-owned only</span>
            </div>
            <div className="table-wrap">
              <table>
                <thead><tr><th>Repository / PR</th><th>State</th><th>CPU</th><th>Memory</th><th>Network I/O</th><th>Block I/O</th><th>PIDs</th><th /></tr></thead>
                <tbody>{resources.previews.filter((preview) => preview.docker || !terminalStates.has(preview.status)).length === 0
                  ? <tr><td className="empty" colSpan={8}>No preview containers are currently running.</td></tr>
                  : resources.previews.filter((preview) => preview.docker || !terminalStates.has(preview.status)).map((preview) => <tr key={preview.preview_id}>
                    <td><strong>{preview.repository}</strong><span>PR #{preview.number} · <code>{shortSha(preview.head_sha)}</code></span></td>
                    <td><span className={`state ${preview.status}`}>{preview.status.replaceAll("_", " ")}</span></td>
                    <td>{preview.docker?.CPUPerc ?? "—"}</td>
                    <td><strong>{preview.docker?.MemPerc ?? "—"}</strong><span>{preview.docker?.MemUsage ?? (preview.container ? "sampling" : "not started")}</span></td>
                    <td>{preview.docker?.NetIO ?? "—"}</td>
                    <td>{preview.docker?.BlockIO ?? "—"}</td>
                    <td>{preview.docker?.PIDs ?? "—"}</td>
                    <td className="preview-resource-actions">
                      {!terminalStates.has(preview.status) && <button
                        disabled={preview.status === "undeploying" || undeployingIds.has(preview.preview_id)}
                        onClick={() => void undeploy({
                          id: preview.preview_id,
                          repository: preview.repository,
                          number: preview.number,
                        })}
                      >{preview.status === "undeploying" || undeployingIds.has(preview.preview_id) ? "Undeploying…" : "Undeploy"}</button>}
                    </td>
                  </tr>)}</tbody>
              </table>
            </div>
          </section>
        </>}

      </main>

      {selected && (
        <div className="drawer-backdrop" onClick={() => setSelected(null)}>
          <aside className="drawer" onClick={(event) => event.stopPropagation()}>
            <button className="drawer-close" onClick={() => setSelected(null)}>×</button>
            <p className="eyebrow">PREVIEW REVISION</p>
            <h2>{repoName(selected)} #{selected.number}</h2>
            <dl>
              <dt>State</dt><dd><span className={`state ${selected.status}`}>{selected.status}</span></dd>
              <dt>Revision</dt><dd><code>{selected.id}</code></dd>
              <dt>Reviewed SHA</dt><dd><code>{selected.head_sha}</code></dd>
              <dt>Active SHA</dt><dd><code>{selected.active_head_sha ?? "—"}</code></dd>
              <dt>Current SHA</dt><dd><code>{selected.current_head_sha ?? "—"}</code></dd>
              <dt>Expires</dt><dd>{selected.expires_at ? new Date(selected.expires_at).toLocaleString() : "Not active"}</dd>
              <dt>Build log</dt><dd><code>{selected.log_path ?? "Not available"}</code></dd>
              <dt>Backend</dt><dd>{previewDiagnostics?.backend ?? "Loading…"}</dd>
              {previewDiagnostics?.queue_position && <><dt>Queue</dt><dd>Position {previewDiagnostics.queue_position} · about {Math.ceil((previewDiagnostics.estimated_wait_seconds ?? 0) / 60)} min</dd></>}
            </dl>
            {previewDiagnostics && <section className={`route-proof ${previewDiagnostics.routing.exact_head ? "exact" : "stale"}`}>
              <strong>{previewDiagnostics.routing.exact_head ? "Exact head routed" : "Not currently routed"}</strong>
              <span>{previewDiagnostics.routing.explanation}</span>
            </section>}
            {previewDiagnostics?.events.length ? <section className="preview-timeline">
              <h3>Deployment timeline</h3>
              {previewDiagnostics.events.slice().reverse().map((event, index) => <div key={`${event.created_at}-${index}`}>
                <span />
                <strong>{event.kind.replaceAll(".", " ")}</strong>
                <time>{relativeTime(event.created_at)}</time>
              </div>)}
            </section> : null}
            {previewDiagnostics?.log_tail && <details className="preview-log" open={selected.status === "failed"}>
              <summary>Build log tail</summary>
              <pre>{previewDiagnostics.log_tail}</pre>
            </details>}
            {selected.error && <pre className="failure">{selected.error}</pre>}
            <div className="drawer-actions">
              {["active", "stale"].includes(selected.status) && (
                <a className="primary" href={selected.public_url} target="_blank">Open preview ↗</a>
              )}
              {!terminalStates.has(selected.status) && (
                <button
                  disabled={selected.status === "undeploying" || undeployingIds.has(selected.id)}
                  onClick={() => void undeploy({ id: selected.id, repository: repoName(selected), number: selected.number })}
                >{selected.status === "undeploying" || undeployingIds.has(selected.id) ? "Undeploying…" : "Undeploy"}</button>
              )}
              {canManuallyDeploy(selected) && (
                <button className="primary" onClick={() => void deploy(selected)}>Deploy exact SHA</button>
              )}
              {selected.status === "failed" && (
                <button onClick={() => void retry(selected)}>Retry build</button>
              )}
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}

function ReviewActionIcon() {
  return <svg viewBox="0 0 20 20" aria-hidden="true">
    <path d="M15.7 7A6.2 6.2 0 1 0 16 12" />
    <path d="M15.7 3.5V7h-3.5" />
    <path d="m8.5 7.5 4 2.5-4 2.5z" className="filled" />
  </svg>;
}

function FixActionIcon() {
  return <svg viewBox="0 0 20 20" aria-hidden="true">
    <path d="m4 16 9.4-9.4" />
    <path d="m11.8 4.2 4 4" />
    <path d="M4 3v3M2.5 4.5h3M15 13v4M13 15h4" />
  </svg>;
}

function Metric({ label, value, tone }: { label: string; value: number | string; tone: string }) {
  const displayValue = typeof value === "number" ? value.toString().padStart(2, "0") : value;
  return (
    <article className={`metric ${tone}`}>
      <span>{label}</span>
      <strong>{displayValue}</strong>
      <div className="metric-line" />
    </article>
  );
}

function HealthFact({ label, value, tone }: { label: string; value: string; tone: string }) {
  return <div className={`health-fact ${tone}`}><span>{label}</span><strong>{value}</strong></div>;
}

function SidebarMeter({
  label,
  value,
  detail,
  remaining = false,
}: {
  label: string;
  value: number;
  detail: string;
  remaining?: boolean;
}) {
  const bounded = Math.max(0, Math.min(100, value));
  return <div className="sidebar-meter">
    <div><strong>{label}</strong><span title={detail}>{detail}</span></div>
    <div className="sidebar-meter-track"><i className={remaining ? "remaining" : ""} style={{ width: `${bounded}%` }} /></div>
  </div>;
}

function RepositoryPolicyEditor({
  repository,
  policy,
  providers,
  onChange,
  onSave,
}: {
  repository: string;
  policy: RepositoryPolicy;
  providers: string[];
  onChange: (policy: RepositoryPolicy) => void;
  onSave: (policy: RepositoryPolicy) => void;
}) {
  return <section className="repository-policy-editor">
    <div className="environment-heading">
      <div><p className="eyebrow">POLICY PROFILE</p><h3>Review and preview gates for {repository}</h3></div>
      <span>Local override · higher precedence than <code>.netra/policy.toml</code></span>
    </div>
    <div className="repository-policy-grid">
      <label><span>Profile</span><select value={policy.profile ?? "balanced"} onChange={(event) => onChange({ ...policy, profile: event.target.value as RepositoryPolicy["profile"] })}><option value="balanced">Balanced</option><option value="strict">Strict</option><option value="fast">Fast</option><option value="custom">Custom</option></select></label>
      <label><span>Blocking priority</span><select value={policy.blocking_priority ?? "P1"} onChange={(event) => onChange({ ...policy, blocking_priority: event.target.value as "P0" | "P1" })}><option value="P1">P0 and P1</option><option value="P0">P0 only</option></select></label>
      <label><span>Provider strategy</span><select value={policy.provider_strategy ?? "adversarial"} onChange={(event) => onChange({ ...policy, provider_strategy: event.target.value as RepositoryPolicy["provider_strategy"] })}><option value="adversarial">Adversarial</option><option value="auto">Quota-aware automatic</option><option value="fixed">Fixed provider</option></select></label>
      <label><span>Fixed provider</span><select disabled={policy.provider_strategy !== "fixed"} value={policy.provider ?? providers[0] ?? ""} onChange={(event) => onChange({ ...policy, provider: event.target.value })}>{providers.map((provider) => <option value={provider} key={provider}>{provider}</option>)}</select></label>
      <label><span>Require exact-head tests</span><input type="checkbox" checked={policy.require_pr_tests ?? false} onChange={(event) => onChange({ ...policy, require_pr_tests: event.target.checked })} /></label>
      <label><span>Minimum changed-line coverage</span><input type="number" min={0} max={100} value={policy.min_changed_line_coverage ?? ""} placeholder="No gate" onChange={(event) => onChange({ ...policy, min_changed_line_coverage: event.target.value === "" ? undefined : Number(event.target.value) })} /></label>
      <label><span>Maximum preview CPU</span><input type="number" min={0.1} max={16} step={0.1} value={policy.max_preview_cpu ?? ""} onChange={(event) => onChange({ ...policy, max_preview_cpu: event.target.value === "" ? undefined : Number(event.target.value) })} /></label>
      <label><span>Maximum preview memory MiB</span><input type="number" min={128} max={65536} value={policy.max_preview_memory_mb ?? ""} onChange={(event) => onChange({ ...policy, max_preview_memory_mb: event.target.value === "" ? undefined : Number(event.target.value) })} /></label>
      <label className="policy-paths"><span>Protected paths</span><textarea value={(policy.protected_paths ?? []).join("\n")} onChange={(event) => onChange({ ...policy, protected_paths: event.target.value.split("\n").map((value) => value.trim()).filter(Boolean) })} placeholder={'migrations/**\ninfrastructure/**\nauth/*'} /><small>One repository-relative path or glob per line. Findings in these paths block the gate.</small></label>
    </div>
    <div className="environment-actions"><button onClick={() => onSave(policy)}>Save policy</button></div>
  </section>;
}

function PreviewEnvironmentEditor({
  repository,
  config,
  onChange,
  onImport,
  onSave,
}: {
  repository: string;
  config: PreviewEnvironmentConfig;
  onChange: (config: PreviewEnvironmentConfig) => void;
  onImport: (contents: string) => Promise<PreviewEnvironmentImport | null>;
  onSave: (config: PreviewEnvironmentConfig) => void;
}) {
  const [envContents, setEnvContents] = useState("");
  const [importing, setImporting] = useState(false);
  const [importSummary, setImportSummary] = useState("");
  const valueRows = Object.entries(config.values);
  const buildValueRows = Object.entries(config.build_values);
  const replaceValue = (index: number, key: string, value: string) => {
    const next = valueRows.map((row, rowIndex) => rowIndex === index ? [key, value] : row);
    onChange({ ...config, values: Object.fromEntries(next) });
  };
  const replaceBuildValue = (index: number, key: string, value: string) => {
    const next = buildValueRows.map((row, rowIndex) => rowIndex === index ? [key, value] : row);
    onChange({ ...config, build_values: Object.fromEntries(next) });
  };
  return <div className="preview-environment-editor">
    <div className="environment-heading">
      <div>
        <p className="eyebrow">PREVIEW DEPLOYMENT</p>
        <h3>Environment for {repository}</h3>
      </div>
      <span>Applies on the next preview build</span>
    </div>
    <p className="environment-explainer">
      Everything on this page is managed by the Netra server and applies to the next exact PR commit that
      Netra previews. Developers keep pushing code to GitHub normally—no Netra files or workflow changes are required.
    </p>
    <section className="deployment-scope-guide" aria-label="How Netra delivers deployment settings">
      <div className="deployment-scope-heading">
        <div><strong>What goes where</strong><span>Keep each value in the narrowest scope that needs it.</span></div>
        <span>Build → start container → run application</span>
      </div>
      <div className="deployment-scope-grid">
        <article>
          <div><span>BUILD</span><em>Non-secret</em></div>
          <strong>Build values</strong>
          <p>Public settings used while Docker creates the image, such as a frontend API URL.</p>
        </article>
        <article>
          <div><span>BUILD</span><em>Secret</em></div>
          <strong>BuildKit secrets</strong>
          <p>Credentials needed only during the build, such as a private package token. They are not stored in the image.</p>
        </article>
        <article>
          <div><span>RUNTIME</span><em>Non-secret</em></div>
          <strong>Runtime values</strong>
          <p>Normal application settings delivered through a temporary private env file when the container starts.</p>
        </article>
        <article>
          <div><span>RUNTIME</span><em>Secret</em></div>
          <strong>Runtime secrets</strong>
          <p>Passwords and tokens used by the running app. A read-only protected file is recommended.</p>
        </article>
      </div>
    </section>
    <section className="environment-paste manifest-editor">
      <div>
        <strong>Machine-local preview manifest</strong>
        <span>
          Tells Netra how to build and run this repository: a repository Dockerfile or Compose file, or
          centrally managed adapter files that exist only in Netra&apos;s staged copy. It also defines the
          build context, service port, health check, and resource limits. Use <code>generated_env_file =
          ".env.production.local"</code> only when a frontend needs the Build values above while compiling.
        </span>
      </div>
      <textarea
        aria-label={`Preview manifest for ${repository}`}
        value={config.manifest ?? ""}
        onChange={(event) => onChange({ ...config, manifest: event.target.value })}
        placeholder={'version = 1\n\n[build]\ndriver = "dockerfile"\ncontext = "."\ndockerfile = "Dockerfile"\ngenerated_env_file = ".env.production.local"\n\n[ingress]\nservice = "app"\nport = 3000\n\n[health]\npath = "/health"'}
        spellCheck={false}
      />
    </section>
    <section className="environment-paste">
      <div>
        <strong>Paste an env file</strong>
        <span>
          Quickly imports <code>NAME=value</code> lines. Normal entries become Runtime values.
          <code>VITE_*</code> and <code>NEXT_PUBLIC_*</code> entries become both Build and Runtime values because frontend bundlers need them while compiling.
          Passwords and tokens are moved to the server's secret store and imported with legacy Environment delivery;
          switch them to Protected file below when the application supports reading a secret from a file path.
        </span>
      </div>
      <textarea
        aria-label={`Env file for ${repository}`}
        value={envContents}
        onChange={(event) => {
          setEnvContents(event.target.value);
          setImportSummary("");
        }}
        placeholder={"APP_ENV=preview\nVITE_UNIFIED_API_BASE=https://unified.test.example\nAPI_TOKEN=secret-value"}
        spellCheck={false}
      />
      <div className="environment-paste-actions">
        <span>
          Secret values are never saved in Netra's database or returned to this page. Netra stores only their
          private-file reference.
        </span>
        <button
          disabled={importing || envContents.trim().length === 0}
          onClick={async () => {
            setImporting(true);
            const imported = await onImport(envContents);
            setImporting(false);
            if (!imported) return;
            setEnvContents("");
            setImportSummary(
              `${imported.imported_values} values · ${imported.imported_secrets} secrets · ` +
              `${imported.imported_build_values} build values`,
            );
          }}
        >
          {importing ? "Importing…" : "Import env"}
        </button>
      </div>
      {importSummary && <p className="environment-import-summary">Imported {importSummary}</p>}
    </section>
    <div className="environment-columns">
      <section>
        <div className="environment-section-heading">
          <div><strong>Build values</strong><span>Public settings available only while the image is built. Never put passwords or tokens here.</span></div>
          <button onClick={() => onChange({
            ...config,
            build_values: { ...config.build_values, [`BUILD_VALUE_${buildValueRows.length + 1}`]: "" },
          })}>+ Add build value</button>
        </div>
        <div className="environment-rows">
          {buildValueRows.length === 0 && <p>No local build overrides.</p>}
          {buildValueRows.map(([key, value], index) => <div key={`${key}-${index}`}>
            <input aria-label="Build argument name" value={key} onChange={(event) => replaceBuildValue(index, event.target.value.toUpperCase(), value)} />
            <input aria-label={`${key} build value`} value={value} onChange={(event) => replaceBuildValue(index, key, event.target.value)} />
            <button aria-label={`Remove ${key}`} onClick={() => onChange({
              ...config,
              build_values: Object.fromEntries(buildValueRows.filter((_, rowIndex) => rowIndex !== index)),
            })}>×</button>
          </div>)}
        </div>
      </section>
      <section>
        <div className="environment-section-heading">
          <div><strong>Runtime values</strong><span>Non-secret settings available after the container starts through a temporary private env file.</span></div>
          <button onClick={() => onChange({
            ...config,
            values: { ...config.values, [`VARIABLE_${valueRows.length + 1}`]: "" },
          })}>+ Add value</button>
        </div>
        <div className="environment-rows">
          {valueRows.length === 0 && <p>No local overrides.</p>}
          {valueRows.map(([key, value], index) => <div key={`${key}-${index}`}>
            <input aria-label="Environment variable name" value={key} onChange={(event) => replaceValue(index, event.target.value.toUpperCase(), value)} />
            <input aria-label={`${key} value`} value={value} onChange={(event) => replaceValue(index, key, event.target.value)} />
            <button aria-label={`Remove ${key}`} onClick={() => onChange({
              ...config,
              values: Object.fromEntries(valueRows.filter((_, rowIndex) => rowIndex !== index)),
            })}>×</button>
          </div>)}
        </div>
      </section>
      <section className="runtime-secrets-section">
        <div className="environment-section-heading">
          <div><strong>Runtime secrets</strong><span>Sensitive values used by the running application. Netra stores only the local secret reference.</span></div>
          <button onClick={() => onChange({
            ...config,
            bindings: [...config.bindings, {
              environment_key: `SECRET_${config.bindings.length + 1}`,
              alias: `secret_${config.bindings.length + 1}`,
              source: "keychain:",
              delivery: "file",
            }],
          })}>+ Add secret</button>
        </div>
        <p className="runtime-secret-help">
          <strong>Protected file (recommended)</strong> sets <code>NAME=/run/secrets/NAME</code>, so the variable contains a file path.
          <strong> Environment (legacy)</strong> sets <code>NAME</code> to the secret value for applications that cannot read secret files.
        </p>
        <div className="environment-rows secret-rows runtime-secret-rows">
          {config.bindings.length === 0 && <p>No approved secret references.</p>}
          {config.bindings.map((binding, index) => <div key={`${binding.environment_key}-${index}`}>
            <input aria-label="Secret environment variable" value={binding.environment_key} onChange={(event) => {
              const bindings = [...config.bindings];
              bindings[index] = { ...binding, environment_key: event.target.value.toUpperCase() };
              onChange({ ...config, bindings });
            }} />
            <input aria-label="Secret alias" value={binding.alias} onChange={(event) => {
              const bindings = [...config.bindings];
              bindings[index] = { ...binding, alias: event.target.value };
              onChange({ ...config, bindings });
            }} />
            <input aria-label="Runtime secret source" placeholder="keychain:service:account or file:repo/SECRET" value={binding.source} onChange={(event) => {
              const bindings = [...config.bindings];
              bindings[index] = { ...binding, source: event.target.value };
              onChange({ ...config, bindings });
            }} />
            <select aria-label={`${binding.environment_key} delivery`} value={binding.delivery} onChange={(event) => {
              const bindings = [...config.bindings];
              bindings[index] = { ...binding, delivery: event.target.value as "environment" | "file" };
              onChange({ ...config, bindings });
            }}>
              <option value="file">Protected file (recommended)</option>
              <option value="environment">Environment (legacy)</option>
            </select>
            <button aria-label={`Remove ${binding.environment_key}`} onClick={() => onChange({
              ...config,
              bindings: config.bindings.filter((_, rowIndex) => rowIndex !== index),
            })}>×</button>
          </div>)}
        </div>
      </section>
      <section>
        <div className="environment-section-heading">
          <div><strong>BuildKit secrets</strong><span>Sensitive credentials available only during the image build, never to the running application.</span></div>
          <button onClick={() => onChange({
            ...config,
            build_bindings: [...config.build_bindings, {
              secret_id: `build_secret_${config.build_bindings.length + 1}`,
              alias: `build-secret-${config.build_bindings.length + 1}`,
              source: "keychain:",
            }],
          })}>+ Add build secret</button>
        </div>
        <div className="environment-rows secret-rows">
          {config.build_bindings.length === 0 && <p>No approved build secrets.</p>}
          {config.build_bindings.map((binding, index) => <div key={`${binding.secret_id}-${index}`}>
            <input aria-label="BuildKit secret id" value={binding.secret_id} onChange={(event) => {
              const build_bindings = [...config.build_bindings];
              build_bindings[index] = { ...binding, secret_id: event.target.value };
              onChange({ ...config, build_bindings });
            }} />
            <input aria-label="Build secret alias" value={binding.alias} onChange={(event) => {
              const build_bindings = [...config.build_bindings];
              build_bindings[index] = { ...binding, alias: event.target.value };
              onChange({ ...config, build_bindings });
            }} />
            <input aria-label="Build secret source" placeholder="keychain:service:account or file:repo/SECRET" value={binding.source} onChange={(event) => {
              const build_bindings = [...config.build_bindings];
              build_bindings[index] = { ...binding, source: event.target.value };
              onChange({ ...config, build_bindings });
            }} />
            <button aria-label={`Remove ${binding.secret_id}`} onClick={() => onChange({
              ...config,
              build_bindings: config.build_bindings.filter((_, rowIndex) => rowIndex !== index),
            })}>×</button>
          </div>)}
        </div>
      </section>
    </div>
    <div className="environment-actions">
      <span>Variable names use uppercase letters, digits, and underscores.</span>
      <button onClick={() => onSave(config)}>Save deployment settings</button>
    </div>
  </div>;
}

function AggregateCard({
  eyebrow,
  title,
  rows,
  className = "",
}: {
  eyebrow: string;
  title: string;
  rows: Array<{ label: string; primary: string; secondary: string }>;
  className?: string;
}) {
  return <div className={`activity-card detail-section ${className}`.trim()}>
    <p className="eyebrow">{eyebrow}</p>
    <h2>{title}</h2>
    <div className="aggregate-list">
      {rows.length === 0 ? <p>No open pull requests.</p> : rows.map((row) => <div key={row.label}>
        <strong>{row.label}</strong>
        <span>{row.primary}</span>
        <small>{row.secondary}</small>
      </div>)}
    </div>
  </div>;
}

/// What a member may open in the staff portal: Requests, and the service
/// status page. Status sits outside the control room on purpose — it reports
/// whether services were up, never any request, repository, or review content,
/// so a reader who needs to know whether Netra is healthy does not need the
/// administrator surface to find out. Everything else lands on Requests.
export function memberCanOpen(pathname: string) {
  const section = pathname.split("/").filter(Boolean)[0] ?? "";
  return section === "requests" || section === "status";
}

export function AuthenticatedRoot() {
  const [user, setUser] = useState<PortalUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [authUnavailable, setAuthUnavailable] = useState(false);

  useEffect(() => {
    let active = true;
    portalApi.me()
      .then((actor) => {
        if (!active) return;
        setUser(actor);
        setLoading(false);
      })
      .catch((reason) => {
        if (!active) return;
        setAuthUnavailable(!(reason instanceof PortalApiError) || reason.status !== 401);
        setLoading(false);
      });
    return () => { active = false; };
  }, []);

  if (loading) return <div className="auth-shell"><div className="auth-card"><NetraBrandMark /><p>Opening Netra…</p></div></div>;
  if (authUnavailable) return <div className="auth-shell"><div className="auth-card">
    <NetraBrandMark />
    <p className="eyebrow">NETRA ENGINEERING PORTAL</p>
    <h1>Sign-in is temporarily unavailable</h1>
    <p>Netra’s webhook and review workers remain active. An administrator needs to restore the Microsoft 365 sign-in configuration.</p>
    <button className="microsoft-sign-in" onClick={() => window.location.reload()}>Try again</button>
  </div></div>;
  if (!user) {
    const returnTo = `${window.location.pathname}${window.location.search}`;
    return <div className="auth-shell">
      <div className="auth-card">
        <NetraBrandMark />
        <p className="eyebrow">NETRA ENGINEERING PORTAL</p>
        <h1>Sign in to Netra</h1>
        <p>Use your Streamoid Microsoft 365 account to raise requests and follow engineering work.</p>
        <a className="microsoft-sign-in" href={`/auth/login?return_to=${encodeURIComponent(returnTo)}`}>
          <span aria-hidden="true"><i /><i /><i /><i /></span>
          Continue with Microsoft 365
        </a>
      </div>
    </div>;
  }

  const isAdmin = user.role === "admin";
  if (window.location.pathname === "/inbox") {
    window.history.replaceState(null, "", "/requests");
  }
  if (window.location.pathname === "/team") {
    window.history.replaceState(null, "", "/settings/team");
  }
  if (!isAdmin) {
    if (!memberCanOpen(window.location.pathname)) {
      window.history.replaceState(null, "", "/requests");
    }
    document.body.classList.add("portal-mode");
    // Above both apps rather than inside either: a deploy leaves every open tab
    // stale, whichever of the two it happens to be showing.
    return <><UpdateNotice /><PortalApp actor={user} /></>;
  }

  document.body.classList.remove("portal-mode");
  return <><UpdateNotice /><App currentUser={user} /></>;
}

export function mount(root: Element) {
  createRoot(root).render(
    <React.StrictMode><AuthenticatedRoot /></React.StrictMode>,
  );
}

if (typeof document !== "undefined") {
  const root = document.getElementById("root");
  if (root) mount(root);
}
